import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Plus, GripVertical, Trash2, Copy, Image as ImageIcon, Type, Code, Quote, List, Columns, LayoutPanelLeft, LayoutPanelTop, FolderOpen, Palette, Grid3X3, Sparkles, Film, Command, ClipboardList, ChevronDown, ChevronUp, ArrowUp, ArrowDown, ArrowLeft, ArrowRight, Images, MonitorPlay, FileCode, MousePointerClick, Eye, EyeOff, MailX, Search, FileText, Newspaper, Settings, MessageSquareQuote, DollarSign, HelpCircle, Layout, Users, Star, Bookmark, Trash, Clock, SlidersHorizontal, Workflow, Footprints, ExternalLink } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Slider } from './ui/slider';
import { Switch } from './ui/switch';
import { Label } from './ui/label';
import TipTapEditor from './TipTapEditor';
import MediaPickerModal from './MediaPickerModal';
import BlockStyleControls from './BlockStyleControls';
import ImageSizeControls from './ImageSizeControls';
import CodeBlock, { CODE_LANGUAGES, CODE_THEMES } from './CodeBlock';
import SnippetBrowserModal from './SnippetBrowserModal';
import ImageTextCarouselEditor from './ImageTextCarouselEditor';
import HeroBlockEditor from './HeroBlockEditor';
import AnimationControls from './AnimationControls';
import BlockWidthControl from './BlockWidthControl';
import SlashCommandMenu from './SlashCommandMenu';
import FormBlockEditor from './FormBlockEditor';
import SaveBlockTemplateModal from './SaveBlockTemplateModal';
import api from '../utils/api';
import { toast } from 'sonner';
import ColorPickerSwatch, { ColorHexInput } from './ColorPickerSwatch';
import FONT_FAMILIES from '../constants/fonts';
import GalleryBlockEditor from './GalleryBlockEditor';
import TickerTapeEditor from './TickerTapeEditor';
import ArticleFeedEditor from './ArticleFeedEditor';
import EventsCalendarEditor from './EventsCalendarEditor';
import BlockButtonEditor from './BlockButtonEditor';
import ButtonEnhancements from './ButtonEnhancements';
import ButtonPresetsBar from './ButtonPresetsBar';
import { BlockTabs, BlockTabContent, BlockSplitPane } from './BlockTabs';
import SectionHeaderEditor from './SectionHeaderEditor';
import TitleStyleControls from './TitleStyleControls';
import PresetSelector from './PresetSelector';
import LinkSelector from './LinkSelector';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { BUTTON_ICON_OPTIONS, renderButtonIcon } from './buttonBuilderUtils';

// Shared Block Width + Min Height controls for the extra Hero blocks (mirrors the main Hero block's Layout & Style)
// Reorder a list item from one index to another (used for moving columns/plans).
const reorderList = (list, from, to) => {
  const arr = [...(list || [])];
  if (from < 0 || to < 0 || from >= arr.length || to >= arr.length) return arr;
  const [moved] = arr.splice(from, 1);
  arr.splice(to, 0, moved);
  return arr;
};

// Reusable ◀ ▶ controls to move a column/plan left or right within a block.
const ColumnMoveButtons = ({ index, total, onMove, testIdPrefix }) => (
  <div className="flex items-center">
    <Button
      variant="ghost"
      size="sm"
      className="h-6 w-6 p-0 text-slate-500 hover:text-slate-800 disabled:opacity-30"
      disabled={index === 0}
      onClick={() => onMove(index, index - 1)}
      title="Move left"
      data-testid={testIdPrefix ? `${testIdPrefix}-move-left-${index}` : undefined}
    >
      <ArrowLeft size={14} />
    </Button>
    <Button
      variant="ghost"
      size="sm"
      className="h-6 w-6 p-0 text-slate-500 hover:text-slate-800 disabled:opacity-30"
      disabled={index === total - 1}
      onClick={() => onMove(index, index + 1)}
      title="Move right"
      data-testid={testIdPrefix ? `${testIdPrefix}-move-right-${index}` : undefined}
    >
      <ArrowRight size={14} />
    </Button>
  </div>
);

// Duplicate-column button (clones a column/plan in place, inserted right after).
const DuplicateColumnButton = ({ onClick, testId }) => (
  <Button
    variant="ghost"
    size="sm"
    className="h-6 w-6 p-0 text-slate-500 hover:text-indigo-600"
    onClick={onClick}
    title="Duplicate column"
    data-testid={testId}
  >
    <Copy size={13} />
  </Button>
);

const newColId = (prefix) => `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

// Draggable wrapper for a single column/plan card. Renders a grip handle bar
// above the original card content (children stay untouched).
const SortableColumnCard = ({ id, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    position: 'relative',
    zIndex: isDragging ? 30 : undefined,
  };
  return (
    <div ref={setNodeRef} style={style}>
      <div
        {...attributes}
        {...listeners}
        className="flex items-center justify-center gap-1 py-1 mb-1 rounded bg-slate-100 hover:bg-slate-200 cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600 touch-none select-none"
        title="Drag to reorder"
        data-testid={`drag-col-${id}`}
      >
        <GripVertical size={12} />
        <span className="text-[10px] uppercase tracking-wide">Drag to reorder</span>
      </div>
      {children}
    </div>
  );
};

// Wraps a list of column/plan cards in a sortable DnD context.
// ids: stable string id per card (same order as rendered children).
// onReorder(fromIndex, toIndex): apply the new order to the block content.
const ColumnDndContext = ({ ids, onReorder, children }) => (
  <DndContext
    collisionDetection={closestCenter}
    onDragEnd={({ active, over }) => {
      if (over && active.id !== over.id) {
        const from = ids.indexOf(active.id);
        const to = ids.indexOf(over.id);
        if (from !== -1 && to !== -1) onReorder(from, to);
      }
    }}
  >
    <SortableContext items={ids} strategy={verticalListSortingStrategy}>
      {children}
    </SortableContext>
  </DndContext>
);




const HERO_WIDTH_OPTIONS = [
  { value: 'full', label: 'Full', description: 'Edge to edge (100vw)' },
  { value: 'container', label: 'Container', description: 'Matches page width' },
  { value: 'large', label: 'Large', description: 'Centered, 1200px cap' },
  { value: 'medium', label: 'Medium', description: 'Centered, 900px cap' },
  { value: 'small', label: 'Small', description: 'Centered, 600px cap' },
];
const HERO_H_POSITIONS = [
  { value: 'left', label: 'Left' },
  { value: 'center', label: 'Center' },
  { value: 'right', label: 'Right' },
];
const HERO_V_POSITIONS = [
  { value: 'top', label: 'Top' },
  { value: 'middle', label: 'Middle' },
  { value: 'bottom', label: 'Bottom' },
];
const HERO_OVERLAY_OPTIONS = [
  { value: 'none', label: 'No Overlay' },
  { value: 'dark', label: 'Dark' },
  { value: 'darker', label: 'Darker' },
  { value: 'light', label: 'Light' },
  { value: 'gradient-dark', label: 'Gradient (Dark)' },
  { value: 'gradient-primary', label: 'Gradient (Purple)' },
  { value: 'gradient-warm', label: 'Gradient (Warm)' },
  { value: 'gradient-cool', label: 'Gradient (Cool)' },
];

// Shared "Layout & Style" panel for the new hero variants
// (hero-spotlight, hero-tabs, hero-feature-cards, hero-split-stats,
// hero-split-cards). Mirrors the panel in HeroBlockEditor.js but with
// prop toggles for controls that don't apply to every variant.
//   includeOverlay     -> show Overlay dropdown (bg-image heroes only)
//   includeTextColor   -> show Default Text Colour (bg-image heroes only)
//   includeHeight      -> show Min height slider
const HeroLayoutControls = ({ id, content, updateBlock, prefix, includeHeight = true, includeOverlay = false, includeTextColor = false, includePosition = true }) => {
  const width = content.width || 'container';
  const posH = content.textPositionH || 'center';
  const posV = content.textPositionV || 'middle';
  const overlay = content.overlay || 'none';
  const textColor = content.textColor || '#ffffff';
  const height = content.height || 500;
  const setC = (patch) => updateBlock(id, { ...content, ...patch });
  return (
    <div className="space-y-4 p-4 rounded-lg bg-slate-50 border border-slate-200" data-testid={`${prefix}-layout-style`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 pb-2 border-b border-slate-200">
        <Layout size={16} className="text-blue-500" />
        Layout & Style
      </div>

      {/* Block Width - 5-button row (matches HeroBlockEditor) */}
      <div className="space-y-2 pb-3 border-b border-slate-200">
        <Label className="text-xs font-semibold flex items-center gap-1">
          <Layout size={12} /> Block Width
        </Label>
        <div className="grid grid-cols-5 gap-2">
          {HERO_WIDTH_OPTIONS.map((w) => (
            <button
              key={w.value}
              type="button"
              onClick={() => setC({ width: w.value })}
              className={`p-2 rounded-lg border text-xs font-medium transition-all ${
                width === w.value
                  ? 'bg-indigo-600 text-white border-indigo-600'
                  : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-300'
              }`}
              title={w.description}
              data-testid={`${prefix}-width-${w.value}-${id}`}
            >
              {w.label}
            </button>
          ))}
        </div>
        <p className="text-xs text-slate-500">{HERO_WIDTH_OPTIONS.find(w => w.value === width)?.description}</p>
      </div>

      {/* Horizontal + Vertical Position */}
      {includePosition && (
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label className="text-xs">Horizontal Position</Label>
            <Select value={posH} onValueChange={(v) => setC({ textPositionH: v })}>
              <SelectTrigger className="h-9" data-testid={`${prefix}-posh-${id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {HERO_H_POSITIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs">Vertical Position</Label>
            <Select value={posV} onValueChange={(v) => setC({ textPositionV: v })}>
              <SelectTrigger className="h-9" data-testid={`${prefix}-posv-${id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {HERO_V_POSITIONS.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Overlay + Default Text Colour - only for bg-image variants */}
      {(includeOverlay || includeTextColor) && (
        <div className="grid grid-cols-2 gap-3">
          {includeOverlay && (
            <div className="space-y-1">
              <Label className="text-xs">Overlay</Label>
              <Select value={overlay} onValueChange={(v) => setC({ overlay: v })}>
                <SelectTrigger className="h-9" data-testid={`${prefix}-overlay-${id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {HERO_OVERLAY_OPTIONS.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {includeTextColor && (
            <div className="space-y-1">
              <Label className="text-xs">Default Text Colour</Label>
              <div className="flex items-center gap-2">
                <ColorPickerSwatch value={textColor.startsWith('#') ? textColor : '#ffffff'} onChange={(e) => setC({ textColor: e.target.value })} className="w-9 h-9 rounded border cursor-pointer" data-testid={`${prefix}-textcolor-${id}`} />
                <ColorHexInput value={content.textColor || ''} onChange={(e) => setC({ textColor: e.target.value })} placeholder="#ffffff" className="h-9 text-sm font-mono flex-1" />
              </div>
            </div>
          )}
        </div>
      )}

      {/* Height */}
      {includeHeight && (
        <div className="space-y-1">
          <Label className="text-xs">Height: {height}px</Label>
          <Slider value={[height]} onValueChange={([v]) => setC({ height: v })} min={200} max={800} step={20} data-testid={`${prefix}-height-${id}`} />
        </div>
      )}
    </div>
  );
};

// Independent Headline / Subheadline typography panel used by all new
// hero variants. Lets the editor pick font family, size, weight & colour
// for headline and subheadline separately. Reads/writes into
// content.headlineStyle and content.subheadlineStyle.
const HERO_FONT_FAMILIES = [
  { value: '', label: 'Inherit from site theme' },
  { value: 'Inter, sans-serif', label: 'Inter (Sans)' },
  { value: '"Open Sans", sans-serif', label: 'Open Sans' },
  { value: 'Poppins, sans-serif', label: 'Poppins' },
  { value: 'Montserrat, sans-serif', label: 'Montserrat' },
  { value: 'Roboto, sans-serif', label: 'Roboto' },
  { value: '"Playfair Display", serif', label: 'Playfair Display (Serif)' },
  { value: 'Merriweather, serif', label: 'Merriweather (Serif)' },
  { value: 'Georgia, serif', label: 'Georgia (Serif)' },
  { value: '"Courier New", monospace', label: 'Courier (Mono)' },
];
const HERO_FONT_WEIGHTS = [
  { value: '300', label: 'Light 300' },
  { value: '400', label: 'Regular 400' },
  { value: '500', label: 'Medium 500' },
  { value: '600', label: 'Semibold 600' },
  { value: '700', label: 'Bold 700' },
  { value: '800', label: 'Extrabold 800' },
  { value: '900', label: 'Black 900' },
];
const HeroTypographyRow = ({ label, styleKey, style, defaultSize, prefix, id, setC }) => (
  <div className="p-3 rounded-md bg-white border border-slate-200 space-y-2">
    <Label className="text-xs font-semibold text-slate-700">{label}</Label>
    <div className="grid grid-cols-2 gap-2">
      <div className="space-y-1">
        <Label className="text-[11px] text-slate-500">Font family</Label>
        <Select value={style.fontFamily || ''} onValueChange={(v) => setC(styleKey, { fontFamily: v })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`${prefix}-${styleKey}-family-${id}`}><SelectValue placeholder="Inherit" /></SelectTrigger>
          <SelectContent>
            {HERO_FONT_FAMILIES.map(f => <SelectItem key={f.value || 'inherit'} value={f.value || 'inherit'}>{f.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-[11px] text-slate-500">Weight</Label>
        <Select value={style.fontWeight || ''} onValueChange={(v) => setC(styleKey, { fontWeight: v })}>
          <SelectTrigger className="h-8 text-xs" data-testid={`${prefix}-${styleKey}-weight-${id}`}><SelectValue placeholder="Inherit" /></SelectTrigger>
          <SelectContent>
            {HERO_FONT_WEIGHTS.map(w => <SelectItem key={w.value} value={w.value}>{w.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-1">
        <Label className="text-[11px] text-slate-500">Size (px)</Label>
        <Input type="number" min={10} max={140} value={style.fontSize || ''} onChange={(e) => setC(styleKey, { fontSize: e.target.value ? Number(e.target.value) : undefined })} placeholder={String(defaultSize)} className="h-8 text-xs" data-testid={`${prefix}-${styleKey}-size-${id}`} />
      </div>
      <div className="space-y-1">
        <Label className="text-[11px] text-slate-500">Colour</Label>
        <div className="flex items-center gap-1">
          <ColorPickerSwatch value={(style.color || '').startsWith('#') ? style.color : '#111827'} onChange={(e) => setC(styleKey, { color: e.target.value })} className="w-7 h-8 rounded border cursor-pointer" data-testid={`${prefix}-${styleKey}-color-${id}`} />
          <Input value={style.color || ''} onChange={(e) => setC(styleKey, { color: e.target.value })} placeholder="inherit" className="h-8 text-xs font-mono flex-1" />
        </div>
      </div>
    </div>
  </div>
);

const HeroTypographyControls = ({ id, content, updateBlock, prefix }) => {
  const head = content.headlineStyle || {};
  const sub = content.subheadlineStyle || {};
  const setC = (key, patch) => updateBlock(id, { ...content, [key]: { ...(content[key] || {}), ...patch } });
  return (
    <div className="space-y-3 p-4 rounded-lg bg-slate-50 border border-slate-200" data-testid={`${prefix}-typography`}>
      <div className="flex items-center gap-2 text-sm font-semibold text-slate-700 pb-2 border-b border-slate-200">
        <Type size={16} className="text-purple-500" />
        Typography
      </div>
      <HeroTypographyRow label="Headline" styleKey="headlineStyle" style={head} defaultSize={48} prefix={prefix} id={id} setC={setC} />
      <HeroTypographyRow label="Subheadline" styleKey="subheadlineStyle" style={sub} defaultSize={18} prefix={prefix} id={id} setC={setC} />
    </div>
  );
};

// Compact per-item typography editor used inside <details> panels of the
// hero-tabs / hero-feature-cards / hero-split-stats item editors. Writes
// into e.g. `stats[i].valueStyle` — the renderer spreads it inline on top
// of the block-level defaults, so leaving fields blank falls through to
// the block-level Typography panel above (and ultimately the site theme).
const HeroItemTypographyRow = ({ style, defaultSize, testPrefix, onPatch }) => {
  const s = style || {};
  const clear = () => onPatch({ color: undefined, fontFamily: undefined, fontSize: undefined, fontWeight: undefined });
  return (
    <div className="p-2 rounded-md bg-white border border-slate-200 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <Label className="text-[10px] text-slate-500">Font family</Label>
          <Select value={s.fontFamily || ''} onValueChange={(v) => onPatch({ fontFamily: v === 'inherit' ? undefined : v })}>
            <SelectTrigger className="h-7 text-xs" data-testid={`${testPrefix}-family`}><SelectValue placeholder="Inherit" /></SelectTrigger>
            <SelectContent>{HERO_FONT_FAMILIES.map(f => <SelectItem key={f.value || 'inherit'} value={f.value || 'inherit'}>{f.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-[10px] text-slate-500">Size (px)</Label>
          <Input type="number" min={10} max={140} value={s.fontSize || ''} onChange={(e) => onPatch({ fontSize: e.target.value ? Number(e.target.value) : undefined })} placeholder={String(defaultSize)} className="h-7 text-xs" data-testid={`${testPrefix}-size`} />
        </div>
        <div className="space-y-1 col-span-2">
          <Label className="text-[10px] text-slate-500">Colour</Label>
          <div className="flex items-center gap-1">
            <ColorPickerSwatch value={(s.color || '').startsWith('#') ? s.color : '#111827'} onChange={(e) => onPatch({ color: e.target.value })} className="w-7 h-7 rounded border cursor-pointer" data-testid={`${testPrefix}-color`} />
            <Input value={s.color || ''} onChange={(e) => onPatch({ color: e.target.value || undefined })} placeholder="inherit" className="h-7 text-xs font-mono flex-1" />
            {(s.color || s.fontFamily || s.fontSize) && (
              <button type="button" onClick={clear} className="text-[10px] text-slate-500 hover:text-slate-700 underline" title="Reset this item's typography">Reset</button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


// Helper function to generate anchor ID from block content
const generateAnchorId = (block) => {
  // Try to extract text content for anchor ID
  let text = '';
  
  if (block.type === 'heading') {
    // Extract text from heading HTML
    text = block.content?.html?.replace(/<[^>]*>/g, '') || '';
  } else if (block.type === 'hero') {
    text = block.content?.headline || '';
  } else if (block.type === 'paragraph') {
    // Get first few words from paragraph
    text = block.content?.html?.replace(/<[^>]*>/g, '').substring(0, 50) || '';
  } else if (block.type === 'quote') {
    text = block.content?.html?.replace(/<[^>]*>/g, '').substring(0, 50) || '';
  } else if (block.type === 'form') {
    text = block.content?.formName || 'form';
  } else if (block.type === 'gallery') {
    text = 'gallery';
  } else if (block.type === 'article-feed') {
    text = block.content?.heading || 'articles';
  }
  
  // If we have text, convert to slug format
  if (text) {
    return text.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .substring(0, 50) || block.type;
  }
  
  // Fallback to block type + index
  return `${block.type}-${block.id?.split('-').pop() || 'block'}`;
};

const BLOCK_COLORS = {
  heading: { border: '#6366f1', bg: '#eef2ff', label: 'Heading' },      // Indigo
  paragraph: { border: '#8b5cf6', bg: '#f5f3ff', label: 'Paragraph' },  // Purple
  image: { border: '#ec4899', bg: '#fdf2f8', label: 'Image' },          // Pink
  button: { border: '#10b981', bg: '#ecfdf5', label: 'Button' },        // Emerald
  hero: { border: '#f59e0b', bg: '#fffbeb', label: 'Hero' },            // Amber
  quote: { border: '#14b8a6', bg: '#f0fdfa', label: 'Quote' },          // Teal
  list: { border: '#22c55e', bg: '#f0fdf4', label: 'List' },            // Green
  code: { border: '#64748b', bg: '#f8fafc', label: 'Code' },            // Slate
  'custom-html': { border: '#ef4444', bg: '#fef2f2', label: 'Custom HTML' }, // Red
  columns: { border: '#3b82f6', bg: '#eff6ff', label: 'Columns' },      // Blue
  'card-grid': { border: '#2563eb', bg: '#eff6ff', label: 'Card Grid' }, // Blue
  'two-column': { border: '#3b82f6', bg: '#eff6ff', label: '2-Column' },
  'image-text-row': { border: '#f97316', bg: '#fff7ed', label: 'Image + Text' },    // Orange
  'image-text-stack': { border: '#f97316', bg: '#fff7ed', label: 'Image + Text' },
  form: { border: '#0ea5e9', bg: '#f0f9ff', label: 'Form' },            // Sky
  gallery: { border: '#d946ef', bg: '#fdf4ff', label: 'Gallery' },      // Fuchsia
  ticker: { border: '#06b6d4', bg: '#ecfeff', label: 'Ticker Tape' },   // Cyan
  'article-feed': { border: '#f97316', bg: '#fff7ed', label: 'Article Feed' }, // Orange
  'logo-cloud': { border: '#0ea5e9', bg: '#f0f9ff', label: 'Logo Cloud' },     // Sky
  'hero-spotlight': { border: '#f59e0b', bg: '#fffbeb', label: 'Animated Hero' }, // Amber
  'countdown': { border: '#e11d48', bg: '#fff1f2', label: 'Countdown' },          // Rose
  'before-after': { border: '#0d9488', bg: '#f0fdfa', label: 'Before / After' },  // Teal
  'hero-tabs': { border: '#6366f1', bg: '#eef2ff', label: 'Hero Tabs' },           // Indigo
  'hero-feature-cards': { border: '#16a34a', bg: '#f0fdf4', label: 'Hero Feature Cards' }, // Green
  'hero-split-stats': { border: '#2563eb', bg: '#eff6ff', label: 'Hero Split + Stats' },   // Blue
  'hero-split-cards': { border: '#9333ea', bg: '#faf5ff', label: 'Hero Split Cards' },      // Purple
  'unsubscribe': { border: '#dc2626', bg: '#fef2f2', label: 'Unsubscribe' }, // Red
  'newsletter-header': { border: '#8b5cf6', bg: '#f5f3ff', label: 'Newsletter Header' }, // Violet
  'search': { border: '#0891b2', bg: '#ecfeff', label: 'Site Search' },   // Cyan
  'expandable-tabs': { border: '#6366f1', bg: '#eef2ff', label: 'Expandable Tabs' }, // Indigo
  'sparkles': { border: '#8b5cf6', bg: '#f5f3ff', label: 'Sparkles' },             // Violet (light)
  'gallery-carousel': { border: '#ec4899', bg: '#fdf2f8', label: 'Gallery Carousel' }, // Pink
  'testimonial-deck': { border: '#f59e0b', bg: '#fffbeb', label: 'Testimonial Cards' },   // Amber
  'liquid-glass': { border: '#0ea5e9', bg: '#f0f9ff', label: 'Liquid Glass' },             // Sky (light)
  'testimonials-columns': { border: '#14b8a6', bg: '#f0fdfa', label: 'Testimonials Columns' }, // Teal
  'typewriter': { border: '#eab308', bg: '#fefce8', label: 'Typewriter' },                   // Yellow
  'gooey-text': { border: '#7c3aed', bg: '#f5f3ff', label: 'Gooey Text' },                   // Violet (light)
  'text-cycle': { border: '#0ea5e9', bg: '#f0f9ff', label: 'Animated Text Cycle' },          // Sky
  'circular-testimonials': { border: '#0d9488', bg: '#f0fdfa', label: 'Block Testimonials' }, // Teal
  'stagger-testimonials': { border: '#e11d48', bg: '#fff1f2', label: 'Stagger Testimonials' },   // Rose
  'steps-icons': { border: '#7c3aed', bg: '#f5f3ff', label: 'Steps — Icons & Arrows' },   // Violet
  'steps-cards': { border: '#7c3aed', bg: '#f5f3ff', label: 'Steps — Numbered Cards' },   // Violet
  'steps-grid-image': { border: '#7c3aed', bg: '#f5f3ff', label: 'Steps — Grid + Image' },   // Violet
  'steps-timeline-image': { border: '#7c3aed', bg: '#f5f3ff', label: 'Steps — Timeline + Image' },   // Violet
  'handwriting-text': { border: '#9333ea', bg: '#faf5ff', label: 'Hand Writing Text' },           // Purple
  'typewriter-smooth': { border: '#2563eb', bg: '#eff6ff', label: 'Typewriter (Smooth)' },         // Blue
  'folder-gallery': { border: '#d97706', bg: '#fff7ed', label: 'Folder Gallery' },                 // Amber (light)
};

const SortableBlock = ({ block, index, children, onDuplicate, onDelete, onStyleClick, onMoveUp, onMoveDown, isFirst, isLast, isCollapsed, onToggleCollapse, onAnchorChange, isDuplicateAnchor, allAnchorIds, onApplyPreset, onApplyColumnPreset, onTogglePublished, onSaveBlock }) => {
  const [isEditingAnchor, setIsEditingAnchor] = useState(false);
  const [anchorValue, setAnchorValue] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  // Check if block is published (default to true if not set)
  const isPublished = block.isPublished !== false;
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: block.id });

  const blockStyles = block.styles || {};
  const blockColor = BLOCK_COLORS[block.type] || { border: '#94a3b8', bg: '#f8fafc', label: block.type };
  
  const getHoverClass = () => {
    switch (blockStyles.hoverEffect) {
      case 'shadow':
        return 'hover:shadow-xl';
      case 'lift':
        return 'hover:-translate-y-1 hover:shadow-lg';
      case 'glow':
        return ''; // Handled in inline style
      case 'darken':
        return 'hover:brightness-95';
      case 'scale':
        return 'hover:scale-[1.02]';
      case 'border-highlight':
        return ''; // Handled in inline style
      default:
        return '';
    }
  };

  const getClickClass = () => {
    switch (blockStyles.clickEffect) {
      case 'scale-down':
        return 'active:scale-[0.98]';
      case 'flash':
        return 'active:opacity-70';
      case 'shake':
        return 'active:animate-shake';
      default:
        return '';
    }
  };

  const getShadowClass = () => {
    switch (blockStyles.shadow) {
      case 'sm':
        return 'shadow-sm';
      case 'md':
        return 'shadow-md';
      case 'lg':
        return 'shadow-lg';
      case 'xl':
        return 'shadow-xl';
      case 'inner':
        return 'shadow-inner';
      default:
        return '';
    }
  };

  const getHoverStyle = () => {
    if (blockStyles.hoverEffect === 'glow') {
      return { '--glow-color': blockStyles.glowColor || '#6366f1' };
    }
    if (blockStyles.hoverEffect === 'border-highlight') {
      return { '--highlight-color': blockStyles.highlightColor || '#6366f1' };
    }
    return {};
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition: isDragging ? transition : `all ${blockStyles.transitionDuration || '0.2s'} ease`,
    opacity: isDragging ? 0.5 : isPublished ? 1 : 0.5,
    // Use a fixed light background for editor UI - don't apply user's background color here
    backgroundColor: isPublished ? blockColor.bg : '#f1f5f9',
    borderColor: isPublished ? '#e2e8f0' : '#cbd5e1',
    borderWidth: '1px',
    borderStyle: isPublished ? 'solid' : 'dashed',
    borderRadius: '0.5rem',
    padding: '1rem',
    borderLeftWidth: '4px',
    borderLeftColor: isPublished ? blockColor.border : '#94a3b8',
    ...getHoverStyle(),
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      data-testid={`block-${block.type}-${index}`}
      className={`group relative mb-4 transition-all ${getHoverClass()} ${getClickClass()} ${getShadowClass()} ${
        isDragging ? 'shadow-lg ring-2 ring-indigo-400 z-50' : ''
      } ${blockStyles.hoverEffect === 'glow' ? 'hover:shadow-[0_0_20px_var(--glow-color)]' : ''} ${
        blockStyles.hoverEffect === 'border-highlight' ? 'hover:border-[var(--highlight-color)]' : ''
      }`}
    >
      {/* Block Header with Label and Controls */}
      <div className="flex items-center justify-between mb-2">
        {/* Left side: Type Label + Published Status + Anchor ID + Collapse Toggle */}
        <div className="flex items-center gap-2">
          <div 
            className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full"
            style={{ 
              backgroundColor: isPublished ? blockColor.border : '#94a3b8', 
              color: 'white',
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
          >
            {blockColor.label}
          </div>
          {/* Unpublished Badge */}
          {!isPublished && (
            <div 
              className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-full bg-amber-100 text-amber-700 border border-amber-300"
            >
              Hidden
            </div>
          )}
          {/* Editable Anchor ID */}
          {isEditingAnchor ? (
            <div className="flex items-center gap-1">
              <span className="text-xs text-slate-500">#</span>
              <input
                type="text"
                value={anchorValue}
                onChange={(e) => {
                  // Convert to slug format as user types
                  const newValue = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                  setAnchorValue(newValue);
                }}
                onBlur={() => {
                  if (anchorValue.trim()) {
                    onAnchorChange(block.id, anchorValue.trim());
                  }
                  setIsEditingAnchor(false);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (anchorValue.trim()) {
                      onAnchorChange(block.id, anchorValue.trim());
                    }
                    setIsEditingAnchor(false);
                  } else if (e.key === 'Escape') {
                    setIsEditingAnchor(false);
                  }
                }}
                className={`text-xs font-mono w-32 px-1 py-0.5 border rounded ${
                  isDuplicateAnchor ? 'border-red-500 bg-red-50' : 'border-indigo-300'
                }`}
                autoFocus
                placeholder="anchor-id"
              />
              {isDuplicateAnchor && (
                <span className="text-[10px] text-red-500">Duplicate!</span>
              )}
            </div>
          ) : (
            <button
              onClick={() => {
                const currentAnchor = block.content?.anchor_id || generateAnchorId(block);
                setAnchorValue(currentAnchor);
                setIsEditingAnchor(true);
              }}
              className={`text-xs font-mono px-1.5 py-0.5 rounded hover:bg-indigo-100 transition-colors ${
                isDuplicateAnchor ? 'text-red-600 bg-red-50' : 'text-indigo-600'
              }`}
              title="Click to edit anchor ID"
            >
              #{block.content?.anchor_id || generateAnchorId(block)}
              {isDuplicateAnchor && <span className="ml-1 text-[10px]">⚠️</span>}
            </button>
          )}
        </div>

        {/* Right side: Drag Handle, Delete, Collapse */}
        <div className="flex items-center gap-1">
          {/* Drag Handle */}
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1.5 hover:bg-slate-100 rounded text-slate-400 hover:text-slate-600"
            title="Drag to reorder"
          >
            <GripVertical size={16} />
          </div>
          
          {/* Save block as reusable */}
          {onSaveBlock && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onSaveBlock(block)}
              className="h-7 w-7 p-0 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50"
              title="Save this block as reusable"
              data-testid={`save-block-${block.id}`}
            >
              <Bookmark size={14} />
            </Button>
          )}
          
          {/* Delete Block Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowDeleteConfirm(true)}
            className="h-7 w-7 p-0 text-slate-400 hover:text-red-600 hover:bg-red-50"
            title="Delete this block"
            data-testid={`delete-block-${block.id}`}
          >
            <Trash2 size={14} />
          </Button>
          
          {/* Collapse Toggle */}
          <Button
            variant="ghost"
            size="sm"
            onClick={onToggleCollapse}
            className="h-7 w-7 p-0 text-slate-400 hover:text-slate-600"
            title={isCollapsed ? "Expand block" : "Collapse block"}
          >
            {isCollapsed ? <ChevronDown size={14} /> : <ChevronUp size={14} />}
          </Button>
          
          {/* Delete Confirmation Dialog */}
          <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete Block?</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete this {blockColor.label} block? This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction 
                  onClick={() => onDelete(block.id)}
                  className="bg-red-600 hover:bg-red-700"
                >
                  Delete
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </div>
      </div>

      {/* Block Content - Collapsible */}
      {!isCollapsed && (
        <div className="pl-2 pr-2">{children}</div>
      )}
      
      {/* Collapsed Preview */}
      {isCollapsed && (
        <div className="text-xs text-slate-400 italic pl-2 truncate">
          {block.type === 'heading' || block.type === 'paragraph' 
            ? (block.content?.text || block.content?.html?.replace(/<[^>]*>/g, '') || 'Empty block').substring(0, 80) + '...'
            : block.type === 'image' 
              ? `Image: ${block.content?.alt || block.content?.url || 'No image'}`
              : block.type === 'columns'
                ? `${block.content?.columnCount || 2} columns`
                : block.type === 'card-grid'
                  ? `${(block.content?.columns || []).length} cards · ${block.content?.cardsPerRow || 3}/row`
                  : 'Click to expand'
          }
        </div>
      )}
    </div>
  );
};

const BlockEditor = ({ blocks, onChange, pages = [], allPagesAnchors = [], siteTheme = null }) => {
  // Resolve the theme colours a button should follow when the block doesn't
  // set explicit colours. Any missing theme field falls back to sensible
  // hardcoded defaults so the editor never shows a broken button.
  const themeBtnColor = siteTheme?.primary_color || '#6366f1';
  const themeBtnHover = siteTheme?.link_hover_color || siteTheme?.primary_color || '#4f46e5';
  const themeBtnGradient = siteTheme?.secondary_color || '#8b5cf6';
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [mediaTargetBlockId, setMediaTargetBlockId] = useState(null);
  const [activeStyleBlockId, setActiveStyleBlockId] = useState(null);
  const [showSnippetBrowser, setShowSnippetBrowser] = useState(false);
  const [snippetTargetBlockId, setSnippetTargetBlockId] = useState(null);

  // Save-block-as-reusable + saved blocks library
  const [blockToSave, setBlockToSave] = useState(null);
  const [savedBlocks, setSavedBlocks] = useState([]);
  const [loadingSavedBlocks, setLoadingSavedBlocks] = useState(false);

  const fetchSavedBlocks = useCallback(async () => {
    setLoadingSavedBlocks(true);
    try {
      const res = await api.get('/templates', { params: { kind: 'block' } });
      setSavedBlocks(res.data?.custom || []);
    } catch (e) {
      console.error('Failed to load saved blocks', e);
    } finally {
      setLoadingSavedBlocks(false);
    }
  }, []);

  const handleSaveBlockClick = useCallback((block) => {
    setBlockToSave(block);
  }, []);

  const insertSavedBlock = useCallback((savedTemplate) => {
    const src = savedTemplate?.blocks?.[0];
    if (!src) {
      toast.error('This saved block is empty');
      return;
    }
    const { id, ...rest } = src;
    const cleanContent = { ...(rest.content || {}) };
    delete cleanContent.anchor_id;
    const newBlock = { ...rest, id: `block-${Date.now()}`, content: cleanContent };
    onChange([...blocks, newBlock]);
    setShowAddMenu(false);
    toast.success('Block added');
  }, [blocks, onChange]);

  const deleteSavedBlock = useCallback(async (templateId, e) => {
    e?.stopPropagation();
    try {
      await api.delete(`/templates/${templateId}`);
      setSavedBlocks((prev) => prev.filter((t) => t.template_id !== templateId));
      toast.success('Saved block removed');
    } catch (err) {
      toast.error('Failed to remove saved block');
    }
  }, []);
  
  // Track active tab for each block (content, settings, style, animation)
  const [activeBlockTabs, setActiveBlockTabs] = useState({});
  
  const getActiveTab = (blockId) => activeBlockTabs[blockId] || 'content';
  const setActiveTab = (blockId, tab) => {
    setActiveBlockTabs(prev => ({ ...prev, [blockId]: tab }));
  };
  
  // Articles state for Insert Article block
  const [availableArticles, setAvailableArticles] = useState([]);
  const [loadingArticles, setLoadingArticles] = useState(false);
  
  // Fetch articles for the article-card block
  useEffect(() => {
    const fetchArticles = async () => {
      // Only fetch if there's an article-card block
      const hasArticleCard = blocks.some(b => b.type === 'article-card');
      if (!hasArticleCard && availableArticles.length > 0) return;
      
      setLoadingArticles(true);
      try {
        const response = await fetch('/api/articles', {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableArticles(data?.articles || data || []);
        }
      } catch (error) {
        console.error('Failed to fetch articles:', error);
      } finally {
        setLoadingArticles(false);
      }
    };
    fetchArticles();
  }, [blocks.length]);
  
  // Collapsed blocks state
  const [collapsedBlocks, setCollapsedBlocks] = useState(new Set());
  
  // Slash command state
  const [showSlashMenu, setShowSlashMenu] = useState(false);
  const [slashQuery, setSlashQuery] = useState('');
  const [slashMenuPosition, setSlashMenuPosition] = useState(null);
  const slashInputRef = useRef(null);
  const savedSelectionRef = useRef(null);
  
  // Color palette states removed - now using TipTapEditor for rich text formatting
  
  // Helper to save and restore text selection for inline color pickers
  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel.rangeCount > 0) {
      return sel.getRangeAt(0).cloneRange();
    }
    return null;
  };

  const restoreSelection = (range) => {
    if (range) {
      const sel = window.getSelection();
      sel.removeAllRanges();
      sel.addRange(range);
    }
  };
  
  // Get all anchor IDs and check for duplicates
  const getAllAnchorIds = useCallback(() => {
    return blocks.map(block => block.content?.anchor_id || generateAnchorId(block));
  }, [blocks]);
  
  const getDuplicateAnchorIds = useCallback(() => {
    const anchorIds = getAllAnchorIds();
    const seen = new Set();
    const duplicates = new Set();
    anchorIds.forEach(id => {
      if (seen.has(id)) {
        duplicates.add(id);
      }
      seen.add(id);
    });
    return duplicates;
  }, [getAllAnchorIds]);

  // Extract all anchors from current page blocks for the LinkSelector dropdown
  const localAnchors = useMemo(() => {
    return blocks
      .filter(block => block.content?.anchor_id || ['heading', 'hero', 'form', 'gallery', 'article-feed'].includes(block.type))
      .map(block => {
        // Get a display name for the block
        let name = block.type.charAt(0).toUpperCase() + block.type.slice(1);
        if (block.type === 'heading' && block.content?.text) {
          name = block.content.text.substring(0, 30);
        } else if (block.type === 'heading' && block.content?.html) {
          name = block.content.html.replace(/<[^>]*>/g, '').substring(0, 30);
        }
        
        return {
          id: block.content?.anchor_id || generateAnchorId(block),
          name: name,
          pageSlug: '' // Current page
        };
      });
  }, [blocks]);

  // Combine local anchors with anchors from all other pages
  const availableAnchors = useMemo(() => {
    // Add "This Page" label to local anchors
    const localWithLabel = localAnchors.map(a => ({
      ...a,
      name: a.name + ' (this page)'
    }));
    
    // Combine with all pages anchors (which already have pageSlug set)
    return [...localWithLabel, ...allPagesAnchors];
  }, [localAnchors, allPagesAnchors]);
  
  // Handle anchor ID change
  const handleAnchorChange = useCallback((blockId, newAnchorId) => {
    const updatedBlocks = blocks.map(block => {
      if (block.id === blockId) {
        return {
          ...block,
          content: {
            ...block.content,
            anchor_id: newAnchorId
          }
        };
      }
      return block;
    });
    onChange(updatedBlocks);
  }, [blocks, onChange]);
  
  // Check if a specific block has a duplicate anchor
  const isBlockAnchorDuplicate = useCallback((block) => {
    const anchorId = block.content?.anchor_id || generateAnchorId(block);
    const duplicates = getDuplicateAnchorIds();
    return duplicates.has(anchorId);
  }, [getDuplicateAnchorIds]);
  
  // Toggle collapse for a block
  const toggleBlockCollapse = (blockId) => {
    setCollapsedBlocks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(blockId)) {
        newSet.delete(blockId);
      } else {
        newSet.add(blockId);
      }
      return newSet;
    });
  };
  
  // Collapse/Expand all blocks
  const collapseAllBlocks = () => {
    setCollapsedBlocks(new Set(blocks.map(b => b.id)));
  };
  
  const expandAllBlocks = () => {
    setCollapsedBlocks(new Set());
  };
  
  // Toggle block published state
  const toggleBlockPublished = (blockId) => {
    const newBlocks = blocks.map(block => {
      if (block.id === blockId) {
        // Toggle isPublished - default to true if not set
        return { ...block, isPublished: block.isPublished === false ? true : false };
      }
      return block;
    });
    onChange(newBlocks);
  };
  
  // Move block up/down
  const moveBlockUp = (index) => {
    if (index === 0) return;
    const newBlocks = [...blocks];
    [newBlocks[index - 1], newBlocks[index]] = [newBlocks[index], newBlocks[index - 1]];
    onChange(newBlocks);
  };
  
  const moveBlockDown = (index) => {
    if (index === blocks.length - 1) return;
    const newBlocks = [...blocks];
    [newBlocks[index], newBlocks[index + 1]] = [newBlocks[index + 1], newBlocks[index]];
    onChange(newBlocks);
  };

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Define getDefaultContent before addBlock (which uses it)
  const getDefaultContent = (type) => {
    // Default animation for all blocks
    const defaultAnimation = { entrance: 'none', speed: 'normal', delay: '0' };
    
    switch (type) {
      case 'heading':
        return { text: 'Heading', level: 2, html: '<h2>Heading</h2>', animation: defaultAnimation };
      case 'paragraph':
        return { text: '', html: '<p>Start typing...</p>', animation: defaultAnimation };
      case 'image':
        return { url: '', alt: '', animation: defaultAnimation };
      case 'button':
        // Note: color / hoverColor / gradientColor are intentionally left
        // undefined so the button inherits the current site theme's
        // primary / link-hover / secondary colours. Users can still set
        // explicit colours from the Colors panel to override the theme.
        return { 
          text: 'Click Here',
          url: '#',
          style: 'solid', // solid, outline, ghost
          size: 'md', // sm, md, lg, xl
          textColor: '#ffffff',
          align: 'left', // left, center, right
          fullWidth: false,
          borderRadius: 'rounded', // square, rounded, pill
          openInNewTab: false,
          icon: 'none', // none, arrow-right, external-link, download, etc.
          iconPosition: 'right', // left, right
          animation: defaultAnimation
        };
      case 'hero':
        return { 
          imageUrl: '',
          headline: 'Your Headline Here',
          subheadline: 'Add a compelling subheadline to engage your audience',
          buttonText: 'Learn More',
          buttonUrl: '',
          overlay: 'dark',
          textPositionH: 'center',
          textPositionV: 'middle',
          textColor: 'white',
          height: 500,
          headlineAnimation: { entrance: 'fadeInUp', speed: 'normal', delay: '0' },
          subheadlineAnimation: { entrance: 'fadeInUp', speed: 'normal', delay: '200' },
          buttonAnimation: { entrance: 'fadeInUp', speed: 'normal', delay: '400' },
        };
      case 'code':
        return { 
          code: '// Your code here\nconst greeting = "Hello World";\nconsole.log(greeting);', 
          language: 'javascript',
          theme: 'github-dark',
          showLineNumbers: true,
          animation: defaultAnimation
        };
      case 'custom-html':
        return { 
          html: '<!-- Your custom HTML here -->\n<div class="custom-content">\n  <h2>Custom HTML Block</h2>\n  <p>Add your own HTML, CSS, and even embedded scripts.</p>\n</div>',
          enableScripts: false,
          animation: defaultAnimation
        };
      case 'quote':
        return { text: '', html: '<blockquote></blockquote>', animation: defaultAnimation };
      case 'list':
        return { items: [''], animation: defaultAnimation };
      case 'columns':
        return { 
          columnCount: 2,
          showAsCards: false,
          cardStyle: { backgroundColor: '#ffffff', borderRadius: '8px', padding: '16px', shadow: false },
          columns: [
            { type: 'text', html: '<p>Column 1</p>', imageUrl: '', imageAlt: '', imageWidth: 200, imageAlign: 'center', textAlign: 'left' },
            { type: 'text', html: '<p>Column 2</p>', imageUrl: '', imageAlt: '', imageWidth: 200, imageAlign: 'center', textAlign: 'left' },
          ],
          animation: defaultAnimation
        };
      case 'card-grid':
        return {
          cardsPerRow: 3,
          showAsCards: true,
          cardStyle: { backgroundColor: '#ffffff', borderRadius: '8px', padding: '16px', shadow: true },
          columns: [
            { type: 'card', html: '<h3>Card 1</h3><p>Card description</p>', imageUrl: '', imageAlt: '', imageWidth: 200, imageAlign: 'center', textAlign: 'left' },
            { type: 'card', html: '<h3>Card 2</h3><p>Card description</p>', imageUrl: '', imageAlt: '', imageWidth: 200, imageAlign: 'center', textAlign: 'left' },
            { type: 'card', html: '<h3>Card 3</h3><p>Card description</p>', imageUrl: '', imageAlt: '', imageWidth: 200, imageAlign: 'center', textAlign: 'left' },
          ],
          animation: defaultAnimation
        };
      case 'two-column':
        return { leftHtml: '<p>Left column text</p>', rightHtml: '<p>Right column text</p>', animation: defaultAnimation };
      case 'image-text-row':
        return { imageUrl: '', imageAlt: '', imagePosition: 'left', textHtml: '<p>Text content</p>', animation: defaultAnimation };
      case 'image-text-stack':
        return { imageUrl: '', imageAlt: '', imagePosition: 'top', textHtml: '<p>Text content</p>' };
      case 'form':
        return { formId: '', formName: '', animation: defaultAnimation };
      case 'gallery':
        return { 
          layout: 'grid-3', 
          images: [], 
          linkedFolderId: null, 
          linkedFolderName: null,
          gap: '16',
          imageRadius: '8',
          showCaptions: false,
          enableLightbox: true,
          aspectRatio: 'auto',
          animation: defaultAnimation 
        };
      case 'ticker':
        return {
          items: [],
          animationStyle: 'continuous', // 'continuous' or 'step'
          direction: 'left', // 'left' or 'right'
          speed: 'medium', // 'slow', 'medium', 'fast'
          pauseOnHover: true,
          backgroundColor: '#f8fafc',
          transparentBg: false,
          itemSpacing: 48,
          itemHeight: 48,
          verticalPadding: 16,
          logoFilter: 'none', // 'none', 'grayscale', 'grayscale-hover'
          animation: defaultAnimation,
        };
      case 'article-feed':
        return {
          layout: 'grid', // 'grid' or 'list'
          columns: 3,
          limit: 6,
          categoryFilter: '',
          showSearch: false,
          enablePagination: false,
          perPage: 6,
          showImage: true,
          showDate: true,
          showExcerpt: true,
          showCategory: true,
          heading: '',
          animation: defaultAnimation,
        };
      case 'events-calendar':
        return {
          title: 'Upcoming Events',
          defaultView: 'calendar',
          groupId: '',
          limit: 10,
          showRsvp: true,
          showIcal: true,
          animation: defaultAnimation,
        };
      case 'article-card':
        return {
          articleId: '',
          articleTitle: '',
          articleSlug: '',
          displayStyle: 'card', // 'card', 'full', 'headline'
          showImage: true,
          showExcerpt: true,
          showDate: true,
          showReadMore: true,
          readMoreText: 'Read Full Article',
          animation: defaultAnimation,
        };
      case 'newsletter-header':
        return {
          logoUrl: '',
          logoAlt: 'Logo',
          logoWidth: 150,
          showLogo: true,
          backgroundColor: '#6366f1',
          textColor: '#ffffff',
          links: [
            { text: 'Home', url: '#' },
            { text: 'About', url: '#' },
            { text: 'Contact', url: '#' },
          ],
          alignment: 'center', // 'left', 'center', 'right'
          padding: 20,
        };
      case 'video':
        return {
          videoUrl: '',
          caption: '',
          width: 'large',
          aspectRatio: '16:9',
          autoplay: false,
          muted: false,
          loop: false,
          controls: true,
          animation: defaultAnimation,
        };
      case 'testimonials':
        return {
          title: 'What Our Customers Say',
          titleStyle: { fontSize: '36', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          items: [
            { id: `ti-${Date.now()}-1`, quote: 'Absolutely brilliant service — exceeded our expectations.', author: 'Jane Smith', role: 'CEO, Acme Co', avatar: '' },
            { id: `ti-${Date.now()}-2`, quote: 'A game changer for our team. Highly recommended.', author: 'John Doe', role: 'Marketing Director', avatar: '' },
          ],
          animation: defaultAnimation,
        };
      case 'pricing':
        return {
          title: 'Pricing Plans',
          titleStyle: { fontSize: '36', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          items: [
            { id: `pp-${Date.now()}-1`, name: 'Basic', price: '$9', period: '/month', features: ['Feature 1', 'Feature 2'], highlighted: false, buttonText: 'Get Started', buttonUrl: '#' },
            { id: `pp-${Date.now()}-2`, name: 'Pro', price: '$29', period: '/month', features: ['Everything in Basic', 'Feature 3', 'Feature 4'], highlighted: true, buttonText: 'Get Started', buttonUrl: '#' },
            { id: `pp-${Date.now()}-3`, name: 'Enterprise', price: '$99', period: '/month', features: ['Everything in Pro', 'Priority Support', 'Custom Integrations'], highlighted: false, buttonText: 'Contact Sales', buttonUrl: '#' },
          ],
          animation: defaultAnimation,
        };
      case 'faq':
        return {
          title: 'Frequently Asked Questions',
          titleStyle: { fontSize: '36', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          items: [
            { id: `fq-${Date.now()}-1`, question: 'What is your service?', answer: 'We provide a range of solutions tailored to our customers.' },
            { id: `fq-${Date.now()}-2`, question: 'How do I get started?', answer: 'Just click the sign-up button and follow the simple steps.' },
            { id: `fq-${Date.now()}-3`, question: 'Can I cancel anytime?', answer: 'Yes, you can cancel your subscription at any time from your account.' },
          ],
          animation: defaultAnimation,
        };
      case 'cta':
        return {
          title: 'Ready to Get Started?',
          titleStyle: { fontSize: '36', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          subtitle: 'Join thousands of satisfied customers today',
          buttonText: 'Start Now',
          buttonUrl: '#',
          style: 'primary', // primary, secondary, dark
          animation: defaultAnimation,
        };
      case 'team':
        return {
          title: 'Meet Our Team',
          titleStyle: { fontSize: '36', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          members: [
            { id: `tm-${Date.now()}-1`, name: 'Jane Smith', role: 'CEO', image: '', bio: '' },
            { id: `tm-${Date.now()}-2`, name: 'John Doe', role: 'CTO', image: '', bio: '' },
            { id: `tm-${Date.now()}-3`, name: 'Sarah Lee', role: 'Designer', image: '', bio: '' },
          ],
          animation: defaultAnimation,
        };
      case 'stats':
        return {
          title: '',
          titleStyle: { fontSize: '36', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          items: [
            { id: `st-${Date.now()}-1`, value: '100+', label: 'Customers' },
            { id: `st-${Date.now()}-2`, value: '50+', label: 'Projects' },
            { id: `st-${Date.now()}-3`, value: '99%', label: 'Satisfaction' },
            { id: `st-${Date.now()}-4`, value: '24/7', label: 'Support' },
          ],
          animation: defaultAnimation,
        };
      case 'feature-comparison': {
        const t = Date.now();
        return {
          title: 'Compare Plans',
          subtitle: 'Find the plan that fits your team',
          titleStyle: { fontSize: '36', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          enableRecommender: false,
          recommenderTitle: 'Find the right plan for you',
          recommenderSubtitle: 'Answer a few quick questions and we\'ll recommend the best fit.',
          recommenderQuestions: [
            { id: `rq-${t}-1`, text: 'Do you need to support more than 10 team members?', yesPlanIds: [`pl-${t}-2`, `pl-${t}-3`] },
            { id: `rq-${t}-2`, text: 'Do you require SSO / SAML?', yesPlanIds: [`pl-${t}-3`] },
            { id: `rq-${t}-3`, text: 'Just trying things out?', yesPlanIds: [`pl-${t}-1`] },
          ],
          plans: [
            { id: `pl-${t}-1`, name: 'Starter', price: '$0', period: '/month', highlighted: false, buttonText: 'Get Started', buttonUrl: '#' },
            { id: `pl-${t}-2`, name: 'Pro', price: '$29', period: '/month', highlighted: true, badge: 'Most Popular', buttonText: 'Try Pro', buttonUrl: '#' },
            { id: `pl-${t}-3`, name: 'Enterprise', price: 'Custom', period: '', highlighted: false, buttonText: 'Contact Sales', buttonUrl: '#' },
          ],
          categories: [
            {
              id: `cat-${t}-1`,
              name: 'Core',
              features: [
                { id: `f-${t}-1`, name: 'Users', values: { [`pl-${t}-1`]: '3', [`pl-${t}-2`]: '25', [`pl-${t}-3`]: 'Unlimited' } },
                { id: `f-${t}-2`, name: 'Projects', values: { [`pl-${t}-1`]: '1', [`pl-${t}-2`]: '20', [`pl-${t}-3`]: 'Unlimited' } },
                { id: `f-${t}-3`, name: 'Storage', values: { [`pl-${t}-1`]: '1 GB', [`pl-${t}-2`]: '100 GB', [`pl-${t}-3`]: '1 TB' } },
              ],
            },
            {
              id: `cat-${t}-2`,
              name: 'Collaboration',
              features: [
                { id: `f-${t}-4`, name: 'Real-time editing', values: { [`pl-${t}-1`]: 'no', [`pl-${t}-2`]: 'yes', [`pl-${t}-3`]: 'yes' } },
                { id: `f-${t}-5`, name: 'Comments', values: { [`pl-${t}-1`]: 'yes', [`pl-${t}-2`]: 'yes', [`pl-${t}-3`]: 'yes' } },
                { id: `f-${t}-6`, name: 'SSO / SAML', values: { [`pl-${t}-1`]: 'no', [`pl-${t}-2`]: 'no', [`pl-${t}-3`]: 'yes' } },
              ],
            },
            {
              id: `cat-${t}-3`,
              name: 'Support',
              features: [
                { id: `f-${t}-7`, name: 'Email support', values: { [`pl-${t}-1`]: 'yes', [`pl-${t}-2`]: 'yes', [`pl-${t}-3`]: 'yes' } },
                { id: `f-${t}-8`, name: 'Priority support', values: { [`pl-${t}-1`]: 'no', [`pl-${t}-2`]: 'yes', [`pl-${t}-3`]: 'yes' } },
                { id: `f-${t}-9`, name: 'Dedicated CSM', values: { [`pl-${t}-1`]: 'no', [`pl-${t}-2`]: 'no', [`pl-${t}-3`]: 'yes' } },
              ],
            },
          ],
          animation: defaultAnimation,
        };
      }
      case 'plan-comparison': {
        const t = Date.now();
        return {
          title: 'Compare Plans',
          subtitle: 'All the features, side-by-side.',
          titleStyle: { fontSize: '36', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          source: 'pricing',
          plans: [],
          rows: [],
          enableRecommender: false,
          recommenderTitle: 'Find the right plan for you',
          recommenderSubtitle: 'Answer a few quick questions and we\'ll recommend the best fit.',
          recommenderQuestions: [{ id: `rq-${t}-1`, text: 'Do you need priority support?', yesPlanIds: [] }],
          animation: defaultAnimation,
        };
      }
      case 'logo-cloud':
        return {
          title: 'Trusted by leading teams',
          titleStyle: { fontSize: '30', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          grayscale: true,
          columns: 5,
          logos: [
            { id: `lg-${Date.now()}-1`, imageUrl: '', name: 'Acme', link: '' },
            { id: `lg-${Date.now()}-2`, imageUrl: '', name: 'Globex', link: '' },
            { id: `lg-${Date.now()}-3`, imageUrl: '', name: 'Initech', link: '' },
            { id: `lg-${Date.now()}-4`, imageUrl: '', name: 'Umbrella', link: '' },
            { id: `lg-${Date.now()}-5`, imageUrl: '', name: 'Soylent', link: '' },
          ],
          animation: defaultAnimation,
        };
      case 'hero-spotlight':
        return {
          eyebrow: 'NEW',
          headline: 'Build something people love',
          subheadline: 'A modern, animated hero section to make a strong first impression.',
          primaryButtonText: 'Get Started',
          primaryButtonUrl: '',
          secondaryButtonText: 'Learn more',
          secondaryButtonUrl: '',
          align: 'center',
          height: 560,
          animation: defaultAnimation,
        };
      case 'countdown': {
        const _d = new Date(Date.now() + 7 * 24 * 3600 * 1000);
        const _pad = (n) => String(n).padStart(2, '0');
        const _dl = `${_d.getFullYear()}-${_pad(_d.getMonth() + 1)}-${_pad(_d.getDate())}T${_pad(_d.getHours())}:${_pad(_d.getMinutes())}`;
        return {
          heading: 'Launching soon',
          subheading: "Don't miss it — the countdown is on.",
          targetDate: _dl,
          expiredText: "We're live!",
          buttonText: 'Notify me',
          buttonUrl: '',
          align: 'center',
          animation: defaultAnimation,
        };
      }
      case 'before-after':
        return {
          heading: 'See the difference',
          beforeImage: '',
          afterImage: '',
          beforeLabel: 'Before',
          afterLabel: 'After',
          height: 420,
          animation: defaultAnimation,
        };
      case 'hero-tabs':
        return {
          eyebrow: 'Featured',
          headline: 'One platform, endless possibilities',
          subtitle: 'A modern hero with a tabbed showcase. Switch tabs to highlight different views.',
          ctaText: 'Get Started', ctaUrl: '',
          titleStyle: { fontSize: '52', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          subtitleColor: '',
          tabs: [
            { id: `t-${Date.now()}-1`, label: 'Dashboard', imageUrl: '' },
            { id: `t-${Date.now()}-2`, label: 'Analytics', imageUrl: '' },
            { id: `t-${Date.now()}-3`, label: 'Reports', imageUrl: '' },
          ],
          animation: defaultAnimation,
        };
      case 'hero-feature-cards':
        return {
          headline: 'Invest in what shares your',
          accentWord: 'values.',
          subtitle: 'A simple, transparent platform designed for people who care about impact.',
          ctaText: 'Join the waitlist', ctaUrl: '',
          titleStyle: { fontSize: '52', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'left' },
          subtitleColor: '',
          features: [
            { id: `f-${Date.now()}-1`, title: 'Fight deforestation', imageUrl: '', link: '' },
            { id: `f-${Date.now()}-2`, title: 'Ocean health', imageUrl: '', link: '' },
            { id: `f-${Date.now()}-3`, title: 'Animal welfare', imageUrl: '', link: '' },
          ],
          animation: defaultAnimation,
        };
      case 'hero-split-stats':
        return {
          headline: 'A new way to learn & grow',
          subtitle: 'Courses and materials from skilled mentors around the world.',
          primaryButtonText: 'Get Started', primaryButtonUrl: '',
          secondaryButtonText: 'Learn more', secondaryButtonUrl: '',
          titleStyle: { fontSize: '50', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'left' },
          subtitleColor: '',
          stats: [
            { value: '15.2K', label: 'Active students' },
            { value: '4.5K', label: 'Mentors' },
            { value: '500+', label: 'Resources' },
          ],
          images: [
            { id: `i-${Date.now()}-1`, imageUrl: '' },
            { id: `i-${Date.now()}-2`, imageUrl: '' },
            { id: `i-${Date.now()}-3`, imageUrl: '' },
          ],
          animation: defaultAnimation,
        };
      case 'hero-split-cards':
        return {
          headline: 'Ready to transform your',
          accentWord: 'workflow?',
          subtitle: 'Experience the future of your operations with our platform. Start today!',
          buttonText: 'Download app', buttonUrl: '',
          titleStyle: { fontSize: '50', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'left' },
          subtitleColor: '',
          imageUrl1: '', imageUrl2: '',
          animation: defaultAnimation,
        };
      case 'expandable-tabs':
        return {
          heading: 'Quick navigation',
          subheading: 'Hover or tap an item to reveal its label.',
          titleStyle: { fontSize: '34', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          subtitleColor: '',
          tabs: [
            { id: `et-${Date.now()}-1`, label: 'Dashboard', icon: 'home', url: '' },
            { id: `et-${Date.now()}-2`, label: 'Notifications', icon: 'bell', url: '' },
            { type: 'separator' },
            { id: `et-${Date.now()}-3`, label: 'Settings', icon: 'settings', url: '' },
            { id: `et-${Date.now()}-4`, label: 'Support', icon: 'help', url: '' },
          ],
          animation: defaultAnimation,
        };
      case 'sparkles':
        return {
          headline: 'Build faster',
          subheadline: 'A glowing sparkles hero to make a stunning first impression.',
          buttonText: 'Get Started', buttonUrl: '',
          backgroundColor: '#020617',
          particleColor: '#FFFFFF',
          density: 120,
          height: 460,
          titleStyle: { fontSize: '64', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          subtitleColor: '',
          animation: defaultAnimation,
        };
      case 'gallery-carousel':
        return {
          title: 'Projects',
          description: 'A swipeable gallery of full-height image cards. Drag or use the arrows to browse.',
          titleStyle: { fontSize: '40', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'left' },
          subtitleColor: '',
          items: [
            { id: `gc-${Date.now()}-1`, title: 'Case study one', description: 'A short summary of this project and the results achieved.', href: '', imageUrl: '' },
            { id: `gc-${Date.now()}-2`, title: 'Case study two', description: 'A short summary of this project and the results achieved.', href: '', imageUrl: '' },
            { id: `gc-${Date.now()}-3`, title: 'Case study three', description: 'A short summary of this project and the results achieved.', href: '', imageUrl: '' },
          ],
          animation: defaultAnimation,
        };
      case 'testimonial-deck':
        return {
          heading: 'Loved by teams everywhere',
          subheading: 'Don’t just take our word for it.',
          autoplay: true,
          titleStyle: { fontSize: '38', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          subtitleColor: '',
          items: [
            { id: `td-${Date.now()}-1`, quote: 'This product completely changed how our team works. Onboarding was effortless.', name: 'Sarah Chen', role: 'Head of Product, Acme', avatarUrl: '', rating: 5 },
            { id: `td-${Date.now()}-2`, quote: 'The best investment we made this year. Support is incredibly responsive.', name: 'Marcus Lee', role: 'CTO, Northwind', avatarUrl: '', rating: 5 },
            { id: `td-${Date.now()}-3`, quote: 'Beautiful, fast, and reliable. Our customers noticed the difference immediately.', name: 'Priya Nair', role: 'Founder, Bloom', avatarUrl: '', rating: 5 },
          ],
          animation: defaultAnimation,
        };
      case 'liquid-glass':
        return {
          eyebrow: 'NEW',
          headline: 'Crafted with liquid glass',
          subtitle: 'A frosted, translucent surface that floats above your content.',
          buttonText: 'Get Started', buttonUrl: '',
          glassTint: 'light',
          backgroundImageUrl: '',
          backgroundColor: '',
          textColor: '',
          titleStyle: { fontSize: '44', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          animation: defaultAnimation,
        };
      case 'testimonials-columns':
        return {
          badge: 'Testimonials',
          heading: 'What our users say',
          subheading: 'See how teams are shipping faster with our platform.',
          speed: 40,
          titleStyle: { fontSize: '40', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          subtitleColor: '',
          items: [
            { id: `tc-${Date.now()}-1`, quote: 'Absolutely seamless experience from day one.', name: 'Alex Rivera', role: '@alexr', avatarUrl: '' },
            { id: `tc-${Date.now()}-2`, quote: 'We cut our launch time in half. Incredible.', name: 'Jamie Wu', role: '@jamiew', avatarUrl: '' },
            { id: `tc-${Date.now()}-3`, quote: 'The support team is fantastic and quick.', name: 'Dana Kim', role: '@danak', avatarUrl: '' },
            { id: `tc-${Date.now()}-4`, quote: 'Our conversion rate jumped 30% after switching.', name: 'Sam Patel', role: '@samp', avatarUrl: '' },
            { id: `tc-${Date.now()}-5`, quote: 'Clean, intuitive, and a joy to use daily.', name: 'Robin Fox', role: '@robinf', avatarUrl: '' },
            { id: `tc-${Date.now()}-6`, quote: 'Best decision we made for our stack.', name: 'Lee Morgan', role: '@leem', avatarUrl: '' },
          ],
          animation: defaultAnimation,
        };
      case 'typewriter':
        return {
          prefixText: "We're born 🌞 to ",
          phrases: ['experience', 'dance', 'love', 'be alive', 'create things that matter'],
          speed: 70, deleteSpeed: 40, waitTime: 1500, loop: true, cursorChar: '_',
          titleStyle: { fontSize: '44', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'left' },
          animation: defaultAnimation,
        };
      case 'gooey-text':
        return {
          texts: ['Design', 'Engineering', 'Is', 'Awesome'],
          morphTime: 1, cooldownTime: 0.25,
          titleStyle: { fontSize: '64', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          animation: defaultAnimation,
        };
      case 'text-cycle':
        return {
          prefix: 'Your',
          words: ['business', 'team', 'workflow', 'productivity', 'analytics', 'platform'],
          suffix: 'deserves better tools',
          interval: 3000,
          titleStyle: { fontSize: '44', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          animation: defaultAnimation,
        };
      case 'circular-testimonials':
        return {
          heading: '',
          autoplay: true,
          titleStyle: { fontSize: '38', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          subtitleColor: '',
          items: [
            { id: `ct-${Date.now()}-1`, quote: "I was impressed by the quality! The team was friendly and attentive. I'll definitely be back for more.", name: 'Tamar Mendelson', designation: 'Restaurant Critic', imageUrl: '' },
            { id: `ct-${Date.now()}-2`, quote: 'This place exceeded all expectations! The atmosphere is inviting and the staff goes above and beyond.', name: 'Joe Charlescraft', designation: 'Frequent Visitor', imageUrl: '' },
            { id: `ct-${Date.now()}-3`, quote: 'A hidden gem! The impeccable service and attention to detail created a memorable experience.', name: 'Martina Edelweist', designation: 'Satisfied Customer', imageUrl: '' },
          ],
          animation: defaultAnimation,
        };
      case 'stagger-testimonials':
        return {
          heading: 'What people are saying',
          autoplay: true,
          titleStyle: { fontSize: '38', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          subtitleColor: '',
          items: [
            { id: `st-${Date.now()}-1`, quote: 'My favorite solution in the market. We work 5x faster now.', name: 'Alex', role: 'CEO at TechCorp', avatarUrl: '' },
            { id: `st-${Date.now()}-2`, quote: "I'm confident my data is safe. I can't say that about other providers.", name: 'Dan', role: 'CTO at SecureNet', avatarUrl: '' },
            { id: `st-${Date.now()}-3`, quote: "We were lost before we found them. Can't thank you guys enough!", name: 'Stephanie', role: 'COO at InnovateCo', avatarUrl: '' },
            { id: `st-${Date.now()}-4`, quote: 'Planning for the future is seamless now. Highly recommend!', name: 'Marie', role: 'CFO at FuturePlanning', avatarUrl: '' },
            { id: `st-${Date.now()}-5`, quote: "If I could give 11 stars, I'd give 12.", name: 'Andre', role: 'Head of Design', avatarUrl: '' },
          ],
          animation: defaultAnimation,
        };
      case 'steps-icons':
        return {
          badge: 'Brand new',
          badgeColor: '#10b981',
          heading: 'How it works in three simple steps',
          subheading: 'Everything you need to get up and running, explained clearly.',
          accentColor: '',
          subtitleColor: '',
          titleStyle: { fontSize: '44', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          steps: [
            { id: `si-${Date.now()}-1`, icon: 'zap', title: 'Step 1', description: 'Create your free account in seconds and connect your data.', linkText: 'Learn more', linkUrl: '' },
            { id: `si-${Date.now()}-2`, icon: 'zap', title: 'Step 2', description: 'Customise your workspace and invite your team members.', linkText: 'Learn more', linkUrl: '' },
            { id: `si-${Date.now()}-3`, icon: 'zap', title: 'Step 3', description: 'Publish and watch everything come together beautifully.', linkText: 'Learn more', linkUrl: '' },
          ],
          animation: defaultAnimation,
        };
      case 'steps-cards':
        return {
          badge: 'Brand new',
          badgeColor: '#10b981',
          heading: 'A clear path from start to finish',
          subheading: 'Follow the numbered steps to reach your goal.',
          accentColor: '',
          subtitleColor: '',
          buttonText: 'Learn more', buttonUrl: '', buttonColor: '', buttonTextColor: '',
          titleStyle: { fontSize: '44', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          steps: [
            { id: `sc-${Date.now()}-1`, title: 'Believe', description: 'Start with a clear vision of what you want to achieve.' },
            { id: `sc-${Date.now()}-2`, title: 'Plan', description: 'Break the vision into achievable, measurable milestones.' },
            { id: `sc-${Date.now()}-3`, title: 'Build', description: 'Execute step by step with focus and consistency.' },
            { id: `sc-${Date.now()}-4`, title: 'Ship', description: 'Launch with confidence and iterate from real feedback.' },
          ],
          animation: defaultAnimation,
        };

      case 'steps-grid-image':
        return {
          heading: 'Before you begin',
          subheading: '',
          accentColor: '',
          subtitleColor: '',
          imageUrl: '',
          imagePosition: 'right',
          titleStyle: { fontSize: '40', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'left' },
          steps: [
            { id: `sg-${Date.now()}-1`, title: 'Read the recipe', description: "Take a good look at the recipe. Don't just skim it; read it through from start to finish.", badgeColor: '#7c3aed', highlight: true },
            { id: `sg-${Date.now()}-2`, title: 'Know the assumptions', description: 'All recipes are written using certain conventions, which define common ingredients.', badgeColor: '#10b981', highlight: false },
            { id: `sg-${Date.now()}-3`, title: 'Figure out the timing', description: 'Check the prep time and total time listed at the top to be sure you have enough time.', badgeColor: '#10b981', highlight: false },
            { id: `sg-${Date.now()}-4`, title: 'Plan ahead', description: 'Missing a prep instruction can leave you scrambling in the middle of a recipe.', badgeColor: '#7c3aed', highlight: false },
          ],
          animation: defaultAnimation,
        };
      case 'steps-timeline-image':
        return {
          heading: '',
          subheading: '',
          accentColor: '',
          subtitleColor: '',
          imageUrl: '',
          imagePosition: 'right',
          successLabel: 'Success',
          showSuccess: true,
          titleStyle: { fontSize: '40', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'left' },
          steps: [
            { id: `stl-${Date.now()}-1`, title: 'Step 1', description: 'All recipes are written using certain conventions, which define the characteristics of common ingredients.' },
            { id: `stl-${Date.now()}-2`, title: 'Step 2', description: 'The first mate and his Skipper too will do their very best to make the others comfortable.' },
            { id: `stl-${Date.now()}-3`, title: 'Step 3', description: 'Plan your milestones and set clear expectations for each stage.' },
            { id: `stl-${Date.now()}-4`, title: 'Step 4', description: 'Execute with focus and review the outcome against your goals.' },
          ],
          animation: defaultAnimation,
        };


      case 'handwriting-text':
        return {
          title: 'Hello, World',
          subtitle: 'A hand-drawn title that writes itself in.',
          titleStyle: { fontSize: '64', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          subtitleColor: '',
          animation: defaultAnimation,
        };
      case 'typewriter-smooth':
        return {
          prefix: 'The road to freedom starts from here',
          words: ['Build', 'awesome', 'apps', 'with', 'NexusCMS'],
          button1Text: 'Join now', button1Url: '',
          button2Text: 'Sign up', button2Url: '',
          titleStyle: { fontSize: '56', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          subtitleColor: '',
          animation: defaultAnimation,
        };
      case 'folder-gallery':
        return {
          heading: 'Our Gallery',
          subheading: 'Click the folder to fan out the photos.',
          folderLabel: 'Photos',
          folderColor: '#d97706',
          closedHint: 'Click the folder to open',
          openHint: 'Click the folder to close',
          titleStyle: { fontSize: '34', fontFamily: 'inherit', fontWeight: 'bold', color: '', align: 'center' },
          subtitleColor: '',
          items: [
            { id: `fg-${Date.now()}-1`, imageUrl: '' },
            { id: `fg-${Date.now()}-2`, imageUrl: '' },
            { id: `fg-${Date.now()}-3`, imageUrl: '' },
            { id: `fg-${Date.now()}-4`, imageUrl: '' },
            { id: `fg-${Date.now()}-5`, imageUrl: '' },
          ],
          animation: defaultAnimation,
        };
      default:
        return {};
    }
  };

  const addBlock = useCallback((type) => {
    const newBlock = {
      id: `block-${Date.now()}`,
      type,
      content: getDefaultContent(type),
      order: blocks.length,
      styles: {},
    };
    onChange([...blocks, newBlock]);
    setShowAddMenu(false);
    // Close slash menu if open
    setShowSlashMenu(false);
    setSlashQuery('');
  }, [blocks, onChange]);

  // Slash command handlers
  const handleSlashCommand = useCallback((e) => {
    const value = e.target.value;
    
    if (value.startsWith('/')) {
      setSlashQuery(value.slice(1)); // Remove the leading /
      if (!showSlashMenu) {
        // Position the menu near the input
        const rect = e.target.getBoundingClientRect();
        setSlashMenuPosition({
          top: rect.bottom + 8,
          left: rect.left
        });
        setShowSlashMenu(true);
      }
    } else {
      setShowSlashMenu(false);
      setSlashQuery('');
    }
  }, [showSlashMenu]);

  const handleSlashSelect = useCallback((type) => {
    addBlock(type);
    setShowSlashMenu(false);
    setSlashQuery('');
    if (slashInputRef.current) {
      slashInputRef.current.value = '';
    }
  }, [addBlock]);

  const handleSlashKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      setShowSlashMenu(false);
      setSlashQuery('');
      if (slashInputRef.current) {
        slashInputRef.current.value = '';
      }
    }
  }, []);

  const handleSnippetSelect = (snippetData) => {
    if (snippetTargetBlockId) {
      // Update existing block
      const updatedBlocks = blocks.map(block => 
        block.id === snippetTargetBlockId 
          ? { ...block, content: { ...block.content, code: snippetData.code, language: snippetData.language } }
          : block
      );
      onChange(updatedBlocks);
    }
    setShowSnippetBrowser(false);
    setSnippetTargetBlockId(null);
  };

  const openSnippetBrowser = (blockId) => {
    setSnippetTargetBlockId(blockId);
    setShowSnippetBrowser(true);
  };

  const updateBlock = (id, newContent, newStyles) => {
    onChange(
      blocks.map((block) =>
        block.id === id 
          ? { 
              ...block, 
              content: newContent !== undefined ? newContent : block.content,
              styles: newStyles !== undefined ? newStyles : block.styles
            } 
          : block
      )
    );
  };

  const deleteBlock = (id) => {
    onChange(blocks.filter((block) => block.id !== id));
  };

  const duplicateBlock = (block) => {
    const newBlock = {
      ...block,
      id: `block-${Date.now()}`,
      order: blocks.length,
    };
    onChange([...blocks, newBlock]);
  };

  // Apply preset styles to entire block
  const applyBlockPreset = (blockId, presetStyles) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block) return;
    
    if (presetStyles.preset === null) {
      // Reset preset - remove preset-related styles
      const updatedContent = { ...block.content };
      delete updatedContent.preset;
      delete updatedContent.presetStyles;
      updateBlock(blockId, updatedContent);
    } else {
      // Apply new preset
      updateBlock(blockId, {
        ...block.content,
        preset: presetStyles.preset,
        presetStyles: presetStyles
      });
    }
  };

  // Apply preset styles to specific columns within a columns block
  const applyColumnPreset = (blockId, presetStyles, columnIndices) => {
    const block = blocks.find(b => b.id === blockId);
    if (!block || block.type !== 'columns' || !block.content?.columns) return;
    
    const updatedColumns = block.content.columns.map((col, idx) => {
      if (columnIndices.includes(idx)) {
        if (presetStyles.preset === null) {
          // Reset preset - remove preset-related styles from column
          const { preset, presetStyles: _, ...rest } = col;
          return rest;
        } else {
          // Apply new preset to column
          return {
            ...col,
            preset: presetStyles.preset,
            presetStyles: presetStyles
          };
        }
      }
      return col;
    });
    
    updateBlock(blockId, {
      ...block.content,
      columns: updatedColumns
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      const oldIndex = blocks.findIndex((block) => block.id === active.id);
      const newIndex = blocks.findIndex((block) => block.id === over.id);

      const reorderedBlocks = arrayMove(blocks, oldIndex, newIndex).map((block, index) => ({
        ...block,
        order: index,
      }));

      onChange(reorderedBlocks);
    }
  };

  const handleMediaSelect = (media) => {
    if (mediaTargetBlockId) {
      // Check if it's a ticker tape item (format: "blockId:ticker:itemIndex")
      if (mediaTargetBlockId.includes(':ticker:')) {
        const parts = mediaTargetBlockId.split(':');
        const blockId = parts[0];
        const itemIndex = parseInt(parts[2]);
        const block = blocks.find(b => b.id === blockId);
        if (block && block.type === 'ticker') {
          const items = [...(block.content.items || [])];
          if (items[itemIndex]) {
            items[itemIndex] = { 
              ...items[itemIndex], 
              imageUrl: media.url, 
              imageAlt: media.alt_text || media.filename 
            };
            updateBlock(blockId, { ...block.content, items });
          }
        }
      // Generic Hero block image (format: "blockId:heroimg:<field>[:index]")
      } else if (mediaTargetBlockId.includes(':heroimg:')) {
        const segs = mediaTargetBlockId.split(':');
        const blockId = segs[0];
        const field = segs[2];
        const idxStr = segs[3];
        const block = blocks.find(b => b.id === blockId);
        if (block) {
          const c = { ...block.content };
          if (idxStr === undefined || idxStr === '') {
            c[field] = media.url;
          } else {
            const arr = [...(c[field] || [])];
            const i = parseInt(idxStr);
            if (arr[i]) { arr[i] = { ...arr[i], imageUrl: media.url }; c[field] = arr; }
          }
          updateBlock(blockId, c);
        }
      // Testimonials block avatar (format: "blockId:testiavatar:<index>") -> items[i].avatar
      } else if (mediaTargetBlockId.includes(':testiavatar:')) {
        const parts = mediaTargetBlockId.split(':');
        const blockId = parts[0];
        const i = parseInt(parts[2]);
        const block = blocks.find(b => b.id === blockId);
        if (block && block.type === 'testimonials') {
          const items = [...(block.content.items || [])];
          if (items[i]) { items[i] = { ...items[i], avatar: media.url }; updateBlock(blockId, { ...block.content, items }); }
        }
      // Testimonial avatar (format: "blockId:tavatar:<index>") -> items[i].avatarUrl
      } else if (mediaTargetBlockId.includes(':tavatar:')) {
        const parts = mediaTargetBlockId.split(':');
        const blockId = parts[0];
        const i = parseInt(parts[2]);
        const block = blocks.find(b => b.id === blockId);
        if (block) {
          const items = [...(block.content.items || [])];
          if (items[i]) { items[i] = { ...items[i], avatarUrl: media.url }; updateBlock(blockId, { ...block.content, items }); }
        }
      // Before/After slider image (format: "blockId:beforeafter:before|after")
      } else if (mediaTargetBlockId.includes(':beforeafter:')) {
        const parts = mediaTargetBlockId.split(':');
        const blockId = parts[0];
        const which = parts[2];
        const block = blocks.find(b => b.id === blockId);
        if (block && block.type === 'before-after') {
          const key = which === 'after' ? 'afterImage' : 'beforeImage';
          updateBlock(blockId, { ...block.content, [key]: media.url });
        }
      // Team member photo (format: "blockId:teammember:index") -> members[i].image
      } else if (mediaTargetBlockId.includes(':teammember:')) {
        const parts = mediaTargetBlockId.split(':');
        const blockId = parts[0];
        const i = parseInt(parts[2]);
        const block = blocks.find(b => b.id === blockId);
        if (block && block.type === 'team') {
          const members = [...(block.content.members || [])];
          if (members[i]) { members[i] = { ...members[i], image: media.url }; updateBlock(blockId, { ...block.content, members }); }
        }
      // Logo Cloud logo (format: "blockId:logocloud:index")
      } else if (mediaTargetBlockId.includes(':logocloud:')) {
        const parts = mediaTargetBlockId.split(':');
        const blockId = parts[0];
        const logoIndex = parseInt(parts[2]);
        const block = blocks.find(b => b.id === blockId);
        if (block && block.type === 'logo-cloud') {
          const logos = [...(block.content.logos || [])];
          if (logos[logoIndex]) {
            logos[logoIndex] = { ...logos[logoIndex], imageUrl: media.url, name: logos[logoIndex].name || media.alt_text || '' };
            updateBlock(blockId, { ...block.content, logos });
          }
        }
      // Check if it's a carousel slide (format: "blockId:carousel:slideIndex")
      } else if (mediaTargetBlockId.includes(':carousel:')) {
        const parts = mediaTargetBlockId.split(':');
        const blockId = parts[0];
        const slideIndex = parseInt(parts[2]);
        const block = blocks.find(b => b.id === blockId);
        if (block && block.type === 'image-text-row') {
          const slides = block.content.slides || [];
          // Create default slide if no slides exist
          const effectiveSlides = slides.length > 0 ? [...slides] : [{
            imageUrl: block.content.imageUrl || '',
            imageAlt: block.content.imageAlt || '',
            textHtml: block.content.textHtml || '<p>Text content</p>',
            imagePosition: block.content.imagePosition || 'left',
          }];
          effectiveSlides[slideIndex] = { 
            ...effectiveSlides[slideIndex], 
            imageUrl: media.url, 
            imageAlt: media.alt_text || media.filename 
          };
          updateBlock(blockId, { ...block.content, slides: effectiveSlides });
        }
      // Check if it's a columns block (format: "blockId:columnIndex")
      } else if (mediaTargetBlockId.includes(':')) {
        const [blockId, colIndexStr] = mediaTargetBlockId.split(':');
        const colIndex = parseInt(colIndexStr);
        const block = blocks.find(b => b.id === blockId);
        if (block && (block.type === 'columns' || block.type === 'card-grid')) {
          const newColumns = [...block.content.columns];
          newColumns[colIndex] = { 
            ...newColumns[colIndex], 
            imageUrl: media.url, 
            imageAlt: media.alt_text || media.filename 
          };
          updateBlock(blockId, { ...block.content, columns: newColumns });
        }
      } else {
        const block = blocks.find(b => b.id === mediaTargetBlockId);
        if (block) {
          if (block.type === 'image') {
            updateBlock(mediaTargetBlockId, { ...block.content, url: media.url, alt: media.alt_text || media.filename });
          } else if (block.type === 'image-text-row' || block.type === 'image-text-stack') {
            updateBlock(mediaTargetBlockId, { ...block.content, imageUrl: media.url, imageAlt: media.alt_text || media.filename });
          } else if (block.type === 'newsletter-header') {
            updateBlock(mediaTargetBlockId, { ...block.content, logoUrl: media.url, logoAlt: media.alt_text || media.filename || block.content.logoAlt });
          }
        }
      }
    }
    setMediaTargetBlockId(null);
  };

  const openMediaPicker = (blockId) => {
    setMediaTargetBlockId(blockId);
    setShowMediaPicker(true);
  };

  const renderBlockContent = (block, index, blockActions = {}) => {
    const { id, type, styles } = block;
    // Ensure content is always an object to prevent undefined errors
    const content = block.content || {};
    
    // Default block actions
    const {
      onMoveUp = () => {},
      onMoveDown = () => {},
      onDuplicate = () => {},
      onDelete = () => {},
      onTogglePublished = () => {},
      isFirst = false,
      isLast = false,
      isPublished = true,
    } = blockActions;
    
    // Block types that support section headers.
    // NOTE: 'hero' is intentionally excluded — a Hero is already a banner
    // with its own headline/subheadline. Stacking a small Section Header
    // above it creates visual conflict and the header was rendering
    // outside the hero image, which looked broken.
    const supportsHeader = ['columns', 'card-grid', 'image-text-row', 'image-text-stack', 'image', 'gallery', 'form', 'video', 'code', 'ticker', 'quote', 'list', 'article-feed', 'paragraph', 'heading', 'testimonials', 'pricing', 'feature-comparison', 'plan-comparison', 'faq', 'cta', 'team', 'stats', 'hero-spotlight', 'hero-tabs', 'hero-feature-cards', 'hero-split-stats', 'hero-split-cards', 'logo-cloud', 'countdown', 'before-after', 'expandable-tabs', 'sparkles', 'gallery-carousel', 'testimonial-deck', 'liquid-glass', 'testimonials-columns', 'typewriter', 'gooey-text', 'text-cycle', 'circular-testimonials', 'stagger-testimonials', 'handwriting-text', 'typewriter-smooth', 'folder-gallery', 'steps-icons', 'steps-cards', 'steps-grid-image', 'steps-timeline-image'].includes(type);

    // Block types that get the generic 5-level "Block Width" control.
    // Hero is excluded - it has its own dedicated full/wide/contained width logic.
    // Heading is excluded - it's always inline and sized by its parent section.
    const supportsBlockWidth = !['hero', 'heading'].includes(type) && [
      'columns', 'card-grid', 'image-text-row', 'image-text-stack', 'image', 'gallery',
      'form', 'video', 'code', 'ticker', 'quote', 'list', 'article-feed',
      'paragraph', 'testimonials', 'pricing', 'feature-comparison', 'plan-comparison', 'faq', 'cta', 'team', 'stats'
    ].includes(type);

    return (
      <>
        {/* Section Header Editor - for supported block types */}
        {supportsHeader && (
          <SectionHeaderEditor 
            content={content} 
            onChange={(newContent) => updateBlock(id, newContent)}
          />
        )}

        {/* Block Width Control - one consistent control across all eligible blocks */}
        {supportsBlockWidth && (
          <div className="mb-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <BlockWidthControl
              value={content.blockWidth || 'container'}
              onChange={(width) => updateBlock(id, { ...content, blockWidth: width })}
              testIdPrefix={`block-width-${id}`}
            />
          </div>
        )}
        
        {activeStyleBlockId === id && (
          <BlockStyleControls
            styles={styles || {}}
            onChange={(newStyles) => updateBlock(id, content, newStyles)}
            content={content}
            onContentChange={(newContent) => updateBlock(id, newContent, styles)}
          />
        )}

        {(type === 'heading' || type === 'paragraph') && (
          <>
            {/* Tab Navigation with Quick Actions */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Split Layout: Content always visible on left, Settings on right */}
            <BlockSplitPane
              showRightPane={getActiveTab(id) !== 'content' && getActiveTab(id) !== null}
              contentPane={
                <div className="space-y-4">
                  <TipTapEditor
                    content={content.html || (type === 'heading' ? '<h2>Heading</h2>' : '<p>Start typing...</p>')}
                    onChange={(html) => {
                      updateBlock(id, {
                        ...content,
                        html,
                        text: html.replace(/<[^>]*>/g, ''),
                      });
                    }}
                    placeholder={type === 'heading' ? 'Enter heading...' : 'Start typing...'}
                    enableAiAssist={true}
                  />
                  
                  {/* Add Button Option */}
                  <BlockButtonEditor
                    content={content}
                    onChange={(newContent) => updateBlock(id, newContent)}
                    label="Add Button"
                    pages={pages}
                    anchors={availableAnchors}
                  />
                </div>
              }
              settingsPane={
                <>
                  {/* Settings Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
                    <div className="space-y-4">
                      {/* Typography */}
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Typography</div>
                        <div className="flex flex-wrap items-center gap-3">
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">Font:</label>
                            <Select
                              value={(() => {
                                const storedFont = content.fontFamily || 'inherit';
                                const exactMatch = FONT_FAMILIES.find(f => f.value === storedFont);
                                if (exactMatch) return storedFont;
                                const partialMatch = FONT_FAMILIES.find(f => f.value.toLowerCase().startsWith(storedFont.toLowerCase()));
                                if (partialMatch) return partialMatch.value;
                                return 'inherit';
                              })()}
                              onValueChange={(value) => updateBlock(id, { ...content, fontFamily: value })}
                            >
                              <SelectTrigger className="w-32 h-8 text-xs">
                                <SelectValue placeholder="Font" />
                              </SelectTrigger>
                              <SelectContent>
                                {FONT_FAMILIES.map(font => (
                                  <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                                    {font.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">Size:</label>
                            <Select
                              value={content.fontSize || '16'}
                              onValueChange={(value) => updateBlock(id, { ...content, fontSize: value })}
                            >
                              <SelectTrigger className="w-24 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="12">12px</SelectItem>
                                <SelectItem value="14">14px</SelectItem>
                                <SelectItem value="16">16px</SelectItem>
                                <SelectItem value="18">18px</SelectItem>
                                <SelectItem value="20">20px</SelectItem>
                                <SelectItem value="24">24px</SelectItem>
                                <SelectItem value="28">28px</SelectItem>
                                <SelectItem value="32">32px</SelectItem>
                                <SelectItem value="36">36px</SelectItem>
                                <SelectItem value="48">48px</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">Weight:</label>
                            <Select
                              value={content.fontWeight || 'normal'}
                              onValueChange={(value) => updateBlock(id, { ...content, fontWeight: value })}
                            >
                              <SelectTrigger className="w-24 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="normal">Normal</SelectItem>
                                <SelectItem value="medium">Medium</SelectItem>
                                <SelectItem value="semibold">Semibold</SelectItem>
                                <SelectItem value="bold">Bold</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        
                        {/* Alignment and Color Row */}
                        <div className="flex flex-wrap items-center gap-3 mt-3 pt-3 border-t border-slate-200">
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">Align:</label>
                            <Select
                              value={content.textAlign || 'left'}
                              onValueChange={(value) => updateBlock(id, { ...content, textAlign: value })}
                            >
                              <SelectTrigger className="w-24 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="left">Left</SelectItem>
                                <SelectItem value="center">Center</SelectItem>
                                <SelectItem value="right">Right</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">Color:</label>
                            <ColorPickerSwatch value={content.textColor || '#1e293b'}
                              onChange={(e) => updateBlock(id, { ...content, textColor: e.target.value })}
                              className="w-8 h-8 rounded border cursor-pointer"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </BlockTabContent>
                  
                  {/* Style Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  
                  {/* Animation Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {type === 'image' && (
          <>
            {/* Tab Navigation with Quick Actions */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Split Layout: Image always visible on left, Settings on right */}
            <BlockSplitPane
              showRightPane={getActiveTab(id) !== 'content' && getActiveTab(id) !== null}
              contentPane={
                <div className="space-y-4">
                  {/* Image Selection */}
                  <div className="flex gap-2 items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openMediaPicker(id)}
                      data-testid={`open-media-library-${index}`}
                    >
                      <FolderOpen size={16} className="mr-2" />
                      {content.url ? 'Change Image' : 'Select Image'}
                    </Button>
                    {content.url && (
                      <>
                        <span className="text-xs text-green-600 flex items-center gap-1">
                          ✓ Image selected
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => updateBlock(id, { ...content, url: '', alt: '' })}
                          className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Remove image"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {/* Image Preview - Always visible */}
                  {content.url && (
                    <div style={{ textAlign: styles?.imageAlign || 'center' }}>
                      {content.shapeMask && content.shapeMask !== 'none' ? (
                        <div 
                          className="inline-block overflow-hidden"
                          style={{
                            width: `${content.shapeSize || 300}px`,
                            height: content.shapeMask === 'ellipse' ? `${(content.shapeSize || 300) * 1.25}px` : `${content.shapeSize || 300}px`,
                            maxWidth: '100%',
                            borderRadius: content.shapeMask === 'circle' ? '50%' : 
                                         content.shapeMask === 'ellipse' ? '50%' :
                                         content.shapeMask === 'rounded' ? '20px' : '0',
                            clipPath: content.shapeMask === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
                                     content.shapeMask === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' :
                                     content.shapeMask === 'octagon' ? 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' :
                                     content.shapeMask === 'star' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' :
                                     content.shapeMask === 'arch' ? 'ellipse(50% 100% at 50% 100%)' : 'none',
                            border: content.shapeBorderWidth ? `${content.shapeBorderWidth}px solid ${content.shapeBorderColor || '#ffffff'}` : 'none',
                          }}
                        >
                          <img 
                            src={content.url} 
                            alt={content.alt || ''} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <img 
                          src={content.url} 
                          alt={content.alt || ''} 
                          className="rounded mt-2 inline-block" 
                          style={{ 
                            width: styles?.imageSizeMode === 'height' ? 'auto' : (styles?.imageWidth || '100%'),
                            height: styles?.imageSizeMode === 'height' ? (styles?.imageHeight || '300px') : 'auto',
                            maxWidth: '100%',
                          }}
                        />
                      )}
                    </div>
                  )}
                  
                  {/* Add Button Option */}
                  <BlockButtonEditor 
                    content={content} 
                    onChange={(newContent) => updateBlock(id, newContent)}
                    label="Add Button Below Image"
                    pages={pages}
                    anchors={availableAnchors}
                  />
                </div>
              }
              settingsPane={
                <>
                  {/* Settings Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
                    <div className="space-y-4">
                      {/* Alt Text & Caption */}
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Alt Text (Accessibility)</label>
                          <Input
                            value={content.alt || ''}
                            onChange={(e) => updateBlock(id, { ...content, alt: e.target.value })}
                            placeholder="Describe image for screen readers"
                            data-testid={`image-alt-input-${index}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Caption (Visible below image)</label>
                          <Input
                            value={content.caption || ''}
                            onChange={(e) => updateBlock(id, { ...content, caption: e.target.value })}
                            placeholder="Optional visible caption"
                            data-testid={`image-caption-input-${index}`}
                          />
                        </div>
                        <div>
                          <label className="text-xs text-slate-500 mb-1 block">Link URL (Make image clickable)</label>
                          <Input
                            value={content.linkUrl || ''}
                            onChange={(e) => updateBlock(id, { ...content, linkUrl: e.target.value })}
                            placeholder="https://..."
                            data-testid={`image-link-url-${index}`}
                          />
                          {content.linkUrl && (
                            <label className="flex items-center gap-2 text-xs text-slate-500 mt-1.5 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={content.linkNewTab !== false}
                                onChange={(e) => updateBlock(id, { ...content, linkNewTab: e.target.checked })}
                                className="rounded border-slate-300"
                              />
                              Open in new tab
                            </label>
                          )}
                        </div>
                      </div>
                      
                      {/* Layout Options */}
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Layout</div>
                        <div className="space-y-3">
                          <label className="flex items-center gap-2 text-sm cursor-pointer">
                            <input
                              type="checkbox"
                              checked={content.fullWidth || false}
                              onChange={(e) => updateBlock(id, { ...content, fullWidth: e.target.checked })}
                              className="rounded border-slate-300"
                            />
                            <span>Full Width Banner</span>
                          </label>
                          
                          {/* Padding Controls */}
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-slate-500">Padding:</label>
                            <Select
                              value={content.paddingSide || 'all'}
                              onValueChange={(value) => updateBlock(id, { ...content, paddingSide: value })}
                            >
                              <SelectTrigger className="w-20 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="all">All</SelectItem>
                                <SelectItem value="top">Top</SelectItem>
                                <SelectItem value="right">Right</SelectItem>
                                <SelectItem value="bottom">Bottom</SelectItem>
                                <SelectItem value="left">Left</SelectItem>
                              </SelectContent>
                            </Select>
                            <Input
                              type="number"
                              value={content.paddingSize || 0}
                              onChange={(e) => updateBlock(id, { ...content, paddingSize: parseInt(e.target.value) || 0 })}
                              className="w-20 h-8 text-xs"
                              min="0"
                              max="100"
                            />
                            <span className="text-xs text-slate-400">px</span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Shape Mask */}
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Shape Mask</div>
                        <div className="space-y-2">
                          <Select
                            value={content.shapeMask || 'none'}
                            onValueChange={(value) => updateBlock(id, { ...content, shapeMask: value })}
                          >
                            <SelectTrigger className="w-full h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Mask</SelectItem>
                              <SelectItem value="circle">Circle</SelectItem>
                              <SelectItem value="ellipse">Ellipse</SelectItem>
                              <SelectItem value="rounded">Rounded</SelectItem>
                              <SelectItem value="diamond">Diamond</SelectItem>
                              <SelectItem value="hexagon">Hexagon</SelectItem>
                              <SelectItem value="octagon">Octagon</SelectItem>
                              <SelectItem value="star">Star</SelectItem>
                              <SelectItem value="arch">Arch</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {content.shapeMask && content.shapeMask !== 'none' && (
                            <div className="flex flex-wrap items-center gap-2 pt-2">
                              <div className="flex items-center gap-1">
                                <label className="text-xs text-slate-500">Size:</label>
                                <Input
                                  type="number"
                                  value={content.shapeSize || 300}
                                  onChange={(e) => updateBlock(id, { ...content, shapeSize: parseInt(e.target.value) || 300 })}
                                  className="w-20 h-8 text-xs"
                                  min="50"
                                  max="800"
                                />
                                <span className="text-xs text-slate-400">px</span>
                              </div>
                              
                              <div className="flex items-center gap-1">
                                <label className="text-xs text-slate-500">Border:</label>
                                <Input
                                  type="number"
                                  value={content.shapeBorderWidth || 0}
                                  onChange={(e) => updateBlock(id, { ...content, shapeBorderWidth: parseInt(e.target.value) || 0 })}
                                  className="w-20 h-8 text-xs"
                                  min="0"
                                  max="20"
                                />
                                <ColorPickerSwatch value={content.shapeBorderColor || '#ffffff'}
                                  onChange={(e) => updateBlock(id, { ...content, shapeBorderColor: e.target.value })}
                                  className="w-8 h-8 rounded border cursor-pointer"
                                  title="Border Color"
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </BlockTabContent>
                  
                  {/* Style Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  
                  {/* Animation Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {type === 'button' && (
          <>
            {/* Tab Navigation with Quick Actions */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Split Layout: Content always visible on left, Settings on right */}
            <BlockSplitPane
              showRightPane={['settings', 'style', 'animation'].includes(getActiveTab(id))}
              contentPane={
                <div className="space-y-4">
                  {/* Saved button styles (presets). Applies the variant, colours,
                      corners & size; this block keeps its own icon & hover colour. */}
                  <ButtonPresetsBar
                    currentStyle={{ style: content.style, size: content.size, color: content.color, textColor: content.textColor, gradientColor: content.gradientColor, radius: content.borderRadius }}
                    onApply={(nested) => {
                      const patch = {};
                      if (nested.style !== undefined) patch.style = nested.style;
                      if (nested.size !== undefined) patch.size = nested.size;
                      if (nested.color !== undefined) patch.color = nested.color;
                      if (nested.textColor !== undefined) patch.textColor = nested.textColor;
                      if (nested.gradientColor !== undefined) patch.gradientColor = nested.gradientColor;
                      if (nested.radius !== undefined) patch.borderRadius = nested.radius;
                      updateBlock(id, { ...content, ...patch });
                    }}
                  />
                  {/* Button Preview */}
                  <div 
                    className="p-4 bg-slate-50 rounded-lg border border-slate-200"
                    style={{ textAlign: content.align || 'left' }}
                  >
                    <a
                      href={content.url || '#'}
                      target={content.openInNewTab ? '_blank' : '_self'}
                      rel={content.openInNewTab ? 'noopener noreferrer' : ''}
                      className={`
                        inline-flex items-center gap-2 font-medium transition-all
                        ${content.size === 'sm' ? 'px-3 py-1.5 text-sm' : ''}
                        ${content.size === 'md' || !content.size ? 'px-5 py-2.5 text-base' : ''}
                        ${content.size === 'lg' ? 'px-6 py-3 text-lg' : ''}
                        ${content.size === 'xl' ? 'px-8 py-4 text-xl' : ''}
                        ${content.fullWidth ? 'w-full justify-center' : ''}
                        ${content.borderRadius === 'square' ? 'rounded-none' : ''}
                        ${content.borderRadius === 'rounded' || !content.borderRadius ? 'rounded-lg' : ''}
                        ${content.borderRadius === 'pill' ? 'rounded-full' : ''}
                      `}
                      style={{
                        ...(content.style === 'gradient'
                          ? { background: `linear-gradient(135deg, ${content.color || themeBtnColor}, ${content.gradientColor || themeBtnGradient})`, color: content.textColor || '#ffffff' }
                          : content.style === 'soft'
                            ? { backgroundColor: `${content.color || themeBtnColor}26`, color: content.color || themeBtnColor }
                            : {
                                backgroundColor: content.style === 'solid' || !content.style ? (content.color || themeBtnColor) : 'transparent',
                                color: content.style === 'solid' || !content.style ? (content.textColor || '#ffffff') : (content.color || themeBtnColor),
                                border: content.style === 'outline' ? `2px solid ${content.color || themeBtnColor}` : 'none',
                              }),
                      }}
                      onClick={(e) => e.preventDefault()}
                    >
                      {content.icon && content.icon !== 'none' && content.iconPosition === 'left' && (
                        <span className="inline-flex items-center">{renderButtonIcon(content.icon, 16, content.iconColor)}</span>
                      )}
                      {content.text || 'Click Here'}
                      {content.icon && content.icon !== 'none' && (content.iconPosition === 'right' || !content.iconPosition) && (
                        <span className="inline-flex items-center">{renderButtonIcon(content.icon, 16, content.iconColor)}</span>
                      )}
                    </a>
                    <p className="text-xs text-slate-400 mt-2">Preview (click disabled)</p>
                  </div>

                  {/* Button Text */}
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Button Text</label>
                    <Input
                      value={content.text || ''}
                      onChange={(e) => updateBlock(id, { ...content, text: e.target.value })}
                      placeholder="Click Here"
                      data-testid={`button-text-${index}`}
                    />
                  </div>
                  
                  {/* Link URL with Selector */}
                  <LinkSelector
                    value={content.url || ''}
                    onChange={(url) => updateBlock(id, { ...content, url })}
                    pages={pages}
                    anchors={availableAnchors}
                    label="Link URL"
                    testId={`button-url-${index}`}
                  />
                </div>
              }
              settingsPane={
                <>
                  {/* Settings Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
                    <div className="space-y-4">
                      {/* Style & Size */}
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Appearance</div>
                        <div className="grid grid-cols-2 gap-3">
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Style</label>
                            <Select
                              value={content.style || 'solid'}
                              onValueChange={(value) => updateBlock(id, { ...content, style: value })}
                            >
                              <SelectTrigger className="h-9" data-testid={`button-style-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="solid">Solid</SelectItem>
                                <SelectItem value="outline">Outline</SelectItem>
                                <SelectItem value="ghost">Ghost</SelectItem>
                                <SelectItem value="gradient">Gradient</SelectItem>
                                <SelectItem value="soft">Soft</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Size</label>
                            <Select
                              value={content.size || 'md'}
                              onValueChange={(value) => updateBlock(id, { ...content, size: value })}
                            >
                              <SelectTrigger className="h-9" data-testid={`button-size-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="sm">Small</SelectItem>
                                <SelectItem value="md">Medium</SelectItem>
                                <SelectItem value="lg">Large</SelectItem>
                                <SelectItem value="xl">Extra Large</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Corners</label>
                            <Select
                              value={content.borderRadius || 'rounded'}
                              onValueChange={(value) => updateBlock(id, { ...content, borderRadius: value })}
                            >
                              <SelectTrigger className="h-9" data-testid={`button-radius-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="square">Square</SelectItem>
                                <SelectItem value="rounded">Rounded</SelectItem>
                                <SelectItem value="pill">Pill</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Alignment</label>
                            <Select
                              value={content.align || 'left'}
                              onValueChange={(value) => updateBlock(id, { ...content, align: value })}
                            >
                              <SelectTrigger className="h-9" data-testid={`button-align-${index}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="left">Left</SelectItem>
                                <SelectItem value="center">Center</SelectItem>
                                <SelectItem value="right">Right</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>

                      {/* Colors */}
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="flex items-center justify-between mb-3">
                          <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Colors</div>
                          {(content.color || content.hoverColor || content.gradientColor) && (
                            <button
                              type="button"
                              data-testid={`button-reset-theme-colors-${index}`}
                              onClick={() => {
                                const { color: _c, hoverColor: _h, gradientColor: _g, ...rest } = content;
                                updateBlock(id, rest);
                              }}
                              className="text-[10px] font-medium text-indigo-600 hover:text-indigo-700 underline"
                              title="Clear the colours below so this button follows your site theme"
                            >
                              Reset to theme
                            </button>
                          )}
                        </div>
                        {!(content.color || content.hoverColor || content.gradientColor) && (
                          <div className="mb-3 text-[11px] text-indigo-600 bg-indigo-50 border border-indigo-100 rounded px-2 py-1.5">
                            Following site theme. Pick a colour below to override.
                          </div>
                        )}
                        <div className="space-y-3">
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Button Color</label>
                            <div className="flex items-center gap-2">
                              <ColorPickerSwatch value={content.color || themeBtnColor}
                                onChange={(e) => updateBlock(id, { ...content, color: e.target.value })}
                                className="w-9 h-9 rounded border border-slate-200 cursor-pointer"
                              />
                              <Input
                                value={content.color || ''}
                                onChange={(e) => updateBlock(id, { ...content, color: e.target.value })}
                                className="h-9 font-mono text-xs flex-1"
                                placeholder={`Theme (${themeBtnColor})`}
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Text Color</label>
                            <div className="flex items-center gap-2">
                              <ColorPickerSwatch value={content.textColor || '#ffffff'}
                                onChange={(e) => updateBlock(id, { ...content, textColor: e.target.value })}
                                className="w-9 h-9 rounded border border-slate-200 cursor-pointer"
                              />
                              <ColorHexInput
                                value={content.textColor || ''}
                                onChange={(e) => updateBlock(id, { ...content, textColor: e.target.value })}
                                className="h-9 font-mono text-xs flex-1"
                                placeholder="#ffffff"
                              />
                            </div>
                          </div>
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Hover Color</label>
                            <div className="flex items-center gap-2">
                              <ColorPickerSwatch value={content.hoverColor || themeBtnHover}
                                onChange={(e) => updateBlock(id, { ...content, hoverColor: e.target.value })}
                                className="w-9 h-9 rounded border border-slate-200 cursor-pointer"
                              />
                              <Input
                                value={content.hoverColor || ''}
                                onChange={(e) => updateBlock(id, { ...content, hoverColor: e.target.value })}
                                className="h-9 font-mono text-xs flex-1"
                                placeholder={`Theme (${themeBtnHover})`}
                              />
                            </div>
                          </div>
                          {content.style === 'gradient' && (
                            <div>
                              <label className="text-xs text-slate-500 mb-1 block">Gradient End Color</label>
                              <div className="flex items-center gap-2">
                                <ColorPickerSwatch data-testid={`button-gradient-color-${index}`}
                                  value={content.gradientColor || themeBtnGradient}
                                  onChange={(e) => updateBlock(id, { ...content, gradientColor: e.target.value })}
                                  className="w-9 h-9 rounded border border-slate-200 cursor-pointer"
                                />
                                <Input
                                  value={content.gradientColor || ''}
                                  onChange={(e) => updateBlock(id, { ...content, gradientColor: e.target.value })}
                                  className="h-9 font-mono text-xs flex-1"
                                  placeholder={`Theme (${themeBtnGradient})`}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Additional Options */}
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Options</div>
                        <div className="space-y-3">
                          <div className="flex flex-wrap items-center gap-3">
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={content.fullWidth || false}
                                onChange={(e) => updateBlock(id, { ...content, fullWidth: e.target.checked })}
                                className="rounded border-slate-300"
                              />
                              <span className="text-xs">Full Width</span>
                            </label>
                            <label className="flex items-center gap-2 text-sm cursor-pointer">
                              <input
                                type="checkbox"
                                checked={content.openInNewTab || false}
                                onChange={(e) => updateBlock(id, { ...content, openInNewTab: e.target.checked })}
                                className="rounded border-slate-300"
                              />
                              <span className="text-xs">Open in New Tab</span>
                            </label>
                          </div>
                          
                          <div>
                            <label className="text-xs text-slate-500 mb-1 block">Icon</label>
                            <Select
                              value={content.icon || 'none'}
                              onValueChange={(value) => updateBlock(id, { ...content, icon: value })}
                            >
                              <SelectTrigger className="h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent className="max-h-72">
                                {BUTTON_ICON_OPTIONS.map((opt) => (
                                  <SelectItem key={opt.value} value={opt.value}>
                                    <span className="inline-flex items-center gap-2">
                                      {opt.value !== 'none' && renderButtonIcon(opt.value, 14)}
                                      {opt.label}
                                    </span>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {content.icon && content.icon !== 'none' && (
                            <div>
                              <label className="text-xs text-slate-500 mb-1 block">Icon Position</label>
                              <Select
                                value={content.iconPosition || 'right'}
                                onValueChange={(value) => updateBlock(id, { ...content, iconPosition: value })}
                              >
                                <SelectTrigger className="h-8 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="left">Left</SelectItem>
                                  <SelectItem value="right">Right</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </BlockTabContent>
                  
                  {/* Style Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  
                  {/* Animation Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {type === 'quote' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />

            {/* Split Layout: Quote editor always visible on the left,
                Settings / Style / Animation appear on the right. Fixes the
                bug where switching to Style hid the editor. */}
            <BlockSplitPane
              showRightPane={['settings', 'style', 'animation'].includes(getActiveTab(id))}
              contentPane={
                <div className="space-y-3">
                  <blockquote
                    className="border-l-4 pl-4 py-2"
                    style={{ borderColor: content.color || '#4F46E5' }}
                  >
                    <Textarea
                      value={content.text || ''}
                      onChange={(e) => updateBlock(id, { ...content, text: e.target.value })}
                      className="border-none p-0 focus:ring-0 resize-none bg-transparent"
                      style={{
                        fontFamily: content.fontFamily || 'Merriweather, serif',
                        fontSize: content.fontSize ? `${content.fontSize}px` : '20px',
                        fontStyle: content.fontStyle || 'italic',
                        fontWeight: content.fontWeight || 'normal',
                        color: content.color || '#334155',
                        textAlign: content.align || 'left',
                      }}
                      placeholder="Quote text..."
                      rows={3}
                      data-testid={`quote-input-${index}`}
                    />
                  </blockquote>
                  <Input
                    value={content.author || ''}
                    onChange={(e) => updateBlock(id, { ...content, author: e.target.value })}
                    placeholder="Attribution (optional, e.g., Mark Twain)"
                    className="text-sm"
                    data-testid={`quote-author-${index}`}
                  />
                </div>
              }
              settingsPane={
                <>
                  {/* Settings Tab — Font controls */}
                  <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
                    <div className="space-y-3">
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100 space-y-2">
                        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Typography</div>

                        {/* Font Family */}
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-slate-500 w-16">Font:</Label>
                          <Select
                            value={content.fontFamily || 'Merriweather, serif'}
                            onValueChange={(value) => updateBlock(id, { ...content, fontFamily: value })}
                          >
                            <SelectTrigger className="h-8 text-xs flex-1" data-testid={`quote-font-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FONT_FAMILIES.map((font) => (
                                <SelectItem key={font.value} value={font.value} style={{ fontFamily: font.value }}>
                                  {font.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Font Size */}
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-slate-500 w-16">Size:</Label>
                          <Select
                            value={String(content.fontSize || 20)}
                            onValueChange={(value) => updateBlock(id, { ...content, fontSize: value })}
                          >
                            <SelectTrigger className="h-8 text-xs flex-1" data-testid={`quote-size-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {['14','16','18','20','24','28','32','36','42','48','56'].map((s) => (
                                <SelectItem key={s} value={s}>{s}px</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Font Style (italic toggle) */}
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-slate-500 w-16">Style:</Label>
                          <Select
                            value={content.fontStyle || 'italic'}
                            onValueChange={(value) => updateBlock(id, { ...content, fontStyle: value })}
                          >
                            <SelectTrigger className="h-8 text-xs flex-1" data-testid={`quote-style-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="italic">Italic</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Font Weight */}
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-slate-500 w-16">Weight:</Label>
                          <Select
                            value={content.fontWeight || 'normal'}
                            onValueChange={(value) => updateBlock(id, { ...content, fontWeight: value })}
                          >
                            <SelectTrigger className="h-8 text-xs flex-1" data-testid={`quote-weight-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="normal">Normal</SelectItem>
                              <SelectItem value="500">Medium</SelectItem>
                              <SelectItem value="600">Semi Bold</SelectItem>
                              <SelectItem value="bold">Bold</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Color */}
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-slate-500 w-16">Color:</Label>
                          <ColorPickerSwatch value={content.color || '#334155'}
                            onChange={(e) => updateBlock(id, { ...content, color: e.target.value })}
                            className="w-8 h-8 rounded border cursor-pointer"
                            data-testid={`quote-color-${index}`}
                          />
                          <ColorHexInput
                            value={content.color || ''}
                            onChange={(e) => updateBlock(id, { ...content, color: e.target.value })}
                            className="h-8 text-xs flex-1 font-mono"
                            placeholder="#334155"
                          />
                        </div>

                        {/* Alignment */}
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-slate-500 w-16">Align:</Label>
                          <Select
                            value={content.align || 'left'}
                            onValueChange={(value) => updateBlock(id, { ...content, align: value })}
                          >
                            <SelectTrigger className="h-8 text-xs flex-1" data-testid={`quote-align-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="left">Left</SelectItem>
                              <SelectItem value="center">Center</SelectItem>
                              <SelectItem value="right">Right</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    </div>
                  </BlockTabContent>

                  {/* Style Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>

                  {/* Animation Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {type === 'list' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Content Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'content'}>
              <div className="space-y-2">
                {content.items?.map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <span className="text-slate-400">•</span>
                    <Input
                      value={item}
                      onChange={(e) => {
                        const newItems = [...content.items];
                        newItems[i] = e.target.value;
                        updateBlock(id, { ...content, items: newItems });
                      }}
                      placeholder={`Item ${i + 1}`}
                      data-testid={`list-item-input-${index}-${i}`}
                    />
                    {content.items.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newItems = content.items.filter((_, idx) => idx !== i);
                          updateBlock(id, { ...content, items: newItems });
                        }}
                        className="text-red-500 hover:text-red-700"
                      >
                        <Trash2 size={14} />
                      </Button>
                    )}
                  </div>
                ))}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => updateBlock(id, { ...content, items: [...content.items, ''] })}
                  data-testid={`add-list-item-${index}`}
                >
                  <Plus size={14} className="mr-1" />
                  Add Item
                </Button>
              </div>
            </BlockTabContent>
            
            {/* Settings Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
              <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg">
                No additional settings for list blocks.
              </div>
            </BlockTabContent>
            
            {/* Style Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'style'}>
              <BlockStyleControls
                styles={styles || {}}
                onChange={(newStyles) => updateBlock(id, content, newStyles)}
                content={content}
                onContentChange={(newContent) => updateBlock(id, newContent, styles)}
              />
            </BlockTabContent>
            
            {/* Animation Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
              <AnimationControls
                animation={content.animation || {}}
                onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
              />
            </BlockTabContent>
          </>
        )}

        {type === 'code' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Content Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'content'}>
              <div className="space-y-4">
                {/* Code Input */}
                <Textarea
                  value={content.code || ''}
                  onChange={(e) => updateBlock(id, { ...content, code: e.target.value })}
                  className="font-mono text-sm min-h-[200px] bg-slate-900 text-slate-100 border-slate-700"
                  placeholder="// Enter your code here..."
                  data-testid={`code-input-${index}`}
                  spellCheck={false}
                />

                {/* Live Preview */}
                <div>
                  <Label className="text-xs text-slate-500 mb-2 block">Preview:</Label>
                  <CodeBlock 
                    code={content.code || '// Your code here'} 
                    language={content.language || 'javascript'}
                    theme={content.theme || 'github-dark'}
                    showLineNumbers={content.showLineNumbers !== false}
                  />
                </div>
              </div>
            </BlockTabContent>
            
            {/* Settings Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Code Settings</div>
                  <div className="flex flex-wrap items-center gap-4">
                    {/* Language Selector */}
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-medium">Language:</Label>
                      <Select 
                        value={content.language || 'javascript'} 
                        onValueChange={(value) => updateBlock(id, { ...content, language: value })}
                      >
                        <SelectTrigger className="w-36">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CODE_LANGUAGES.map(lang => (
                            <SelectItem key={lang.value} value={lang.value}>{lang.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Theme Selector */}
                    <div className="flex items-center gap-2">
                      <Label className="text-sm font-medium">Theme:</Label>
                      <Select 
                        value={content.theme || 'github-dark'} 
                        onValueChange={(value) => updateBlock(id, { ...content, theme: value })}
                      >
                        <SelectTrigger className="w-40">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CODE_THEMES.map(t => (
                            <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Line Numbers Toggle */}
                    <div className="flex items-center gap-2">
                      <Switch
                        id={`line-numbers-${id}`}
                        checked={content.showLineNumbers !== false}
                        onCheckedChange={(checked) => updateBlock(id, { ...content, showLineNumbers: checked })}
                      />
                      <Label htmlFor={`line-numbers-${id}`} className="text-sm">Line Numbers</Label>
                    </div>
                  </div>
                </div>

                {/* Browse Snippets Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openSnippetBrowser(id)}
                  className="gap-2"
                >
                  <Code size={14} />
                  Browse Snippets
                </Button>
              </div>
            </BlockTabContent>
            
            {/* Style Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'style'}>
              <BlockStyleControls
                styles={styles || {}}
                onChange={(newStyles) => updateBlock(id, content, newStyles)}
                content={content}
                onContentChange={(newContent) => updateBlock(id, newContent, styles)}
              />
            </BlockTabContent>
            
            {/* Animation Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
              <AnimationControls
                animation={content.animation || {}}
                onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
              />
            </BlockTabContent>
          </>
        )}

        {type === 'unsubscribe' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            disabledTabs={['style', 'animation']}
            />
            
            {/* Content Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'content'}>
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                  <MailX size={16} className="text-red-500" />
                  <span className="text-sm font-medium text-slate-700">Unsubscribe Link Block</span>
                </div>
                
                <div>
                  <Label className="text-sm font-medium">Link Text</Label>
                  <Input
                    value={content.text || 'Unsubscribe from this newsletter'}
                    onChange={(e) => updateBlock(id, { ...content, text: e.target.value })}
                    placeholder="Unsubscribe from this newsletter"
                    className="mt-1"
                  />
                </div>
                
                {/* Preview */}
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <span className="text-slate-600 underline cursor-pointer">
                    {content.text || 'Unsubscribe from this newsletter'}
                  </span>
                </div>
              </div>
            </BlockTabContent>
            
            {/* Settings Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <p className="text-sm text-amber-800">
                  <strong>Note:</strong> The unsubscribe link is automatically personalized for each recipient when the newsletter is sent. It will mark them as unsubscribed from your mailing list.
                </p>
              </div>
            </BlockTabContent>
          </>
        )}

        {type === 'article-card' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            disabledTabs={['style', 'animation']}
            />
            
            {/* Content Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'content'}>
              <div className="space-y-4">
                {/* Article Selection */}
                <div>
                  <Label className="text-sm font-medium">Select Article</Label>
                  <Select
                    value={content.articleId || ''}
                    onValueChange={(value) => {
                      const selectedArticle = availableArticles.find(a => a.article_id === value);
                      updateBlock(id, { 
                        ...content, 
                        articleId: value,
                        articleTitle: selectedArticle?.title || '',
                        articleSlug: selectedArticle?.slug || '',
                        articleImage: selectedArticle?.image_url || '',
                        articleExcerpt: selectedArticle?.excerpt || '',
                        articleDate: selectedArticle?.date || ''
                      });
                    }}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder={loadingArticles ? "Loading articles..." : "Choose an article"} />
                    </SelectTrigger>
                    <SelectContent>
                      {availableArticles.filter(a => a.status === 'published').map((article) => (
                        <SelectItem key={article.article_id} value={article.article_id}>
                          {article.title}
                        </SelectItem>
                      ))}
                      {availableArticles.filter(a => a.status === 'published').length === 0 && (
                        <SelectItem value="__none__" disabled>No published articles</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                {/* Preview */}
                {content.articleId && (
                  <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <p className="text-xs text-slate-500 mb-2">Preview:</p>
                    <div className="bg-white rounded-lg overflow-hidden border border-slate-200">
                      {content.showImage !== false && content.articleImage && (
                        <img 
                          src={content.articleImage} 
                          alt="" 
                          className="w-full h-32 object-cover"
                        />
                      )}
                      <div className="p-4">
                        <h3 className="font-semibold text-slate-800">{content.articleTitle}</h3>
                        {content.showDate !== false && content.articleDate && (
                          <p className="text-xs text-slate-400 mt-1">
                            {new Date(content.articleDate).toLocaleDateString()}
                          </p>
                        )}
                        {content.showExcerpt !== false && content.articleExcerpt && (
                          <p className="text-sm text-slate-600 mt-2 line-clamp-2">{content.articleExcerpt}</p>
                        )}
                        {content.showReadMore !== false && (
                          <p className="text-sm text-indigo-600 mt-3 font-medium">
                            {content.readMoreText || 'Read Full Article'} →
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {!content.articleId && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-sm text-blue-800">
                      Select an article above to insert it into your newsletter. The article link will direct readers to the full article on your website.
                    </p>
                  </div>
                )}
              </div>
            </BlockTabContent>
            
            {/* Settings Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
              <div className="space-y-4">
                {/* Display Style */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Display Style</div>
                  <Select
                    value={content.displayStyle || 'card'}
                    onValueChange={(value) => updateBlock(id, { ...content, displayStyle: value })}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="card">Card (image, title, excerpt)</SelectItem>
                      <SelectItem value="headline">Headline Only (title + link)</SelectItem>
                      <SelectItem value="full">Full Content (embed article)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Options for card style */}
                {content.displayStyle !== 'headline' && (
                  <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                    <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Show in Card</div>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={content.showImage !== false}
                          onChange={(e) => updateBlock(id, { ...content, showImage: e.target.checked })}
                          className="rounded"
                        />
                        Image
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={content.showExcerpt !== false}
                          onChange={(e) => updateBlock(id, { ...content, showExcerpt: e.target.checked })}
                          className="rounded"
                        />
                        Excerpt
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={content.showDate !== false}
                          onChange={(e) => updateBlock(id, { ...content, showDate: e.target.checked })}
                          className="rounded"
                        />
                        Date
                      </label>
                    </div>
                  </div>
                )}

                {/* Read More Button */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Read More Link</div>
                  <div className="space-y-2">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={content.showReadMore !== false}
                        onChange={(e) => updateBlock(id, { ...content, showReadMore: e.target.checked })}
                        className="rounded"
                      />
                      Show "Read More" Link
                    </label>
                    {content.showReadMore !== false && (
                      <Input
                        value={content.readMoreText || 'Read Full Article'}
                        onChange={(e) => updateBlock(id, { ...content, readMoreText: e.target.value })}
                        placeholder="Read Full Article"
                        className="mt-1"
                      />
                    )}
                  </div>
                </div>
              </div>
            </BlockTabContent>
          </>
        )}

        {type === 'newsletter-header' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            disabledTabs={['style', 'animation']}
            />
            
            {/* Content Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'content'}>
              <div className="space-y-4">
                {/* Navigation Links */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Navigation Links (2-5)</Label>
                  {(content.links || []).map((link, linkIndex) => (
                    <div key={linkIndex} className="flex items-center gap-2">
                      <Input
                        value={link.text || ''}
                        onChange={(e) => {
                          const newLinks = [...(content.links || [])];
                          newLinks[linkIndex] = { ...newLinks[linkIndex], text: e.target.value };
                          updateBlock(id, { ...content, links: newLinks });
                        }}
                        placeholder="Link text"
                        className="h-8 flex-1"
                      />
                      <Input
                        value={link.url || ''}
                        onChange={(e) => {
                          const newLinks = [...(content.links || [])];
                          newLinks[linkIndex] = { ...newLinks[linkIndex], url: e.target.value };
                          updateBlock(id, { ...content, links: newLinks });
                        }}
                        placeholder="URL"
                        className="h-8 flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          const newLinks = (content.links || []).filter((_, i) => i !== linkIndex);
                          updateBlock(id, { ...content, links: newLinks });
                        }}
                        className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                        disabled={(content.links || []).length <= 2}
                      >
                        ×
                      </Button>
                    </div>
                  ))}
                  {(content.links || []).length < 5 && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const newLinks = [...(content.links || []), { text: '', url: '#' }];
                        updateBlock(id, { ...content, links: newLinks });
                      }}
                      className="w-full"
                    >
                      + Add Link
                    </Button>
                  )}
                </div>

                {/* Preview */}
                <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-2">Preview:</p>
                  <div 
                    className="rounded-lg p-4"
                    style={{ 
                      backgroundColor: content.backgroundColor || '#6366f1',
                      textAlign: content.alignment || 'center'
                    }}
                  >
                    {content.showLogo !== false && content.logoUrl && (
                      <img 
                        src={content.logoUrl} 
                        alt={content.logoAlt || 'Logo'}
                        style={{ 
                          width: content.logoWidth || 150,
                          display: content.alignment === 'center' ? 'block' : 'inline-block',
                          margin: content.alignment === 'center' ? '0 auto 12px' : '0 0 12px'
                        }}
                      />
                    )}
                    <div style={{ marginTop: content.showLogo !== false && content.logoUrl ? '0' : '0' }}>
                      {(content.links || []).map((link, i) => (
                        <span key={i}>
                          <a 
                            href="#" 
                            style={{ 
                              color: content.textColor || '#ffffff',
                              textDecoration: 'none',
                              fontWeight: 500,
                              fontSize: '14px'
                            }}
                            onClick={(e) => e.preventDefault()}
                          >
                            {link.text || `Link ${i + 1}`}
                          </a>
                          {i < (content.links || []).length - 1 && (
                            <span style={{ color: content.textColor || '#ffffff', margin: '0 12px', opacity: 0.5 }}>|</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </BlockTabContent>
            
            {/* Settings Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
              <div className="space-y-4">
                {/* Logo Settings */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Logo</div>
                  <div className="space-y-3">
                    <label className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={content.showLogo !== false}
                        onChange={(e) => updateBlock(id, { ...content, showLogo: e.target.checked })}
                        className="rounded"
                      />
                      Show Logo
                    </label>
                    {content.showLogo !== false && (
                      <div className="space-y-2">
                        <div className="flex gap-2">
                          <Input
                            value={content.logoUrl || ''}
                            onChange={(e) => updateBlock(id, { ...content, logoUrl: e.target.value })}
                            placeholder="Logo URL"
                            className="flex-1"
                            data-testid="newsletter-header-logo-url"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setMediaTargetBlockId(id);
                              setShowMediaPicker(true);
                            }}
                            data-testid="newsletter-header-logo-media-picker"
                          >
                            <FolderOpen size={14} className="mr-1" />
                            {content.logoUrl ? 'Change' : 'Select'}
                          </Button>
                        </div>
                        <div className="flex items-center gap-2">
                          <Label className="text-xs text-slate-500">Width:</Label>
                          <Input
                            type="number"
                            value={content.logoWidth || 150}
                            onChange={(e) => updateBlock(id, { ...content, logoWidth: parseInt(e.target.value) || 150 })}
                            className="w-20 h-8"
                            min={50}
                            max={300}
                          />
                          <span className="text-xs text-slate-400">px</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Colors */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Colors</div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-600">Background Color</Label>
                      <div className="flex items-center gap-2">
                        <ColorPickerSwatch value={content.backgroundColor || '#6366f1'}
                          onChange={(e) => updateBlock(id, { ...content, backgroundColor: e.target.value })}
                          className="w-8 h-8 rounded cursor-pointer"
                        />
                        <ColorHexInput
                          value={content.backgroundColor || ''}
                          onChange={(e) => updateBlock(id, { ...content, backgroundColor: e.target.value })}
                          className="h-8 text-xs flex-1"
                          placeholder="#6366f1"
                        />
                      </div>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-600">Text/Link Color</Label>
                      <div className="flex items-center gap-2">
                        <ColorPickerSwatch value={content.textColor || '#ffffff'}
                          onChange={(e) => updateBlock(id, { ...content, textColor: e.target.value })}
                          className="w-8 h-8 rounded cursor-pointer"
                        />
                        <ColorHexInput
                          value={content.textColor || ''}
                          onChange={(e) => updateBlock(id, { ...content, textColor: e.target.value })}
                          className="h-8 text-xs flex-1"
                          placeholder="#ffffff"
                        />
                      </div>
                    </div>
                  </div>
                </div>

                {/* Alignment */}
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Layout</div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-600">Alignment</Label>
                    <Select
                      value={content.alignment || 'center'}
                      onValueChange={(value) => updateBlock(id, { ...content, alignment: value })}
                    >
                      <SelectTrigger className="h-8">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="left">Left</SelectItem>
                        <SelectItem value="center">Center</SelectItem>
                        <SelectItem value="right">Right</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </BlockTabContent>
          </>
        )}

        {type === 'search' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Content Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'content'}>
              <div className="space-y-4">
                <div className="flex items-center gap-2 pb-3 border-b border-slate-200">
                  <Search size={16} className="text-cyan-500" />
                  <span className="text-sm font-medium text-slate-700">Site Search Block</span>
                </div>
                
                {/* Preview */}
                <div className="bg-slate-50 rounded-lg p-4">
                  <div className="flex gap-2 max-w-md mx-auto">
                    <div className="flex-1 relative">
                      <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                      <input 
                        type="text" 
                        placeholder={content.placeholder || 'Search pages and articles...'}
                        className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg text-sm"
                        disabled
                      />
                    </div>
                    <button className="px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium">
                      {content.buttonText || 'Search'}
                    </button>
                  </div>
                </div>
              </div>
            </BlockTabContent>
            
            {/* Settings Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
              <div className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Text</div>
                  <div className="space-y-3">
                    <div>
                      <Label className="text-sm font-medium">Placeholder Text</Label>
                      <Input
                        value={content.placeholder || 'Search pages and articles...'}
                        onChange={(e) => updateBlock(id, { ...content, placeholder: e.target.value })}
                        placeholder="Search pages and articles..."
                        className="mt-1"
                      />
                    </div>
                    
                    <div>
                      <Label className="text-sm font-medium">Button Text</Label>
                      <Input
                        value={content.buttonText || 'Search'}
                        onChange={(e) => updateBlock(id, { ...content, buttonText: e.target.value })}
                        placeholder="Search"
                        className="mt-1"
                      />
                    </div>
                  </div>
                </div>
                
                <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                  <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Search Options</div>
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={content.searchPages !== false}
                        onChange={(e) => updateBlock(id, { ...content, searchPages: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      <Label className="text-sm">Search Pages</Label>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={content.searchArticles !== false}
                        onChange={(e) => updateBlock(id, { ...content, searchArticles: e.target.checked })}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                      <Label className="text-sm">Search Articles</Label>
                    </div>
                  </div>
                </div>
              </div>
            </BlockTabContent>
            
            {/* Style Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'style'}>
              <BlockStyleControls
                styles={styles || {}}
                onChange={(newStyles) => updateBlock(id, content, newStyles)}
                content={content}
                onContentChange={(newContent) => updateBlock(id, newContent, styles)}
              />
            </BlockTabContent>
            
            {/* Animation Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
              <AnimationControls
                animation={content.animation || {}}
                onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
              />
            </BlockTabContent>
          </>
        )}

        {type === 'custom-html' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />

            {/* Split Layout: HTML editor + live preview always visible on the
                left; Settings / Style / Animation appear on the right when
                their tab is selected. This prevents the editor from
                disappearing when the user clicks Style and then Settings. */}
            <BlockSplitPane
              showRightPane={['settings', 'style', 'animation'].includes(getActiveTab(id))}
              contentPane={
                <div className="space-y-4">
                  {/* HTML Input */}
                  <Textarea
                    value={content.html || ''}
                    onChange={(e) => updateBlock(id, { ...content, html: e.target.value })}
                    className="font-mono text-sm min-h-[250px] bg-slate-900 text-slate-100 border-slate-700"
                    placeholder="<!-- Enter your custom HTML here -->"
                    data-testid={`custom-html-input-${index}`}
                    spellCheck={false}
                  />

                  {/* Live Preview - Isolated in iframe to prevent CSS leaking */}
                  <div>
                    <Label className="text-xs text-slate-500 mb-2 block">Preview (isolated):</Label>
                    <iframe
                      srcDoc={`<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <style>
    body { margin: 0; padding: 16px; font-family: system-ui, sans-serif; }
  </style>
</head>
<body>${content.html || '<p style="color: #999;">No content yet</p>'}</body>
</html>`}
                      className="w-full border border-slate-200 rounded-lg bg-white"
                      style={{ minHeight: '150px', height: 'auto' }}
                      sandbox={content.enableScripts ? "allow-scripts" : ""}
                      title="HTML Preview"
                      onLoad={(e) => {
                        // Auto-resize iframe to content height
                        try {
                          const iframe = e.target;
                          const height = iframe.contentDocument?.body?.scrollHeight;
                          if (height && height > 100) {
                            iframe.style.height = Math.min(height + 40, 500) + 'px';
                          }
                        } catch (err) {
                          // Cross-origin restrictions may prevent this
                        }
                      }}
                    />
                  </div>
                </div>
              }
              settingsPane={
                <>
                  {/* Settings Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
                    <div className="space-y-4">
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Script Settings</div>
                        <div className="flex items-center gap-2">
                          <Switch
                            id={`enable-scripts-${id}`}
                            checked={content.enableScripts || false}
                            onCheckedChange={(checked) => updateBlock(id, { ...content, enableScripts: checked })}
                          />
                          <Label htmlFor={`enable-scripts-${id}`} className="text-sm">Allow Scripts</Label>
                        </div>
                      </div>

                      {/* Warning about scripts */}
                      {content.enableScripts && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                          <p className="text-sm text-amber-800">
                            <strong>⚠️ Warning:</strong> Scripts are enabled. Only add trusted code as malicious scripts can compromise your site.
                          </p>
                        </div>
                      )}

                      {/* Tips */}
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                        <p className="text-xs text-slate-600">
                          <strong>Tips:</strong> You can add HTML, inline CSS with &lt;style&gt; tags, and embedded widgets. 
                          For scripts, enable &quot;Allow Scripts&quot; above. Use Tailwind classes for styling.
                        </p>
                      </div>
                    </div>
                  </BlockTabContent>

                  {/* Style Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>

                  {/* Animation Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {(type === 'columns' || type === 'card-grid') && (
          <>
            {/* Tab Navigation for Columns */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Content/Settings Tab - Original columns content */}
            <div className={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation' ? 'opacity-40 pointer-events-none max-h-[200px] overflow-hidden relative transition-all duration-200' : 'transition-all duration-200'}>
              {(getActiveTab(id) === 'style' || getActiveTab(id) === 'animation') && (
                <div className="absolute inset-x-0 bottom-0 h-20 bg-gradient-to-t from-white to-transparent z-10"></div>
              )}
            <div className="space-y-4">
            {/* Layout Settings Header */}
            <div className="text-xs font-semibold text-slate-500 flex items-center gap-1 uppercase tracking-wide">
              <Columns size={12} />
              Layout Settings
            </div>

            {/* NOTE: Block Width control intentionally NOT duplicated here.
                The shared BlockWidthControl above (rendered for all eligible
                blocks via `supportsBlockWidth`) already covers it. */}
            
            {/* Column / Cards-per-row Selector */}
            <div className="flex flex-wrap items-center gap-4 mb-3">
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">{type === 'card-grid' ? 'Cards per row:' : 'Columns:'}</label>
                <Select
                  value={String(type === 'card-grid' ? (content.cardsPerRow || 3) : (content.columnCount || 2))}
                  onValueChange={(value) => {
                    const newCount = parseInt(value);
                    if (type === 'card-grid') {
                      // Card Grid: this only controls layout (cards wrap into
                      // rows). It never adds/removes cards.
                      updateBlock(id, { ...content, cardsPerRow: newCount });
                      return;
                    }
                    const currentColumns = content.columns || [];
                    let newColumns = [...currentColumns];
                    
                    if (newCount > currentColumns.length) {
                      for (let i = currentColumns.length; i < newCount; i++) {
                        newColumns.push({
                          type: 'text',
                          html: `<p>Column ${i + 1}</p>`,
                          imageUrl: '',
                          imageAlt: '',
                          imageWidth: 200,
                          imageAlign: 'center',
                          textAlign: 'left'
                        });
                      }
                    } else {
                      newColumns = newColumns.slice(0, newCount);
                    }
                    
                    updateBlock(id, { ...content, columnCount: newCount, columns: newColumns });
                  }}
                >
                  <SelectTrigger className="w-16" data-testid={`columns-count-${index}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="2">2</SelectItem>
                    <SelectItem value="3">3</SelectItem>
                    <SelectItem value="4">4</SelectItem>
                    <SelectItem value="5">5</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {type === 'card-grid' && (
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  data-testid={`card-grid-add-${index}`}
                  onClick={() => {
                    const cols = [...(content.columns || [])];
                    cols.push({ type: 'card', html: `<h3>Card ${cols.length + 1}</h3><p>Card description</p>`, imageUrl: '', imageAlt: '', imageWidth: 200, imageAlign: 'center', textAlign: 'left' });
                    updateBlock(id, { ...content, columns: cols });
                  }}
                >
                  <Plus size={14} className="mr-1" /> Add card
                </Button>
              )}
              
              <div className="flex items-center gap-2">
                <label className="text-sm font-medium">Show as Cards:</label>
                <input
                  type="checkbox"
                  checked={content.showAsCards || false}
                  onChange={(e) => updateBlock(id, { ...content, showAsCards: e.target.checked })}
                  className="w-4 h-4 rounded border-slate-300"
                />
              </div>
            </div>

            {/* Column Width Distribution */}
            <div className="mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-medium text-slate-700">Column Widths</label>
                <Select
                  value={content.columnWidthPreset || 'equal'}
                  onValueChange={(value) => {
                    const columnCount = content.columnCount || 2;
                    let newWidths = [];
                    
                    // Calculate widths based on preset
                    if (value === 'equal') {
                      newWidths = Array(columnCount).fill(Math.floor(100 / columnCount));
                    } else if (value === '2-1' && columnCount === 2) {
                      newWidths = [66, 34];
                    } else if (value === '1-2' && columnCount === 2) {
                      newWidths = [34, 66];
                    } else if (value === '3-1' && columnCount === 2) {
                      newWidths = [75, 25];
                    } else if (value === '1-3' && columnCount === 2) {
                      newWidths = [25, 75];
                    } else if (value === '2-1-1' && columnCount === 3) {
                      newWidths = [50, 25, 25];
                    } else if (value === '1-2-1' && columnCount === 3) {
                      newWidths = [25, 50, 25];
                    } else if (value === '1-1-2' && columnCount === 3) {
                      newWidths = [25, 25, 50];
                    } else if (value === 'custom') {
                      newWidths = content.columnWidths || Array(columnCount).fill(Math.floor(100 / columnCount));
                    } else {
                      newWidths = Array(columnCount).fill(Math.floor(100 / columnCount));
                    }
                    
                    updateBlock(id, { ...content, columnWidthPreset: value, columnWidths: newWidths });
                  }}
                >
                  <SelectTrigger className="w-28 h-8 text-xs">
                    <SelectValue placeholder="Equal" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="equal">Equal</SelectItem>
                    {(content.columnCount || 2) === 2 && (
                      <>
                        <SelectItem value="2-1">2:1 (Wide Left)</SelectItem>
                        <SelectItem value="1-2">1:2 (Wide Right)</SelectItem>
                        <SelectItem value="3-1">3:1 (Wider Left)</SelectItem>
                        <SelectItem value="1-3">1:3 (Wider Right)</SelectItem>
                      </>
                    )}
                    {(content.columnCount || 2) === 3 && (
                      <>
                        <SelectItem value="2-1-1">2:1:1 (Wide Left)</SelectItem>
                        <SelectItem value="1-2-1">1:2:1 (Wide Center)</SelectItem>
                        <SelectItem value="1-1-2">1:1:2 (Wide Right)</SelectItem>
                      </>
                    )}
                    <SelectItem value="custom">Custom</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              {/* Custom Width Inputs */}
              {content.columnWidthPreset === 'custom' && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {(content.columns || []).map((col, colIndex) => (
                    <div key={colIndex} className="flex items-center gap-1">
                      <span className="text-xs text-slate-500">Col {colIndex + 1}:</span>
                      <Input
                        type="number"
                        min="10"
                        max="90"
                        value={content.columnWidths?.[colIndex] || Math.floor(100 / (content.columnCount || 2))}
                        onChange={(e) => {
                          const newWidths = [...(content.columnWidths || Array(content.columnCount || 2).fill(Math.floor(100 / (content.columnCount || 2))))];
                          newWidths[colIndex] = parseInt(e.target.value) || 25;
                          updateBlock(id, { ...content, columnWidths: newWidths });
                        }}
                        className="w-20 h-7 text-xs"
                      />
                      <span className="text-xs text-slate-400">%</span>
                    </div>
                  ))}
                  <span className="text-[10px] text-slate-400 ml-2">(Total: {(content.columnWidths || []).reduce((a, b) => a + b, 0)}%)</span>
                </div>
              )}
              
              {/* Visual Width Preview */}
              <div className="flex gap-1 mt-2 h-4 rounded overflow-hidden">
                {(content.columns || []).map((col, colIndex) => {
                  const width = content.columnWidths?.[colIndex] || Math.floor(100 / (content.columnCount || 2));
                  return (
                    <div 
                      key={colIndex}
                      className="bg-indigo-200 flex items-center justify-center"
                      style={{ width: `${width}%` }}
                    >
                      <span className="text-[8px] text-indigo-700 font-medium">{width}%</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Column Height Control */}
            <div className="mb-3 p-3 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Column Height</label>
                <Select
                  value={content.columnHeight || 'auto'}
                  onValueChange={(value) => updateBlock(id, { ...content, columnHeight: value })}
                >
                  <SelectTrigger className="w-32 h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto (Fit Content)</SelectItem>
                    <SelectItem value="match">Match Tallest</SelectItem>
                    <SelectItem value="200">200px</SelectItem>
                    <SelectItem value="250">250px</SelectItem>
                    <SelectItem value="300">300px</SelectItem>
                    <SelectItem value="350">350px</SelectItem>
                    <SelectItem value="400">400px</SelectItem>
                    <SelectItem value="450">450px</SelectItem>
                    <SelectItem value="500">500px</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                {!content.columnHeight || content.columnHeight === 'auto' ? 'Each column fits its content' : 
                 content.columnHeight === 'match' ? 'All columns will stretch to match the tallest one' : 
                 `All columns will be ${content.columnHeight}px tall`}
              </p>
            </div>

            <div className="space-y-3 mb-3">
              {content.showAsCards && (
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider mb-2">Card Defaults</p>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-slate-500">BG:</label>
                      <ColorPickerSwatch value={content.cardStyle?.backgroundColor || '#ffffff'}
                        onChange={(e) => updateBlock(id, { 
                          ...content, 
                          cardStyle: { ...content.cardStyle, backgroundColor: e.target.value }
                        })}
                        className="w-8 h-6 rounded border cursor-pointer"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-slate-500">Shadow:</label>
                      <input
                        type="checkbox"
                        checked={content.cardStyle?.shadow !== false}
                        onChange={(e) => updateBlock(id, { 
                          ...content, 
                          cardStyle: { ...content.cardStyle, shadow: e.target.checked }
                        })}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-slate-500">Radius:</label>
                      <input
                        type="number"
                        min="0"
                        max="50"
                        value={parseInt(content.cardStyle?.borderRadius) || 8}
                        onChange={(e) => updateBlock(id, { 
                          ...content, 
                          cardStyle: { ...content.cardStyle, borderRadius: `${e.target.value}px` }
                        })}
                        className="w-16 h-7 text-xs border rounded px-2"
                        data-testid="card-radius-input"
                      />
                      <span className="text-xs text-slate-400">px</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-slate-500" title="Default animation for all cards (can be overridden per card below)">Anim:</label>
                      <Select
                        value={content.cardStyle?.animation || 'none'}
                        onValueChange={(value) => updateBlock(id, { 
                          ...content, 
                          cardStyle: { ...content.cardStyle, animation: value }
                        })}
                      >
                        <SelectTrigger className="w-28 h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="fadeIn">Fade In</SelectItem>
                          <SelectItem value="fadeInUp">Fade In Up</SelectItem>
                          <SelectItem value="fadeInDown">Fade In Down</SelectItem>
                          <SelectItem value="slideLeft">Slide Left</SelectItem>
                          <SelectItem value="slideRight">Slide Right</SelectItem>
                          <SelectItem value="zoomIn">Zoom In</SelectItem>
                          <SelectItem value="bounce">Bounce</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-slate-500" title="Stagger animation timing for each card">Stagger:</label>
                      <input
                        type="checkbox"
                        checked={content.cardStyle?.stagger !== false}
                        onChange={(e) => updateBlock(id, { 
                          ...content, 
                          cardStyle: { ...content.cardStyle, stagger: e.target.checked }
                        })}
                        className="w-4 h-4 rounded border-slate-300"
                        title="Stagger animation for each card"
                      />
                    </div>
                  </div>
                </div>
              )}
              
              <div className="flex flex-wrap items-center gap-4">
                {content.showAsCards && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-slate-500">Column Widths:</label>
                    <Select
                      value={content.columnWidths || 'equal'}
                      onValueChange={(value) => updateBlock(id, { 
                        ...content, 
                        columnWidths: value
                      })}
                    >
                      <SelectTrigger className="w-24 h-7 text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="equal">Equal</SelectItem>
                        <SelectItem value="1-2">1:2</SelectItem>
                        <SelectItem value="2-1">2:1</SelectItem>
                        <SelectItem value="1-1-2">1:1:2</SelectItem>
                        <SelectItem value="2-1-1">2:1:1</SelectItem>
                        <SelectItem value="1-2-1">1:2:1</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {content.showAsCards && content.columnWidths === 'custom' && (
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-slate-500">Custom Widths:</label>
                      <div className="flex items-center gap-1">
                        {content.columns?.map((_, colIndex) => (
                          <div key={colIndex} className="flex items-center gap-1">
                            <input
                              type="number"
                              value={content.customWidths?.[colIndex] || Math.floor(100 / content.columns.length)}
                              onChange={(e) => {
                                const newCustomWidths = [...(content.customWidths || content.columns.map(() => Math.floor(100 / content.columns.length)))];
                                newCustomWidths[colIndex] = parseInt(e.target.value) || 0;
                                updateBlock(id, { 
                                  ...content, 
                                  customWidths: newCustomWidths
                                });
                              }}
                              className="w-12 h-6 text-xs border rounded px-1"
                              min="5"
                              max="90"
                            />
                            <span className="text-xs text-slate-400">%</span>
                            {colIndex < content.columns.length - 1 && <span className="text-xs text-slate-400 mx-1">:</span>}
                          </div>
                        ))}
                      </div>
                    </div>
                )}
              
              {/* Padding Controls - Multi-select */}
              <div className="flex items-center gap-2 ml-4 border-l pl-4 border-slate-200">
                <label className="text-xs text-slate-500">Padding:</label>
                <div className="flex items-center gap-1">
                  {['top', 'right', 'bottom', 'left'].map((side) => (
                    <label key={side} className="flex items-center gap-0.5 text-xs cursor-pointer">
                      <input
                        type="checkbox"
                        checked={(content.paddingSides || ['top', 'right', 'bottom', 'left']).includes(side)}
                        onChange={(e) => {
                          const currentSides = content.paddingSides || ['top', 'right', 'bottom', 'left'];
                          const newSides = e.target.checked 
                            ? [...currentSides, side]
                            : currentSides.filter(s => s !== side);
                          updateBlock(id, { ...content, paddingSides: newSides });
                        }}
                        className="w-3 h-3 rounded"
                      />
                      <span className="capitalize">{side[0].toUpperCase()}</span>
                    </label>
                  ))}
                </div>
                <Input
                  type="number"
                  value={content.paddingSize || 0}
                  onChange={(e) => updateBlock(id, { ...content, paddingSize: parseInt(e.target.value) || 0 })}
                  className="w-20 h-7 text-xs"
                  min="0"
                  max="100"
                />
                <span className="text-xs text-slate-400">px</span>
              </div>
            </div>
            </div>

            {/* Column Grid */}
            <div 
              className="grid gap-4"
              style={{ 
                gridTemplateColumns: type === 'card-grid'
                  ? `repeat(${content.cardsPerRow || 3}, minmax(0, 1fr))`
                  : Array.isArray(content.columnWidths) 
                  ? content.columnWidths.map(w => `${w}fr`).join(' ')
                  : content.columnWidths === '1-2' ? '1fr 2fr'
                  : content.columnWidths === '2-1' ? '2fr 1fr'
                  : content.columnWidths === '1-1-2' ? '1fr 1fr 2fr'
                  : content.columnWidths === '2-1-1' ? '2fr 1fr 1fr'
                  : content.columnWidths === '1-2-1' ? '1fr 2fr 1fr'
                  : content.columnWidths === 'custom' && Array.isArray(content.customWidths)
                    ? content.customWidths.map(w => `${w}fr`).join(' ')
                  : `repeat(${content.columnCount || 2}, 1fr)` 
              }}
            >
              <ColumnDndContext ids={(content.columns || []).map((c, i) => `col-${i}`)} onReorder={(from, to) => { const next = { ...content, columns: arrayMove(content.columns || [], from, to) }; if (Array.isArray(content.columnWidths)) next.columnWidths = arrayMove(content.columnWidths, from, to); if (Array.isArray(content.customWidths)) next.customWidths = arrayMove(content.customWidths, from, to); updateBlock(id, next); }}>
              {(content.columns || []).map((col, colIndex) => (
                <SortableColumnCard key={`col-${colIndex}`} id={`col-${colIndex}`}>
                <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-1">
                      <label className="text-xs font-medium text-slate-600">{type === 'card-grid' ? 'Card' : 'Column'} {colIndex + 1}</label>
                      {type === 'card-grid' && (content.columns || []).length > 1 && (
                        <Select
                          value={String(colIndex + 1)}
                          onValueChange={(val) => {
                            const target = parseInt(val, 10) - 1;
                            if (isNaN(target) || target === colIndex) return;
                            updateBlock(id, { ...content, columns: arrayMove(content.columns || [], colIndex, target) });
                          }}
                        >
                          <SelectTrigger
                            className="w-14 h-7 text-xs ml-1"
                            title="Set position — the card moves here and the others shift"
                            data-testid={`card-position-${index}-${colIndex}`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {(content.columns || []).map((_, i) => (
                              <SelectItem key={i} value={String(i + 1)}>{i + 1}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                      <ColumnMoveButtons
                        index={colIndex}
                        total={(content.columns || []).length}
                        testIdPrefix={`column-${index}`}
                        onMove={(from, to) => {
                          const next = { ...content, columns: reorderList(content.columns, from, to) };
                          if (Array.isArray(content.columnWidths)) next.columnWidths = reorderList(content.columnWidths, from, to);
                          if (Array.isArray(content.customWidths)) next.customWidths = reorderList(content.customWidths, from, to);
                          updateBlock(id, next);
                        }}
                      />
                      <DuplicateColumnButton
                        testId={`column-${index}-dup-${colIndex}`}
                        onClick={() => {
                          const cols = [...content.columns];
                          cols.splice(colIndex + 1, 0, { ...col });
                          const next = { ...content, columns: cols, columnCount: cols.length };
                          if (Array.isArray(content.columnWidths)) { const w = [...content.columnWidths]; w.splice(colIndex + 1, 0, w[colIndex]); next.columnWidths = w; }
                          if (Array.isArray(content.customWidths)) { const cw = [...content.customWidths]; cw.splice(colIndex + 1, 0, cw[colIndex]); next.customWidths = cw; }
                          updateBlock(id, next);
                        }}
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Select
                        value={col.type || 'text'}
                        onValueChange={(value) => {
                          const newColumns = [...content.columns];
                          newColumns[colIndex] = { ...newColumns[colIndex], type: value };
                          updateBlock(id, { ...content, columns: newColumns });
                        }}
                      >
                        <SelectTrigger className="w-32 h-7 text-xs" data-testid={`column-type-${index}-${colIndex}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="text">Text Only</SelectItem>
                          <SelectItem value="image">Image Only</SelectItem>
                          <SelectItem value="video">Video Only</SelectItem>
                          <SelectItem value="card">Media + Text</SelectItem>
                          <SelectItem value="avatar">Letter Circle</SelectItem>
                        </SelectContent>
                      </Select>
                      {/* Delete Column Button - only show if more than 1 column */}
                      {content.columns.length > 1 && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            const newColumns = content.columns.filter((_, idx) => idx !== colIndex);
                            const newWidths = Array.isArray(content.columnWidths)
                              ? content.columnWidths.filter((_, idx) => idx !== colIndex)
                              : null;
                            updateBlock(id, { 
                              ...content, 
                              columns: newColumns,
                              columnCount: newColumns.length,
                              columnWidths: newWidths
                            });
                          }}
                          title="Delete this column"
                          data-testid={`delete-column-${index}-${colIndex}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      )}
                    </div>
                  </div>

                  {/* Video Only Type */}
                  {col.type === 'video' && (
                    <div className="space-y-2 border border-slate-200 rounded p-2 bg-white">
                      <label className="text-xs font-medium text-slate-600 block">Video</label>
                      <Input
                        value={col.videoUrl || ''}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[colIndex] = { ...newColumns[colIndex], videoUrl: e.target.value };
                          updateBlock(id, { ...content, columns: newColumns });
                        }}
                        placeholder="YouTube, Vimeo, or video URL"
                        className="h-8 text-xs"
                      />
                      <p className="text-[10px] text-slate-400">Supports YouTube, Vimeo, or direct video links</p>
                      {/* Video Height Control */}
                      <div className="flex items-center gap-2">
                        <label className="text-xs text-slate-500">Height:</label>
                        <Select
                          value={col.videoHeight || '250'}
                          onValueChange={(value) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], videoHeight: value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                        >
                          <SelectTrigger className="w-24 h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="150">150px</SelectItem>
                            <SelectItem value="200">200px</SelectItem>
                            <SelectItem value="250">250px</SelectItem>
                            <SelectItem value="300">300px</SelectItem>
                            <SelectItem value="350">350px</SelectItem>
                            <SelectItem value="400">400px</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {/* Video Options */}
                      <div className="flex flex-wrap gap-3">
                        <div className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            id={`video-autoplay-${id}-${colIndex}`}
                            checked={col.videoAutoplay !== false}
                            onChange={(e) => {
                              const newColumns = [...content.columns];
                              newColumns[colIndex] = { ...newColumns[colIndex], videoAutoplay: e.target.checked };
                              updateBlock(id, { ...content, columns: newColumns });
                            }}
                            className="w-3 h-3"
                          />
                          <label htmlFor={`video-autoplay-${id}-${colIndex}`} className="text-[10px] text-slate-500">Autoplay</label>
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            id={`video-muted-${id}-${colIndex}`}
                            checked={col.videoMuted !== false}
                            onChange={(e) => {
                              const newColumns = [...content.columns];
                              newColumns[colIndex] = { ...newColumns[colIndex], videoMuted: e.target.checked };
                              updateBlock(id, { ...content, columns: newColumns });
                            }}
                            className="w-3 h-3"
                          />
                          <label htmlFor={`video-muted-${id}-${colIndex}`} className="text-[10px] text-slate-500">Muted</label>
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            id={`video-loop-${id}-${colIndex}`}
                            checked={col.videoLoop !== false}
                            onChange={(e) => {
                              const newColumns = [...content.columns];
                              newColumns[colIndex] = { ...newColumns[colIndex], videoLoop: e.target.checked };
                              updateBlock(id, { ...content, columns: newColumns });
                            }}
                            className="w-3 h-3"
                          />
                          <label htmlFor={`video-loop-${id}-${colIndex}`} className="text-[10px] text-slate-500">Loop</label>
                        </div>
                        <div className="flex items-center gap-1">
                          <input
                            type="checkbox"
                            id={`video-controls-${id}-${colIndex}`}
                            checked={col.videoControls !== false}
                            onChange={(e) => {
                              const newColumns = [...content.columns];
                              newColumns[colIndex] = { ...newColumns[colIndex], videoControls: e.target.checked };
                              updateBlock(id, { ...content, columns: newColumns });
                            }}
                            className="w-3 h-3"
                          />
                          <label htmlFor={`video-controls-${id}-${colIndex}`} className="text-[10px] text-slate-500">Controls</label>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Media + Text Type */}
                  {col.type === 'card' && (
                    <div className="space-y-3">
                      {/* Media Position Control */}
                      <div className="flex items-center gap-2 p-2 bg-indigo-50 rounded border border-indigo-100">
                        <label className="text-xs font-medium text-indigo-700">Media Position:</label>
                        <Select
                          value={col.imagePosition || 'top'}
                          onValueChange={(value) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], imagePosition: value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                        >
                          <SelectTrigger className="w-24 h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="top">Above Text</SelectItem>
                            <SelectItem value="bottom">Below Text</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {/* Media Type Selector */}
                      <div className="border border-slate-200 rounded p-2 bg-white">
                        <div className="flex items-center gap-2 mb-2">
                          <label className="text-xs font-medium text-slate-600">Media Type:</label>
                          <Select
                            value={col.mediaType || 'image'}
                            onValueChange={(value) => {
                              const newColumns = [...content.columns];
                              newColumns[colIndex] = { ...newColumns[colIndex], mediaType: value };
                              updateBlock(id, { ...content, columns: newColumns });
                            }}
                          >
                            <SelectTrigger className="w-24 h-7 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="image">Image</SelectItem>
                              <SelectItem value="video">Video</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Image Section */}
                        {(col.mediaType || 'image') === 'image' && (
                          <div className="space-y-2">
                            <div className="flex gap-2 items-center">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setMediaTargetBlockId(`${id}:${colIndex}`);
                                  setShowMediaPicker(true);
                                }}
                                className="h-8"
                              >
                                <FolderOpen size={14} className="mr-1" />
                                {col.imageUrl ? 'Change' : 'Select'}
                              </Button>
                              {col.imageUrl && (
                                <>
                                  <span className="text-xs text-green-600">✓</span>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => {
                                      const newColumns = [...content.columns];
                                      newColumns[colIndex] = { ...newColumns[colIndex], imageUrl: '', imageAlt: '' };
                                      updateBlock(id, { ...content, columns: newColumns });
                                    }}
                                    className="h-7 px-2 text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 size={12} />
                                  </Button>
                                </>
                              )}
                            </div>
                            {col.imageUrl && (
                              <img src={col.imageUrl} alt={col.imageAlt || ''} className="w-full h-24 object-cover rounded" />
                            )}
                            {/* Image Size Controls - Custom */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <label className="text-xs text-slate-500">Size:</label>
                              <Select
                                value={col.cardImageSizeMode || 'height'}
                                onValueChange={(value) => {
                                  const newColumns = [...content.columns];
                                  newColumns[colIndex] = { ...newColumns[colIndex], cardImageSizeMode: value };
                                  updateBlock(id, { ...content, columns: newColumns });
                                }}
                              >
                                <SelectTrigger className="w-28 h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="height">By Height</SelectItem>
                                  <SelectItem value="width">By Width</SelectItem>
                                  <SelectItem value="custom">Custom (W×H)</SelectItem>
                                </SelectContent>
                              </Select>
                              
                              {col.cardImageSizeMode === 'custom' ? (
                                <>
                                  <span className="text-xs text-slate-500">W:</span>
                                  <Input
                                    type="number"
                                    value={col.cardImageWidth || 200}
                                    onChange={(e) => {
                                      const newColumns = [...content.columns];
                                      newColumns[colIndex] = { ...newColumns[colIndex], cardImageWidth: parseInt(e.target.value) || 200 };
                                      updateBlock(id, { ...content, columns: newColumns });
                                    }}
                                    className="w-20 h-7 text-xs"
                                    min="50"
                                    max="800"
                                  />
                                  <span className="text-xs text-slate-500">H:</span>
                                  <Input
                                    type="number"
                                    value={col.cardImageHeight || 200}
                                    onChange={(e) => {
                                      const newColumns = [...content.columns];
                                      newColumns[colIndex] = { ...newColumns[colIndex], cardImageHeight: parseInt(e.target.value) || 200 };
                                      updateBlock(id, { ...content, columns: newColumns });
                                    }}
                                    className="w-20 h-7 text-xs"
                                    min="50"
                                    max="800"
                                  />
                                  <span className="text-xs text-slate-400">px</span>
                                </>
                              ) : col.cardImageSizeMode === 'width' ? (
                                <>
                                  <span className="text-xs text-slate-500">W:</span>
                                  <Input
                                    type="number"
                                    value={col.cardImageWidth || 200}
                                    onChange={(e) => {
                                      const newColumns = [...content.columns];
                                      newColumns[colIndex] = { ...newColumns[colIndex], cardImageWidth: parseInt(e.target.value) || 200 };
                                      updateBlock(id, { ...content, columns: newColumns });
                                    }}
                                    className="w-20 h-7 text-xs"
                                    min="50"
                                    max="800"
                                  />
                                  <span className="text-xs text-slate-400">px</span>
                                </>
                              ) : (
                                <>
                                  <span className="text-xs text-slate-500">H:</span>
                                  <Input
                                    type="number"
                                    value={col.cardImageHeight || 200}
                                    onChange={(e) => {
                                      const newColumns = [...content.columns];
                                      newColumns[colIndex] = { ...newColumns[colIndex], cardImageHeight: parseInt(e.target.value) || 200 };
                                      updateBlock(id, { ...content, columns: newColumns });
                                    }}
                                    className="w-20 h-7 text-xs"
                                    min="50"
                                    max="800"
                                  />
                                  <span className="text-xs text-slate-400">px</span>
                                </>
                              )}
                            </div>
                            {/* Image Fit Mode */}
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-slate-500">Fit:</label>
                              <Select
                                value={col.cardImageFit || 'contain'}
                                onValueChange={(value) => {
                                  const newColumns = [...content.columns];
                                  newColumns[colIndex] = { ...newColumns[colIndex], cardImageFit: value };
                                  updateBlock(id, { ...content, columns: newColumns });
                                }}
                              >
                                <SelectTrigger className="w-28 h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="contain">Show Full</SelectItem>
                                  <SelectItem value="cover">Fill & Crop</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            <Input
                              value={col.imageAlt || ''}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], imageAlt: e.target.value };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              placeholder="Alt text"
                              className="h-7 text-xs"
                            />
                            {/* Shape Mask Controls for Media+Text card image */}
                            <div className="flex gap-2 items-center flex-wrap pt-2 border-t border-slate-100">
                              <label className="text-xs text-slate-500">Shape:</label>
                              <Select
                                value={col.shapeMask || 'none'}
                                onValueChange={(value) => {
                                  const newColumns = [...content.columns];
                                  newColumns[colIndex] = { ...newColumns[colIndex], shapeMask: value };
                                  updateBlock(id, { ...content, columns: newColumns });
                                }}
                              >
                                <SelectTrigger className="w-28 h-7 text-xs" data-testid={`card-shape-mask-${colIndex}`}>
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="none">No Mask</SelectItem>
                                  <SelectItem value="circle">Circle</SelectItem>
                                  <SelectItem value="ellipse">Ellipse</SelectItem>
                                  <SelectItem value="rounded">Rounded</SelectItem>
                                  <SelectItem value="diamond">Diamond</SelectItem>
                                  <SelectItem value="hexagon">Hexagon</SelectItem>
                                  <SelectItem value="octagon">Octagon</SelectItem>
                                  <SelectItem value="star">Star</SelectItem>
                                  <SelectItem value="arch">Arch</SelectItem>
                                </SelectContent>
                              </Select>
                              {col.shapeMask && col.shapeMask !== 'none' && (
                                <>
                                  <label className="text-xs text-slate-500">Border:</label>
                                  <Input
                                    type="number"
                                    value={col.shapeBorderWidth || 0}
                                    onChange={(e) => {
                                      const newColumns = [...content.columns];
                                      newColumns[colIndex] = { ...newColumns[colIndex], shapeBorderWidth: parseInt(e.target.value) || 0 };
                                      updateBlock(id, { ...content, columns: newColumns });
                                    }}
                                    className="w-20 h-7 text-xs"
                                    min="0"
                                    max="20"
                                  />
                                  <ColorPickerSwatch value={col.shapeBorderColor || '#ffffff'}
                                    onChange={(e) => {
                                      const newColumns = [...content.columns];
                                      newColumns[colIndex] = { ...newColumns[colIndex], shapeBorderColor: e.target.value };
                                      updateBlock(id, { ...content, columns: newColumns });
                                    }}
                                    className="w-7 h-7 rounded border cursor-pointer"
                                    title="Border Color"
                                  />
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Video Section */}
                        {col.mediaType === 'video' && (
                          <div className="space-y-2">
                            <Input
                              value={col.videoUrl || ''}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], videoUrl: e.target.value };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              placeholder="YouTube, Vimeo, or video URL"
                              className="h-8 text-xs"
                            />
                            <p className="text-[10px] text-slate-400">Supports YouTube, Vimeo, or direct video links</p>
                            {/* Video Height Control */}
                            <div className="flex items-center gap-2">
                              <label className="text-xs text-slate-500">Height:</label>
                              <Select
                                value={col.videoHeight || '200'}
                                onValueChange={(value) => {
                                  const newColumns = [...content.columns];
                                  newColumns[colIndex] = { ...newColumns[colIndex], videoHeight: value };
                                  updateBlock(id, { ...content, columns: newColumns });
                                }}
                              >
                                <SelectTrigger className="w-24 h-7 text-xs">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="150">150px</SelectItem>
                                  <SelectItem value="200">200px</SelectItem>
                                  <SelectItem value="250">250px</SelectItem>
                                  <SelectItem value="300">300px</SelectItem>
                                  <SelectItem value="350">350px</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>
                            {/* Video Options */}
                            <div className="flex flex-wrap gap-3">
                              <div className="flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  id={`autoplay-${id}-${colIndex}`}
                                  checked={col.videoAutoplay !== false}
                                  onChange={(e) => {
                                    const newColumns = [...content.columns];
                                    newColumns[colIndex] = { ...newColumns[colIndex], videoAutoplay: e.target.checked };
                                    updateBlock(id, { ...content, columns: newColumns });
                                  }}
                                  className="w-3 h-3"
                                />
                                <label htmlFor={`autoplay-${id}-${colIndex}`} className="text-[10px] text-slate-500">Autoplay</label>
                              </div>
                              <div className="flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  id={`muted-${id}-${colIndex}`}
                                  checked={col.videoMuted !== false}
                                  onChange={(e) => {
                                    const newColumns = [...content.columns];
                                    newColumns[colIndex] = { ...newColumns[colIndex], videoMuted: e.target.checked };
                                    updateBlock(id, { ...content, columns: newColumns });
                                  }}
                                  className="w-3 h-3"
                                />
                                <label htmlFor={`muted-${id}-${colIndex}`} className="text-[10px] text-slate-500">Muted</label>
                              </div>
                              <div className="flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  id={`loop-${id}-${colIndex}`}
                                  checked={col.videoLoop !== false}
                                  onChange={(e) => {
                                    const newColumns = [...content.columns];
                                    newColumns[colIndex] = { ...newColumns[colIndex], videoLoop: e.target.checked };
                                    updateBlock(id, { ...content, columns: newColumns });
                                  }}
                                  className="w-3 h-3"
                                />
                                <label htmlFor={`loop-${id}-${colIndex}`} className="text-[10px] text-slate-500">Loop</label>
                              </div>
                              <div className="flex items-center gap-1">
                                <input
                                  type="checkbox"
                                  id={`controls-${id}-${colIndex}`}
                                  checked={col.videoControls !== false}
                                  onChange={(e) => {
                                    const newColumns = [...content.columns];
                                    newColumns[colIndex] = { ...newColumns[colIndex], videoControls: e.target.checked };
                                    updateBlock(id, { ...content, columns: newColumns });
                                  }}
                                  className="w-3 h-3"
                                />
                                <label htmlFor={`controls-${id}-${colIndex}`} className="text-[10px] text-slate-500">Controls</label>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Text Section */}
                      <div className="border border-slate-200 rounded p-2 bg-white">
                        <label className="text-xs font-medium text-slate-600 mb-2 block">Text Content</label>
                        <TipTapEditor
                          content={col.html || '<p>Enter text...</p>'}
                          onChange={(html) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], html };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                          placeholder="Enter text content..."
                          enableAiAssist={true}
                        />
                      </div>
                    </div>
                  )}
                  {col.type === 'avatar' && (
                    <div className="space-y-2 border border-slate-200 rounded p-2 bg-white">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="space-y-1">
                          <Label className="text-[11px] text-slate-500">Letter / Initials</Label>
                          <Input
                            value={col.avatarText || ''}
                            maxLength={3}
                            onChange={(e) => {
                              const newColumns = [...content.columns];
                              newColumns[colIndex] = { ...newColumns[colIndex], avatarText: e.target.value };
                              updateBlock(id, { ...content, columns: newColumns });
                            }}
                            placeholder="A"
                            className="h-8 text-xs"
                            data-testid={`column-avatar-text-${index}-${colIndex}`}
                          />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[11px] text-slate-500">Circle Color</Label>
                          <div className="flex items-center gap-2">
                            <ColorPickerSwatch
                              value={col.avatarColor || '#4F46E5'}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], avatarColor: e.target.value };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                            />
                            <span className="text-xs font-mono text-slate-500">{col.avatarColor || '#4F46E5'}</span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-slate-500">Title (optional)</Label>
                        <Input
                          value={col.avatarTitle || ''}
                          onChange={(e) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], avatarTitle: e.target.value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                          placeholder="e.g. Acme Inc."
                          className="h-8 text-xs"
                          data-testid={`column-avatar-title-${index}-${colIndex}`}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label className="text-[11px] text-slate-500">Subtitle (optional)</Label>
                        <Input
                          value={col.avatarSubtitle || ''}
                          onChange={(e) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], avatarSubtitle: e.target.value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                          placeholder="e.g. Enterprise partner"
                          className="h-8 text-xs"
                        />
                      </div>
                      <p className="text-[11px] text-slate-400">Renders a colored circle with your letter — like the Team cards. Leave the letter blank to auto-use the Title's first character.</p>
                    </div>
                  )}



                  {col.type === 'text' && (
                    <div className="space-y-2">
                      {/* Rich Text Editor using TipTap - includes all formatting in its toolbar */}
                      <TipTapEditor
                        content={col.html || '<p>Enter text...</p>'}
                        onChange={(html) => {
                          const newColumns = [...content.columns];
                          newColumns[colIndex] = { ...newColumns[colIndex], html };
                          updateBlock(id, { ...content, columns: newColumns });
                        }}
                        placeholder="Enter column text..."
                        minHeight="80px"
                        className="bg-white rounded border border-slate-200"
                        showToolbar={true}
                        enableAiAssist={true}
                      />
                    </div>
                  )}

                  {col.type === 'image' && (
                    <div className="space-y-2">
                      <div className="flex gap-2 items-center">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setMediaTargetBlockId(`${id}:${colIndex}`);
                            setShowMediaPicker(true);
                          }}
                          className="h-8"
                          data-testid={`column-media-library-${index}-${colIndex}`}
                        >
                          <FolderOpen size={14} className="mr-1" />
                          {col.imageUrl ? 'Change' : 'Select Image'}
                        </Button>
                        {col.imageUrl && (
                          <>
                            <span className="text-xs text-green-600">✓ Image selected</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], imageUrl: '', imageAlt: '' };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                              title="Remove image"
                            >
                              <Trash2 size={14} />
                            </Button>
                          </>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          value={col.imageAlt || ''}
                          onChange={(e) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], imageAlt: e.target.value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                          placeholder="Alt text (accessibility)"
                          className="h-8 text-sm"
                          data-testid={`column-image-alt-${index}-${colIndex}`}
                        />
                        <Input
                          value={col.imageCaption || ''}
                          onChange={(e) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], imageCaption: e.target.value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                          placeholder="Caption (visible)"
                          className="h-8 text-sm"
                          data-testid={`column-image-caption-${index}-${colIndex}`}
                        />
                      </div>
                      <div className="flex gap-2 items-center flex-wrap">
                        <Select
                          value={col.imageSizeMode || 'width'}
                          onValueChange={(value) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], imageSizeMode: value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                        >
                          <SelectTrigger className="w-24 h-8 text-xs" data-testid={`column-image-size-mode-${index}-${colIndex}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="width">By Width</SelectItem>
                            <SelectItem value="height">By Height</SelectItem>
                          </SelectContent>
                        </Select>
                        {(col.imageSizeMode || 'width') === 'width' ? (
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">W:</label>
                            <Input
                              type="number"
                              value={col.imageWidth || 200}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], imageWidth: parseInt(e.target.value) || 200 };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="w-20 h-8 text-sm"
                              min="50"
                              max="1000"
                              data-testid={`column-image-width-${index}-${colIndex}`}
                            />
                            <span className="text-xs text-slate-500">px</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">H:</label>
                            <Input
                              type="number"
                              value={col.imageHeight || 200}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], imageHeight: parseInt(e.target.value) || 200 };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="w-20 h-8 text-sm"
                              min="50"
                              max="1000"
                              data-testid={`column-image-height-${index}-${colIndex}`}
                            />
                            <span className="text-xs text-slate-500">px</span>
                          </div>
                        )}
                        <Select
                          value={col.imageAlign || 'center'}
                          onValueChange={(value) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], imageAlign: value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                        >
                          <SelectTrigger className="w-24 h-8 text-xs" data-testid={`column-image-align-${index}-${colIndex}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="left">Left</SelectItem>
                            <SelectItem value="center">Center</SelectItem>
                            <SelectItem value="right">Right</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Image Corners */}
                      <div className="flex gap-2 items-center mt-2">
                        <label className="text-xs text-slate-500">Corners:</label>
                        <Select
                          value={col.imageCorners || 'rounded'}
                          onValueChange={(value) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], imageCorners: value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                        >
                          <SelectTrigger className="w-24 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="square">Square</SelectItem>
                            <SelectItem value="rounded">Rounded</SelectItem>
                            <SelectItem value="pill">Pill</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Shape Mask Controls */}
                      <div className="flex gap-2 items-center flex-wrap mt-2">
                        <label className="text-xs text-slate-500">Shape:</label>
                        <Select
                          value={col.shapeMask || 'none'}
                          onValueChange={(value) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], shapeMask: value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                        >
                          <SelectTrigger className="w-28 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">No Mask</SelectItem>
                            <SelectItem value="circle">Circle</SelectItem>
                            <SelectItem value="ellipse">Ellipse</SelectItem>
                            <SelectItem value="rounded">Rounded</SelectItem>
                            <SelectItem value="diamond">Diamond</SelectItem>
                            <SelectItem value="hexagon">Hexagon</SelectItem>
                            <SelectItem value="octagon">Octagon</SelectItem>
                            <SelectItem value="star">Star</SelectItem>
                            <SelectItem value="arch">Arch</SelectItem>
                          </SelectContent>
                        </Select>
                        
                        {/* Shape Size & Border - only show when mask is applied */}
                        {col.shapeMask && col.shapeMask !== 'none' && (
                          <>
                            <label className="text-xs text-slate-500">Size:</label>
                            <Input
                              type="number"
                              value={col.shapeSize || 200}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], shapeSize: parseInt(e.target.value) || 200 };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="w-20 h-8 text-xs"
                              min="50"
                              max="600"
                            />
                            <span className="text-xs text-slate-400">px</span>
                            
                            <label className="text-xs text-slate-500 ml-2">Border:</label>
                            <Input
                              type="number"
                              value={col.shapeBorderWidth || 0}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], shapeBorderWidth: parseInt(e.target.value) || 0 };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="w-20 h-8 text-xs"
                              min="0"
                              max="20"
                            />
                            <ColorPickerSwatch value={col.shapeBorderColor || '#ffffff'}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], shapeBorderColor: e.target.value };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="w-8 h-8 rounded border cursor-pointer"
                              title="Border Color"
                            />
                          </>
                        )}
                      </div>
                      
                      {/* Padding Controls */}
                      <div className="flex gap-2 items-center flex-wrap mt-2">
                        <label className="text-xs text-slate-500">Padding:</label>
                        <Select
                          value={col.paddingSide || 'all'}
                          onValueChange={(value) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], paddingSide: value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                        >
                          <SelectTrigger className="w-20 h-8 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All</SelectItem>
                            <SelectItem value="top">Top</SelectItem>
                            <SelectItem value="right">Right</SelectItem>
                            <SelectItem value="bottom">Bottom</SelectItem>
                            <SelectItem value="left">Left</SelectItem>
                          </SelectContent>
                        </Select>
                        <Input
                          type="number"
                          value={col.paddingSize || 0}
                          onChange={(e) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], paddingSize: parseInt(e.target.value) || 0 };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                          className="w-20 h-8 text-xs"
                          min="0"
                          max="100"
                        />
                        <span className="text-xs text-slate-400">px</span>
                      </div>
                      
                      {col.imageUrl && (
                        <div style={{ textAlign: col.imageAlign || 'center' }}>
                          <div 
                            className="inline-block mt-2 overflow-hidden"
                            style={{
                              width: col.shapeMask && col.shapeMask !== 'none' 
                                ? `${col.shapeSize || 200}px` 
                                : (col.imageSizeMode === 'height' ? 'auto' : `${col.imageWidth || 200}px`),
                              height: col.shapeMask && col.shapeMask !== 'none'
                                ? (col.shapeMask === 'ellipse' ? `${(col.shapeSize || 200) * 1.25}px` : `${col.shapeSize || 200}px`)
                                : (col.imageSizeMode === 'height' ? `${col.imageHeight || 200}px` : 'auto'),
                              borderRadius: col.shapeMask === 'circle' ? '50%' : 
                                           col.shapeMask === 'ellipse' ? '50%' :
                                           col.shapeMask === 'rounded' ? '20px' : '0',
                              clipPath: col.shapeMask === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
                                       col.shapeMask === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' :
                                       col.shapeMask === 'octagon' ? 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' :
                                       col.shapeMask === 'star' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' :
                                       col.shapeMask === 'arch' ? 'ellipse(50% 100% at 50% 100%)' :
                                       'none',
                              border: col.shapeBorderWidth && col.shapeMask !== 'none' 
                                ? `${col.shapeBorderWidth}px solid ${col.shapeBorderColor || '#ffffff'}` 
                                : 'none',
                              ...(col.paddingSize ? {
                                paddingTop: (col.paddingSide === 'all' || col.paddingSide === 'top' || !col.paddingSide) ? `${col.paddingSize}px` : '0',
                                paddingRight: (col.paddingSide === 'all' || col.paddingSide === 'right' || !col.paddingSide) ? `${col.paddingSize}px` : '0',
                                paddingBottom: (col.paddingSide === 'all' || col.paddingSide === 'bottom' || !col.paddingSide) ? `${col.paddingSize}px` : '0',
                                paddingLeft: (col.paddingSide === 'all' || col.paddingSide === 'left' || !col.paddingSide) ? `${col.paddingSize}px` : '0',
                              } : {}),
                            }}
                          >
                            <img 
                              src={col.imageUrl} 
                              alt={col.imageAlt || ''} 
                              className="w-full h-full object-cover"
                            />
                          </div>
                          {col.imageCaption && (
                            <p className="text-xs text-slate-500 mt-1 italic" style={{ textAlign: col.imageAlign || 'center' }}>{col.imageCaption}</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Add Button Toggle - works with any column type */}
                  {/* Hide when column is clickable */}
                  {!col.hasColumnLink && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={col.hasButton || false}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[colIndex] = { ...newColumns[colIndex], hasButton: e.target.checked };
                          updateBlock(id, { ...content, columns: newColumns });
                        }}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <span className="text-xs font-medium text-slate-600">Add Button</span>
                    </label>
                    
                    {col.hasButton && (
                      <div className="mt-2 space-y-2 p-2 bg-slate-100 rounded">
                        <Input
                          value={col.buttonText || 'Click Me'}
                          onChange={(e) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], buttonText: e.target.value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                          placeholder="Button Text"
                          className="h-8 text-sm"
                        />
                        <LinkSelector
                          value={col.buttonUrl || ''}
                          onChange={(url) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], buttonUrl: url };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                          pages={pages}
                          anchors={availableAnchors}
                          label="Button Link"
                          testId={`col-${colIndex}-button-url`}
                        />
                        <div className="flex flex-wrap items-center gap-2">
                          {/* Button Style */}
                          <Select
                            value={col.buttonStyle || 'solid'}
                            onValueChange={(value) => {
                              const newColumns = [...content.columns];
                              newColumns[colIndex] = { ...newColumns[colIndex], buttonStyle: value };
                              updateBlock(id, { ...content, columns: newColumns });
                            }}
                          >
                            <SelectTrigger className="w-24 h-7 text-xs">
                              <SelectValue placeholder="Style" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="solid">Solid</SelectItem>
                              <SelectItem value="outline">Outline</SelectItem>
                              <SelectItem value="ghost">Ghost</SelectItem>
                              <SelectItem value="gradient">Gradient</SelectItem>
                              <SelectItem value="soft">Soft</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {/* Button Size */}
                          <Select
                            value={col.buttonSize || 'md'}
                            onValueChange={(value) => {
                              const newColumns = [...content.columns];
                              newColumns[colIndex] = { ...newColumns[colIndex], buttonSize: value };
                              updateBlock(id, { ...content, columns: newColumns });
                            }}
                          >
                            <SelectTrigger className="w-20 h-7 text-xs">
                              <SelectValue placeholder="Size" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="sm">Small</SelectItem>
                              <SelectItem value="md">Medium</SelectItem>
                              <SelectItem value="lg">Large</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {/* Button Color */}
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">BG:</label>
                            <ColorPickerSwatch value={col.buttonColor || '#6366f1'}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], buttonColor: e.target.value };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="w-7 h-7 rounded border cursor-pointer"
                            />
                          </div>
                          
                          {/* Button Text Color */}
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">Text:</label>
                            <ColorPickerSwatch value={col.buttonTextColor || '#ffffff'}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], buttonTextColor: e.target.value };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="w-7 h-7 rounded border cursor-pointer"
                            />
                          </div>
                          
                          {/* Gradient End Color (gradient variant) */}
                          {col.buttonStyle === 'gradient' && (
                            <div className="flex items-center gap-1">
                              <label className="text-xs text-slate-500">Grad:</label>
                              <ColorPickerSwatch value={col.buttonGradientColor || '#8b5cf6'}
                                onChange={(e) => {
                                  const newColumns = [...content.columns];
                                  newColumns[colIndex] = { ...newColumns[colIndex], buttonGradientColor: e.target.value };
                                  updateBlock(id, { ...content, columns: newColumns });
                                }}
                                className="w-7 h-7 rounded border cursor-pointer"
                              />
                            </div>
                          )}

                          {/* Full Width */}
                          <label className="flex items-center gap-1 text-xs text-slate-500">
                            <input
                              type="checkbox"
                              checked={col.buttonFullWidth || false}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], buttonFullWidth: e.target.checked };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="w-3 h-3 rounded"
                            />
                            Full Width
                          </label>
                          {/* Open in new window */}
                          <label className="flex items-center gap-1 text-xs text-slate-500">
                            <input
                              type="checkbox"
                              checked={col.buttonOpenInNewTab || false}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], buttonOpenInNewTab: e.target.checked };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="w-3 h-3 rounded accent-indigo-600"
                              data-testid={`card-col-${colIndex}-new-tab`}
                            />
                            New window
                          </label>
                        </div>

                        {/* Corners / Hover / Icon */}
                        <div className="mt-2">
                          <ButtonEnhancements
                            fields={col}
                            testPrefix={`col-${colIndex}-btn`}
                            onPatch={(patch) => {
                              const newColumns = [...content.columns];
                              newColumns[colIndex] = { ...newColumns[colIndex], ...patch };
                              updateBlock(id, { ...content, columns: newColumns });
                            }}
                          />
                        </div>
                        
                        {/* Button Position Controls */}
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {/* Button Horizontal Alignment */}
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">H:</label>
                            <Select
                              value={col.buttonAlign || 'center'}
                              onValueChange={(value) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], buttonAlign: value };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                            >
                              <SelectTrigger className="w-20 h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="left">Left</SelectItem>
                                <SelectItem value="center">Center</SelectItem>
                                <SelectItem value="right">Right</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Button Vertical Alignment */}
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">V:</label>
                            <Select
                              value={col.buttonVerticalAlign || 'bottom'}
                              onValueChange={(value) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], buttonVerticalAlign: value };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                            >
                              <SelectTrigger className="w-20 h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="top">Top</SelectItem>
                                <SelectItem value="middle">Middle</SelectItem>
                                <SelectItem value="bottom">Bottom</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          
                          {/* Button Margin Top */}
                          <div className="flex items-center gap-1">
                            <label className="text-xs text-slate-500">Top:</label>
                            <Input
                              type="number"
                              value={col.buttonMarginTop || 16}
                              onChange={(e) => {
                                // Clamp to 0–200 so a stray keystroke (e.g. typing
                                // "1616" instead of "16") can't blow the column
                                // height apart and ruin the grid layout.
                                const raw = parseInt(e.target.value);
                                const safe = Number.isFinite(raw) ? Math.min(200, Math.max(0, raw)) : 0;
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], buttonMarginTop: safe };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="w-20 h-7 text-xs"
                              min="0"
                              max="200"
                            />
                            <span className="text-xs text-slate-400">px</span>
                          </div>
                        </div>
                        
                        {/* Button Position - Inside/Outside Card (only when showAsCards is enabled) */}
                        {content.showAsCards && (
                          <div className="flex items-center gap-2 mt-2 p-2 bg-indigo-50 rounded">
                            <label className="text-xs font-medium text-indigo-700">Card Position:</label>
                            <Select
                              value={col.buttonCardPosition || 'inside'}
                              onValueChange={(value) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], buttonCardPosition: value };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                            >
                              <SelectTrigger className="w-24 h-7 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="inside">Inside</SelectItem>
                                <SelectItem value="outside">Outside</SelectItem>
                              </SelectContent>
                            </Select>
                            <span className="text-[10px] text-indigo-600">
                              {col.buttonCardPosition === 'outside' ? '(Below card)' : '(Within card)'}
                            </span>
                          </div>
                        )}
                        
                        {/* Button Preview */}
                        <div className="mt-2 p-2 bg-white rounded text-center">
                          <button
                            className={`
                              ${col.buttonSize === 'sm' ? 'px-3 py-1 text-sm' : ''}
                              ${col.buttonSize === 'lg' ? 'px-6 py-3 text-lg' : ''}
                              ${col.buttonSize === 'md' || !col.buttonSize ? 'px-4 py-2' : ''}
                              ${col.buttonFullWidth ? 'w-full' : ''}
                              ${col.buttonStyle === 'outline' ? 'bg-transparent border-2' : ''}
                              ${col.buttonStyle === 'ghost' ? 'bg-transparent' : ''}
                              rounded-lg font-medium transition-all
                            `}
                            style={{
                              backgroundColor: col.buttonStyle === 'solid' || !col.buttonStyle ? (col.buttonColor || '#6366f1') : 'transparent',
                              color: col.buttonStyle === 'solid' || !col.buttonStyle ? (col.buttonTextColor || '#ffffff') : (col.buttonColor || '#6366f1'),
                              borderColor: col.buttonStyle === 'outline' ? (col.buttonColor || '#6366f1') : 'transparent',
                            }}
                          >
                            {col.buttonText || 'Click Me'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                  
                  {/* Column Link - Makes entire column clickable */}
                  {/* Hide when button is active */}
                  {!col.hasButton && (
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={col.hasColumnLink || false}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[colIndex] = { ...newColumns[colIndex], hasColumnLink: e.target.checked };
                          updateBlock(id, { ...content, columns: newColumns });
                        }}
                        className="w-4 h-4 rounded border-slate-300"
                      />
                      <span className="text-xs font-medium text-slate-600">Make Column Clickable</span>
                    </label>
                    <p className="text-[10px] text-slate-400 mt-1">
                      When enabled, clicking anywhere on this column navigates to the link
                    </p>
                    
                    {col.hasColumnLink && (
                      <div className="mt-2 p-2 bg-slate-100 rounded">
                        <LinkSelector
                          value={col.columnUrl || ''}
                          onChange={(url) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], columnUrl: url };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                          pages={pages}
                          anchors={availableAnchors}
                          label="Column Link"
                          testId={`col-${colIndex}-column-url`}
                        />
                        <div className="flex items-center gap-2 mt-2">
                          <label className="flex items-center gap-1 text-xs text-slate-500">
                            <input
                              type="checkbox"
                              checked={col.columnLinkNewTab || false}
                              onChange={(e) => {
                                const newColumns = [...content.columns];
                                newColumns[colIndex] = { ...newColumns[colIndex], columnLinkNewTab: e.target.checked };
                                updateBlock(id, { ...content, columns: newColumns });
                              }}
                              className="w-3 h-3 rounded"
                            />
                            Open in new tab
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                  )}
                  
                  {/* Per-Column Style Options */}
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="flex items-center gap-1 mb-2">
                      <span className="text-xs font-medium text-slate-600">Column Style</span>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {/* Column Background Color */}
                      <div className="flex items-center gap-1">
                        <label className="text-xs text-slate-500">BG:</label>
                        <ColorPickerSwatch value={col.backgroundColor || '#ffffff'}
                          onChange={(e) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], backgroundColor: e.target.value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                          className="w-7 h-7 rounded border cursor-pointer"
                        />
                        <label className="flex items-center gap-1 text-xs text-slate-400">
                          <input
                            type="checkbox"
                            checked={col.bgTransparent || false}
                            onChange={(e) => {
                              const newColumns = [...content.columns];
                              newColumns[colIndex] = { ...newColumns[colIndex], bgTransparent: e.target.checked };
                              updateBlock(id, { ...content, columns: newColumns });
                            }}
                            className="w-3 h-3 rounded"
                          />
                          None
                        </label>
                      </div>
                      
                      {/* Column Shadow */}
                      <div className="flex items-center gap-1">
                        <label className="text-xs text-slate-500">Shadow:</label>
                        <Select
                          value={col.shadow || 'none'}
                          onValueChange={(value) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], shadow: value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                        >
                          <SelectTrigger className="w-20 h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="sm">Small</SelectItem>
                            <SelectItem value="md">Medium</SelectItem>
                            <SelectItem value="lg">Large</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Column Hover Effect */}
                      <div className="flex items-center gap-1">
                        <label className="text-xs text-slate-500">Hover:</label>
                        <Select
                          value={col.hoverEffect || 'none'}
                          onValueChange={(value) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], hoverEffect: value };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                        >
                          <SelectTrigger className="w-24 h-7 text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">None</SelectItem>
                            <SelectItem value="shadow">Shadow</SelectItem>
                            <SelectItem value="lift">Lift</SelectItem>
                            <SelectItem value="scale">Scale</SelectItem>
                            <SelectItem value="glow">Glow</SelectItem>
                            <SelectItem value="darken">Darken</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      
                      {/* Column Border Radius */}
                      <div className="flex items-center gap-1">
                        <label className="text-xs text-slate-500" title="0 = use Card Radius default">Radius:</label>
                        <Input
                          type="number"
                          value={col.borderRadius || 0}
                          onChange={(e) => {
                            const newColumns = [...content.columns];
                            newColumns[colIndex] = { ...newColumns[colIndex], borderRadius: parseInt(e.target.value) || 0 };
                            updateBlock(id, { ...content, columns: newColumns });
                          }}
                          className="w-12 h-7 text-xs"
                          min="0"
                          max="50"
                        />
                      </div>
                    </div>
                    
                    {/* Column Padding */}
                    <div className="flex items-center gap-2 mt-2">
                      <label className="text-xs text-slate-500">Padding:</label>
                      <Input
                        type="number"
                        value={col.columnPadding || 0}
                        onChange={(e) => {
                          const newColumns = [...content.columns];
                          newColumns[colIndex] = { ...newColumns[colIndex], columnPadding: parseInt(e.target.value) || 0 };
                          updateBlock(id, { ...content, columns: newColumns });
                        }}
                        className="w-20 h-7 text-xs"
                        min="0"
                        max="100"
                      />
                      <span className="text-xs text-slate-400">px</span>
                    </div>
                  </div>
                  
                  {/* Column Settings */}
                  <div className="mt-3 pt-3 border-t border-slate-200">
                    <div className="text-xs font-semibold text-slate-600 mb-2 flex items-center gap-1">
                      <Settings size={12} />
                      Column Settings
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <label className="text-xs text-slate-500">Animation:</label>
                      <Select
                        value={col.animation || 'none'}
                        onValueChange={(value) => {
                          const newColumns = [...content.columns];
                          newColumns[colIndex] = { ...newColumns[colIndex], animation: value };
                          updateBlock(id, { ...content, columns: newColumns });
                        }}
                      >
                        <SelectTrigger className="w-28 h-7 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          <SelectItem value="fadeIn">Fade In</SelectItem>
                          <SelectItem value="fadeInUp">Fade In Up</SelectItem>
                          <SelectItem value="fadeInDown">Fade In Down</SelectItem>
                          <SelectItem value="fadeInLeft">Slide from Left</SelectItem>
                          <SelectItem value="fadeInRight">Slide from Right</SelectItem>
                          <SelectItem value="zoomIn">Zoom In</SelectItem>
                          <SelectItem value="bounceIn">Bounce In</SelectItem>
                          <SelectItem value="pulse">Pulse (Loop)</SelectItem>
                          <SelectItem value="float">Float (Loop)</SelectItem>
                          <SelectItem value="bounce">Bounce (Loop)</SelectItem>
                          <SelectItem value="glowPulse">Glow (Loop)</SelectItem>
                        </SelectContent>
                      </Select>
                      {col.animation && col.animation !== 'none' && !['pulse', 'float', 'bounce', 'glowPulse'].includes(col.animation) && (
                        <label className="flex items-center gap-1 text-xs text-slate-500 ml-2">
                          <input
                            type="checkbox"
                            checked={col.animateOnScroll || false}
                            onChange={(e) => {
                              const newColumns = [...content.columns];
                              newColumns[colIndex] = { ...newColumns[colIndex], animateOnScroll: e.target.checked };
                              updateBlock(id, { ...content, columns: newColumns });
                            }}
                            className="w-3 h-3"
                          />
                          On scroll
                        </label>
                      )}
                    </div>
                  </div>
                </div>
                </SortableColumnCard>
              ))}
              </ColumnDndContext>
            </div>
          </div>
            </div>
            
            {/* Style Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'style'}>
              <BlockStyleControls
                styles={styles || {}}
                onChange={(newStyles) => updateBlock(id, content, newStyles)}
                content={content}
                onContentChange={(newContent) => updateBlock(id, newContent, styles)}
              />
            </BlockTabContent>
            
            {/* Animation Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
              <AnimationControls
                animation={content.animation || {}}
                onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
              />
              {/* Quick Clear Animation Button */}
              {content.animation && content.animation.entrance && content.animation.entrance !== 'none' && (
                <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-xs text-amber-700 mb-2">
                    This block has an entrance animation: <strong>{content.animation.entrance}</strong>
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => updateBlock(id, { 
                      ...content, 
                      animation: { entrance: 'none', speed: 'normal', delay: '0', attention: 'none' } 
                    })}
                    className="text-amber-700 border-amber-300 hover:bg-amber-100"
                  >
                    Clear All Animations
                  </Button>
                </div>
              )}
            </BlockTabContent>
          </>
        )}

        {type === 'two-column' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Content Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'content'}>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Left Column</label>
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => {
                      updateBlock(id, { ...content, leftHtml: e.currentTarget.innerHTML });
                    }}
                    dangerouslySetInnerHTML={{ __html: content.leftHtml || '<p>Left column</p>' }}
                    className="min-h-[100px] outline-none focus:ring-2 focus:ring-indigo-200 rounded p-2 border border-slate-200"
                    data-testid={`two-column-left-${index}`}
                  />
                </div>
                <div>
                  <label className="text-xs text-slate-500 mb-1 block">Right Column</label>
                  <div
                    contentEditable
                    suppressContentEditableWarning
                    onBlur={(e) => {
                      updateBlock(id, { ...content, rightHtml: e.currentTarget.innerHTML });
                    }}
                    dangerouslySetInnerHTML={{ __html: content.rightHtml || '<p>Right column</p>' }}
                    className="min-h-[100px] outline-none focus:ring-2 focus:ring-indigo-200 rounded p-2 border border-slate-200"
                    data-testid={`two-column-right-${index}`}
                  />
                </div>
              </div>
            </BlockTabContent>
            
            {/* Settings Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
              <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg">
                No additional settings for two-column blocks.
              </div>
            </BlockTabContent>
            
            {/* Style Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'style'}>
              <BlockStyleControls
                styles={styles || {}}
                onChange={(newStyles) => updateBlock(id, content, newStyles)}
                content={content}
                onContentChange={(newContent) => updateBlock(id, newContent, styles)}
              />
            </BlockTabContent>
            
            {/* Animation Tab */}
            <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
              <AnimationControls
                animation={content.animation || {}}
                onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
              />
            </BlockTabContent>
          </>
        )}

        {type === 'image-text-row' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Split Pane Layout - Content always visible on left, settings on right */}
            <BlockSplitPane
              showRightPane={['settings', 'style', 'animation'].includes(getActiveTab(id))}
              contentPane={
                <ImageTextCarouselEditor
                  content={content}
                  onChange={(newContent) => updateBlock(id, newContent)}
                  openMediaPicker={(blockId, slideIndex) => {
                    setMediaTargetBlockId(`${id}:carousel:${slideIndex}`);
                    setShowMediaPicker(true);
                  }}
                  blockId={id}
                  pages={pages}
                  anchors={availableAnchors}
                />
              }
              settingsPane={
                <>
                  {/* Settings Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
                    <div className="text-sm text-slate-500 p-4 bg-slate-50 rounded-lg">
                      Image + Text settings are available in the Content panel on the left.
                    </div>
                  </BlockTabContent>
                  
                  {/* Style Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  
                  {/* Animation Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {type === 'image-text-stack' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Split Pane Layout - Content always visible on left, settings on right */}
            <BlockSplitPane
              showRightPane={['settings', 'style', 'animation'].includes(getActiveTab(id))}
              contentPane={
                <div className="space-y-4">
                  {/* Image Selection */}
                  <div className="flex gap-2 items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openMediaPicker(id)}
                      data-testid={`open-layout-stack-media-library-${index}`}
                    >
                      <FolderOpen size={16} className="mr-2" />
                      {content.imageUrl ? 'Change Image' : 'Select Image'}
                    </Button>
                    {content.imageUrl && (
                      <>
                        <span className="text-xs text-green-600">✓ Image selected</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => updateBlock(id, { ...content, imageUrl: '', imageAlt: '' })}
                          className="h-8 px-2 text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Remove image"
                        >
                          <Trash2 size={14} />
                        </Button>
                      </>
                    )}
                  </div>
                  
                  {/* Text Content */}
                  <div>
                    <label className="text-xs text-slate-500 mb-1 block">Text Content</label>
                    <TipTapEditor
                      content={content.textHtml || '<p>Text content</p>'}
                      onChange={(html) => updateBlock(id, { ...content, textHtml: html })}
                      placeholder="Enter text content..."
                      enableAiAssist={true}
                    />
                  </div>
                  
                  {/* Image Preview */}
                  {content.imageUrl && (
                    <div className="mt-2" style={{ textAlign: content.imageAlign || 'center' }}>
                      {content.shapeMask && content.shapeMask !== 'none' ? (
                        <div 
                          className="inline-block overflow-hidden"
                          style={{
                            width: `${content.shapeSize || 250}px`,
                            height: content.shapeMask === 'ellipse' ? `${(content.shapeSize || 250) * 1.25}px` : `${content.shapeSize || 250}px`,
                            maxWidth: '100%',
                            borderRadius: content.shapeMask === 'circle' ? '50%' : 
                                         content.shapeMask === 'ellipse' ? '50%' :
                                         content.shapeMask === 'rounded' ? '20px' : '0',
                            clipPath: content.shapeMask === 'diamond' ? 'polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)' :
                                     content.shapeMask === 'hexagon' ? 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)' :
                                     content.shapeMask === 'octagon' ? 'polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)' :
                                     content.shapeMask === 'star' ? 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)' :
                                     content.shapeMask === 'arch' ? 'ellipse(50% 100% at 50% 100%)' : 'none',
                            border: content.shapeBorderWidth ? `${content.shapeBorderWidth}px solid ${content.shapeBorderColor || '#ffffff'}` : 'none',
                          }}
                        >
                          <img 
                            src={content.imageUrl} 
                            alt={content.imageAlt || ''} 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      ) : (
                        <img 
                          src={content.imageUrl} 
                          alt={content.imageAlt || ''} 
                          className="rounded inline-block" 
                          style={{
                            width: content.imageSizeMode === 'height' ? 'auto' : `${content.imageWidth || 300}px`,
                            height: content.imageSizeMode === 'height' ? `${content.imageHeight || 200}px` : 'auto',
                            maxWidth: '100%'
                          }}
                        />
                      )}
                    </div>
                  )}
                </div>
              }
              settingsPane={
                <>
                  {/* Settings Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'settings'}>
                    <div className="space-y-4">
                      {/* Layout Settings */}
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Layout</div>
                        <div className="flex items-center gap-4">
                          <label className="text-sm font-medium">Image Position:</label>
                          <Select
                            value={content.imagePosition || 'top'}
                            onValueChange={(value) => updateBlock(id, { ...content, imagePosition: value })}
                          >
                            <SelectTrigger className="w-32" data-testid={`image-position-stack-${index}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="top">Top</SelectItem>
                              <SelectItem value="bottom">Bottom</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                      
                      {/* Image Settings */}
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Image Settings</div>
                        <div className="space-y-3">
                          <Input
                            value={content.imageAlt || ''}
                            onChange={(e) => updateBlock(id, { ...content, imageAlt: e.target.value })}
                            placeholder="Alt text (for accessibility)"
                            data-testid={`layout-stack-image-alt-${index}`}
                          />
                          
                          <ImageSizeControls
                            sizeMode={content.imageSizeMode || 'width'}
                            width={content.imageWidth || 300}
                            height={content.imageHeight || 200}
                            align={content.imageAlign || 'center'}
                            onSizeModeChange={(val) => updateBlock(id, { ...content, imageSizeMode: val })}
                            onWidthChange={(val) => updateBlock(id, { ...content, imageWidth: val })}
                            onHeightChange={(val) => updateBlock(id, { ...content, imageHeight: val })}
                            onAlignChange={(val) => updateBlock(id, { ...content, imageAlign: val })}
                          />
                          
                          <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                            <label className="text-xs text-slate-500">Corners:</label>
                            <Select
                              value={content.imageCorners || 'rounded'}
                              onValueChange={(value) => updateBlock(id, { ...content, imageCorners: value })}
                            >
                              <SelectTrigger className="w-24 h-8 text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="square">Square</SelectItem>
                                <SelectItem value="rounded">Rounded</SelectItem>
                                <SelectItem value="pill">Pill</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                      </div>
                      
                      {/* Shape Mask */}
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Shape Mask</div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Select
                            value={content.shapeMask || 'none'}
                            onValueChange={(value) => updateBlock(id, { ...content, shapeMask: value })}
                          >
                            <SelectTrigger className="w-28 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">No Mask</SelectItem>
                              <SelectItem value="circle">Circle</SelectItem>
                              <SelectItem value="ellipse">Ellipse</SelectItem>
                              <SelectItem value="rounded">Rounded</SelectItem>
                              <SelectItem value="diamond">Diamond</SelectItem>
                              <SelectItem value="hexagon">Hexagon</SelectItem>
                              <SelectItem value="octagon">Octagon</SelectItem>
                              <SelectItem value="star">Star</SelectItem>
                              <SelectItem value="arch">Arch</SelectItem>
                            </SelectContent>
                          </Select>
                          
                          {content.shapeMask && content.shapeMask !== 'none' && (
                            <>
                              <label className="text-xs text-slate-500">Size:</label>
                              <Input
                                type="number"
                                value={content.shapeSize || 250}
                                onChange={(e) => updateBlock(id, { ...content, shapeSize: parseInt(e.target.value) || 250 })}
                                className="w-20 h-8 text-xs"
                                min="50"
                                max="600"
                              />
                              <span className="text-xs text-slate-400">px</span>
                              
                              <label className="text-xs text-slate-500 ml-2">Border:</label>
                              <Input
                                type="number"
                                value={content.shapeBorderWidth || 0}
                                onChange={(e) => updateBlock(id, { ...content, shapeBorderWidth: parseInt(e.target.value) || 0 })}
                                className="w-20 h-8 text-xs"
                                min="0"
                                max="20"
                              />
                              <ColorPickerSwatch value={content.shapeBorderColor || '#ffffff'}
                                onChange={(e) => updateBlock(id, { ...content, shapeBorderColor: e.target.value })}
                                className="w-8 h-8 rounded border cursor-pointer"
                                title="Border Color"
                              />
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Padding */}
                      <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
                        <div className="text-xs font-semibold text-slate-500 mb-3 uppercase tracking-wide">Padding</div>
                        <div className="flex flex-wrap items-center gap-2">
                          <Select
                            value={content.paddingSide || 'all'}
                            onValueChange={(value) => updateBlock(id, { ...content, paddingSide: value })}
                          >
                            <SelectTrigger className="w-20 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="all">All</SelectItem>
                              <SelectItem value="top">Top</SelectItem>
                              <SelectItem value="right">Right</SelectItem>
                              <SelectItem value="bottom">Bottom</SelectItem>
                              <SelectItem value="left">Left</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="number"
                            value={content.paddingSize || 0}
                            onChange={(e) => updateBlock(id, { ...content, paddingSize: parseInt(e.target.value) || 0 })}
                            className="w-20 h-8 text-xs"
                            min="0"
                            max="100"
                          />
                          <span className="text-xs text-slate-400">px</span>
                        </div>
                      </div>
                    </div>
                  </BlockTabContent>
                  
                  {/* Style Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  
                  {/* Animation Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {/* Video Embed Block */}
        {type === 'video' && (
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Video URL</label>
              <input
                type="text"
                value={content.videoUrl || ''}
                onChange={(e) => updateBlock(id, { ...content, videoUrl: e.target.value })}
                placeholder="YouTube, Vimeo, or direct video URL"
                className="w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-300 focus:border-blue-400"
                data-testid="video-url-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Width</label>
                <select
                  value={content.width || 'large'}
                  onChange={(e) => updateBlock(id, { ...content, width: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border rounded-lg"
                >
                  <option value="full">Full Width</option>
                  <option value="large">Large</option>
                  <option value="medium">Medium</option>
                  <option value="small">Small</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-slate-500 mb-1 block">Aspect Ratio</label>
                <select
                  value={content.aspectRatio || '16:9'}
                  onChange={(e) => updateBlock(id, { ...content, aspectRatio: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border rounded-lg"
                >
                  <option value="16:9">16:9 (Widescreen)</option>
                  <option value="4:3">4:3 (Standard)</option>
                  <option value="21:9">21:9 (Cinema)</option>
                  <option value="1:1">1:1 (Square)</option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="flex items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={content.autoplay || false} onChange={(e) => updateBlock(id, { ...content, autoplay: e.target.checked })} className="rounded border-slate-300" />
                Autoplay
              </label>
              <label className="flex items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={content.muted || false} onChange={(e) => updateBlock(id, { ...content, muted: e.target.checked })} className="rounded border-slate-300" />
                Muted
              </label>
              <label className="flex items-center gap-1.5 text-xs text-slate-600">
                <input type="checkbox" checked={content.loop || false} onChange={(e) => updateBlock(id, { ...content, loop: e.target.checked })} className="rounded border-slate-300" />
                Loop
              </label>
            </div>

            <div>
              <label className="text-xs font-medium text-slate-500 mb-1 block">Caption (optional)</label>
              <input
                type="text"
                value={content.caption || ''}
                onChange={(e) => updateBlock(id, { ...content, caption: e.target.value })}
                placeholder="Video caption"
                className="w-full px-3 py-1.5 text-xs border rounded-lg"
                data-testid="video-caption-input"
              />
            </div>
          </div>
        )}


        {/* Hero Block */}
        {type === 'hero' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Split Layout: Hero editor always visible on left, Style/Animation on right */}
            <BlockSplitPane
              showRightPane={['style', 'animation'].includes(getActiveTab(id))}
              contentPane={
                <HeroBlockEditor
                  content={content}
                  onChange={(newContent) => updateBlock(id, newContent)}
                  pages={pages}
                  anchors={availableAnchors}
                />
              }
              settingsPane={
                <>
                  {/* Style Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  
                  {/* Animation Tab */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {/* Form Block */}
        {type === 'form' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Split Layout: Form editor always visible on left, Style/Animation on right */}
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <FormBlockEditor
                  content={content}
                  onChange={(newContent) => updateBlock(id, newContent)}
                />
              }
              settingsPane={
                <>
                  {/* Style Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  
                  {/* Animation Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {type === 'gallery' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Split Layout: Gallery editor always visible on left, Style/Animation on right */}
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <GalleryBlockEditor
                  content={content}
                  onChange={(newContent) => updateBlock(id, newContent)}
                />
              }
              settingsPane={
                <>
                  {/* Style Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  
                  {/* Animation Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {/* Ticker Tape Block */}
        {type === 'ticker' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Split Layout: Ticker editor always visible on left, Style/Animation on right */}
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <TickerTapeEditor
                  content={content}
                  onChange={(newContent) => updateBlock(id, newContent)}
                  openMediaPicker={openMediaPicker}
                  blockId={id}
                />
              }
              settingsPane={
                <>
                  {/* Style Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  
                  {/* Animation Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {/* Article Feed Block */}
        {type === 'article-feed' && (
          <>
            {/* Tab Navigation */}
            <BlockTabs 
              activeTab={getActiveTab(id)} 
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            
            {/* Split Layout: Article feed editor always visible on left, Style/Animation on right */}
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <ArticleFeedEditor
                  content={content}
                  onChange={(newContent) => updateBlock(id, newContent)}
                />
              }
              settingsPane={
                <>
                  {/* Style Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  
                  {/* Animation Panel */}
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {/* Events Calendar Block */}
        {type === 'events-calendar' && (
          <>
            <BlockTabs
              activeTab={getActiveTab(id)}
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <EventsCalendarEditor
                  content={content}
                  onChange={(newContent) => updateBlock(id, newContent)}
                />
              }
              settingsPane={
                <>
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {/* Testimonials Block */}
        {type === 'testimonials' && (
          <>
            <BlockTabs
              activeTab={getActiveTab(id)}
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Section Title</Label>
                    <Input
                      data-testid={`testimonials-title-${id}`}
                      value={content.title || ''}
                      onChange={(e) => updateBlock(id, { ...content, title: e.target.value })}
                      placeholder="What Our Customers Say"
                    />
                  </div>
                  <TitleStyleControls
                    content={content}
                    onChange={(newContent) => updateBlock(id, newContent)}
                    styles={styles}
                    onStylesChange={(newStyles) => updateBlock(id, content, newStyles)}
                    testIdPrefix={`testimonials-title-style-${id}`}
                  />
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs">Testimonials</Label>
                      <Button
                        data-testid={`testimonials-add-${id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => updateBlock(id, {
                          ...content,
                          items: [...(content.items || []), { id: `ti-${Date.now()}`, quote: '', author: '', role: '', avatar: '' }]
                        })}
                      >
                        <Plus size={14} className="mr-1" /> Add
                      </Button>
                    </div>
                    {(content.items || []).map((item, idx) => (
                      <div key={item.id} className="p-3 border rounded-lg space-y-2 bg-slate-50">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-500">Testimonial {idx + 1}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 h-6"
                            onClick={() => updateBlock(id, {
                              ...content,
                              items: (content.items || []).filter(i => i.id !== item.id)
                            })}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                        <Textarea
                          value={item.quote || ''}
                          onChange={(e) => {
                            const items = [...(content.items || [])];
                            items[idx] = { ...item, quote: e.target.value };
                            updateBlock(id, { ...content, items });
                          }}
                          placeholder="Quote"
                          rows={2}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={item.author || ''}
                            onChange={(e) => {
                              const items = [...(content.items || [])];
                              items[idx] = { ...item, author: e.target.value };
                              updateBlock(id, { ...content, items });
                            }}
                            placeholder="Author name"
                          />
                          <Input
                            value={item.role || ''}
                            onChange={(e) => {
                              const items = [...(content.items || [])];
                              items[idx] = { ...item, role: e.target.value };
                              updateBlock(id, { ...content, items });
                            }}
                            placeholder="Role / Company"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          {item.avatar ? (
                            <img src={item.avatar} alt="" className="h-10 w-10 object-cover rounded-full border" />
                          ) : (
                            <div className="h-10 w-10 border border-dashed rounded-full flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>
                          )}
                          <Button variant="outline" size="sm" data-testid={`testimonials-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:testiavatar:${idx}`)}>Choose from Library</Button>
                        </div>
                        <Input
                          value={item.avatar || ''}
                          onChange={(e) => {
                            const items = [...(content.items || [])];
                            items[idx] = { ...item, avatar: e.target.value };
                            updateBlock(id, { ...content, items });
                          }}
                          placeholder="…or paste avatar image URL"
                          className="h-8 text-xs"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={
                <>
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {/* Pricing Table Block */}
        {type === 'pricing' && (
          <>
            <BlockTabs
              activeTab={getActiveTab(id)}
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Section Title</Label>
                    <Input
                      data-testid={`pricing-title-${id}`}
                      value={content.title || ''}
                      onChange={(e) => updateBlock(id, { ...content, title: e.target.value })}
                      placeholder="Pricing Plans"
                    />
                  </div>
                  <TitleStyleControls
                    content={content}
                    onChange={(newContent) => updateBlock(id, newContent)}
                    styles={styles}
                    onStylesChange={(newStyles) => updateBlock(id, content, newStyles)}
                    testIdPrefix={`pricing-title-style-${id}`}
                  />
                  <div className="space-y-1 pt-1 border-t border-slate-100">
                    <Label className="text-xs text-slate-500">Button style (applies to all plan buttons)</Label>
                    <ButtonEnhancements
                      fields={content}
                      testPrefix={`pricing-btn-${id}`}
                      onPatch={(patch) => updateBlock(id, { ...content, ...patch })}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Columns</Label>
                    <Select
                      value={String(content.columnCount || 'auto')}
                      onValueChange={(value) => {
                        const next = value === 'auto'
                          ? { ...content }
                          : { ...content, columnCount: parseInt(value, 10) };
                        if (value === 'auto') delete next.columnCount;
                        updateBlock(id, next);
                      }}
                    >
                      <SelectTrigger data-testid={`pricing-columns-${id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="auto">Auto (up to 4)</SelectItem>
                        <SelectItem value="1">1 column</SelectItem>
                        <SelectItem value="2">2 columns</SelectItem>
                        <SelectItem value="3">3 columns</SelectItem>
                        <SelectItem value="4">4 columns</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-400">Controls how many plans show per row on desktop. On mobile, plans always stack vertically.</p>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs">Pricing Plans</Label>
                      <Button
                        data-testid={`pricing-add-${id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => updateBlock(id, {
                          ...content,
                          items: [...(content.items || []), { id: `pp-${Date.now()}`, name: 'Plan', price: '$0', period: '/month', features: ['Feature'], highlighted: false, buttonText: 'Get Started', buttonUrl: '#' }]
                        })}
                      >
                        <Plus size={14} className="mr-1" /> Add Plan
                      </Button>
                    </div>
                    <ColumnDndContext ids={(content.items || []).map((it, i) => it.id || `pricing-${i}`)} onReorder={(from, to) => updateBlock(id, { ...content, items: arrayMove(content.items || [], from, to) })}>
                    {(content.items || []).map((item, idx) => (
                      <SortableColumnCard key={item.id || `pricing-${idx}`} id={item.id || `pricing-${idx}`}>
                      <div className={`p-3 border rounded-lg space-y-2 ${item.highlighted ? 'bg-indigo-50 border-indigo-300' : 'bg-slate-50'}`}>
                        <div className="flex justify-between items-center">
                          <div className="flex items-center gap-1">
                            <span className="text-xs text-slate-500">Plan {idx + 1}</span>
                            <ColumnMoveButtons
                              index={idx}
                              total={(content.items || []).length}
                              testIdPrefix={`pricing-plan-${id}`}
                              onMove={(from, to) => updateBlock(id, { ...content, items: reorderList(content.items, from, to) })}
                            />
                            <DuplicateColumnButton
                              testId={`pricing-plan-${id}-dup-${idx}`}
                              onClick={() => { const items = [...(content.items || [])]; items.splice(idx + 1, 0, { ...item, id: newColId('pp') }); updateBlock(id, { ...content, items }); }}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs flex items-center gap-1 cursor-pointer">
                              <input
                                type="checkbox"
                                checked={!!item.highlighted}
                                onChange={(e) => {
                                  const items = [...(content.items || [])];
                                  items[idx] = { ...item, highlighted: e.target.checked };
                                  updateBlock(id, { ...content, items });
                                }}
                              />
                              Featured
                            </label>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-red-500 h-6"
                              onClick={() => updateBlock(id, {
                                ...content,
                                items: (content.items || []).filter(i => i.id !== item.id)
                              })}
                            >
                              <Trash2 size={14} />
                            </Button>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2">
                          <Input
                            value={item.name || ''}
                            onChange={(e) => {
                              const items = [...(content.items || [])];
                              items[idx] = { ...item, name: e.target.value };
                              updateBlock(id, { ...content, items });
                            }}
                            placeholder="Plan name"
                          />
                          <Input
                            value={item.price || ''}
                            onChange={(e) => {
                              const items = [...(content.items || [])];
                              items[idx] = { ...item, price: e.target.value };
                              updateBlock(id, { ...content, items });
                            }}
                            placeholder="$29"
                          />
                          <Input
                            value={item.period || ''}
                            onChange={(e) => {
                              const items = [...(content.items || [])];
                              items[idx] = { ...item, period: e.target.value };
                              updateBlock(id, { ...content, items });
                            }}
                            placeholder="/month"
                          />
                        </div>
                        <Textarea
                          value={(item.features || []).join('\n')}
                          onChange={(e) => {
                            const items = [...(content.items || [])];
                            items[idx] = { ...item, features: e.target.value.split('\n') };
                            updateBlock(id, { ...content, items });
                          }}
                          placeholder="Features (one per line)"
                          rows={3}
                        />
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={item.buttonText || ''}
                            onChange={(e) => {
                              const items = [...(content.items || [])];
                              items[idx] = { ...item, buttonText: e.target.value };
                              updateBlock(id, { ...content, items });
                            }}
                            placeholder="Button text"
                          />
                          <Input
                            value={item.buttonUrl || ''}
                            onChange={(e) => {
                              const items = [...(content.items || [])];
                              items[idx] = { ...item, buttonUrl: e.target.value };
                              updateBlock(id, { ...content, items });
                            }}
                            placeholder="Button URL"
                          />
                        </div>
                      </div>
                      </SortableColumnCard>
                    ))}
                    </ColumnDndContext>
                  </div>
                </div>
              }
              settingsPane={
                <>
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {/* Feature Comparison Block */}
        {type === 'feature-comparison' && (() => {
          const plans = content.plans || [];
          const categories = content.categories || [];
          const updatePlans = (next) => updateBlock(id, { ...content, plans: next });
          const updateCategories = (next) => updateBlock(id, { ...content, categories: next });
          const addPlan = () => {
            const pid = `pl-${Date.now()}`;
            updatePlans([
              ...plans,
              { id: pid, name: 'New Plan', price: '$0', period: '/month', highlighted: false, buttonText: 'Get Started', buttonUrl: '#' },
            ]);
          };
          const removePlan = (pid) => {
            updateBlock(id, {
              ...content,
              plans: plans.filter((p) => p.id !== pid),
              categories: categories.map((c) => ({
                ...c,
                features: (c.features || []).map((f) => {
                  const v = { ...(f.values || {}) };
                  delete v[pid];
                  return { ...f, values: v };
                }),
              })),
            });
          };
          const addCategory = () => {
            updateCategories([...categories, { id: `cat-${Date.now()}`, name: 'New Section', features: [] }]);
          };
          const removeCategory = (cid) => {
            updateCategories(categories.filter((c) => c.id !== cid));
          };
          const addFeature = (cid) => {
            updateCategories(categories.map((c) =>
              c.id === cid
                ? { ...c, features: [...(c.features || []), { id: `f-${Date.now()}`, name: 'New feature', values: {} }] }
                : c
            ));
          };
          const removeFeature = (cid, fid) => {
            updateCategories(categories.map((c) =>
              c.id === cid ? { ...c, features: (c.features || []).filter((f) => f.id !== fid) } : c
            ));
          };
          const setFeatureValue = (cid, fid, pid, value) => {
            updateCategories(categories.map((c) =>
              c.id === cid
                ? {
                    ...c,
                    features: (c.features || []).map((f) =>
                      f.id === fid ? { ...f, values: { ...(f.values || {}), [pid]: value } } : f
                    ),
                  }
                : c
            ));
          };
          return (
            <>
              <BlockTabs
                activeTab={getActiveTab(id)}
                onTabChange={(tab) => setActiveTab(id, tab)}
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                onTogglePublished={onTogglePublished}
                isFirst={isFirst}
                isLast={isLast}
                isPublished={isPublished}
              />
              <BlockSplitPane
                showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
                contentPane={
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Section Title</Label>
                      <Input
                        data-testid={`fc-title-${id}`}
                        value={content.title || ''}
                        onChange={(e) => updateBlock(id, { ...content, title: e.target.value })}
                        placeholder="Compare Plans"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Subtitle</Label>
                      <Input
                        value={content.subtitle || ''}
                        onChange={(e) => updateBlock(id, { ...content, subtitle: e.target.value })}
                        placeholder="Find the plan that fits your team"
                      />
                    </div>
                    <TitleStyleControls
                      content={content}
                      onChange={(newContent) => updateBlock(id, newContent)}
                      styles={styles}
                      onStylesChange={(newStyles) => updateBlock(id, content, newStyles)}
                      testIdPrefix={`fc-title-style-${id}`}
                    />

                    {/* Plan Recommender Wizard */}
                    <div className="space-y-2 pt-3 border-t border-slate-200">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold">Plan Recommender (Optional)</Label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={!!content.enableRecommender}
                            onChange={(e) => updateBlock(id, { ...content, enableRecommender: e.target.checked })}
                            data-testid={`fc-enable-recommender-${id}`}
                          />
                          <span className="text-xs">Enable</span>
                        </label>
                      </div>
                      {content.enableRecommender && (
                        <div className="space-y-2 p-3 bg-indigo-50/40 border border-indigo-100 rounded-lg">
                          <Input
                            value={content.recommenderTitle || ''}
                            onChange={(e) => updateBlock(id, { ...content, recommenderTitle: e.target.value })}
                            placeholder="Wizard title"
                            className="h-8 text-xs"
                          />
                          <Input
                            value={content.recommenderSubtitle || ''}
                            onChange={(e) => updateBlock(id, { ...content, recommenderSubtitle: e.target.value })}
                            placeholder="Short helper text"
                            className="h-8 text-xs"
                          />
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-slate-500 font-medium">Yes / No Questions</span>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => updateBlock(id, {
                                  ...content,
                                  recommenderQuestions: [
                                    ...(content.recommenderQuestions || []),
                                    { id: `rq-${Date.now()}`, text: 'New question?', yesPlanIds: [] },
                                  ],
                                })}
                                className="h-7 text-xs"
                                data-testid={`fc-add-question-${id}`}
                              >
                                <Plus size={12} className="mr-1" /> Question
                              </Button>
                            </div>
                            {(content.recommenderQuestions || []).map((q, qi) => (
                              <div key={q.id} className="p-2 bg-white border border-slate-200 rounded-md space-y-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-[11px] text-slate-400 font-medium">Q{qi + 1}</span>
                                  <Input
                                    value={q.text || ''}
                                    onChange={(e) => updateBlock(id, {
                                      ...content,
                                      recommenderQuestions: (content.recommenderQuestions || []).map((qq) =>
                                        qq.id === q.id ? { ...qq, text: e.target.value } : qq
                                      ),
                                    })}
                                    placeholder="e.g. Do you need SSO / SAML?"
                                    className="h-7 text-xs flex-1"
                                  />
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => updateBlock(id, {
                                      ...content,
                                      recommenderQuestions: (content.recommenderQuestions || []).filter((qq) => qq.id !== q.id),
                                    })}
                                    className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 size={12} />
                                  </Button>
                                </div>
                                <div className="pl-6">
                                  <p className="text-[11px] text-slate-500 mb-1">If "Yes" → favours:</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {plans.length === 0 && (
                                      <span className="text-[11px] text-slate-400 italic">Add plans first</span>
                                    )}
                                    {plans.map((plan) => {
                                      const checked = (q.yesPlanIds || []).includes(plan.id);
                                      return (
                                        <label
                                          key={plan.id}
                                          className={`px-2 py-0.5 rounded-full text-[11px] cursor-pointer border ${checked ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-300'}`}
                                        >
                                          <input
                                            type="checkbox"
                                            checked={checked}
                                            onChange={() => {
                                              const next = checked
                                                ? (q.yesPlanIds || []).filter((pid) => pid !== plan.id)
                                                : [...(q.yesPlanIds || []), plan.id];
                                              updateBlock(id, {
                                                ...content,
                                                recommenderQuestions: (content.recommenderQuestions || []).map((qq) =>
                                                  qq.id === q.id ? { ...qq, yesPlanIds: next } : qq
                                                ),
                                              });
                                            }}
                                            className="hidden"
                                          />
                                          {plan.name || 'Plan'}
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            ))}
                            <p className="text-[11px] text-slate-400">Each "Yes" answer adds +1 to the favoured plan(s). After all answers, the highest-scoring plan is recommended.</p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Plans */}
                    <div className="space-y-2 pt-3 border-t border-slate-200">
                      <div className="flex justify-between items-center">
                        <Label className="text-xs font-semibold">Plans (Table Columns)</Label>
                        <Button data-testid={`fc-add-plan-${id}`} variant="outline" size="sm" onClick={addPlan}>
                          <Plus size={14} className="mr-1" /> Add Plan
                        </Button>
                      </div>
                      <ColumnDndContext ids={plans.map((p, i) => p.id || `fc-${i}`)} onReorder={(from, to) => updatePlans(arrayMove(plans, from, to))}>
                      {plans.map((plan, idx) => (
                        <SortableColumnCard key={plan.id || `fc-${idx}`} id={plan.id || `fc-${idx}`}>
                        <div className={`p-3 border rounded-lg space-y-2 ${plan.highlighted ? 'bg-indigo-50 border-indigo-300' : 'bg-slate-50'}`}>
                          <div className="flex justify-between items-center">
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-slate-500">Plan {idx + 1}</span>
                              <ColumnMoveButtons index={idx} total={plans.length} testIdPrefix={`fc-plan-${id}`} onMove={(from, to) => updatePlans(reorderList(plans, from, to))} />
                              <DuplicateColumnButton
                                testId={`fc-plan-${id}-dup-${idx}`}
                                onClick={() => {
                                  const nid = newColId('pl');
                                  const np = [...plans];
                                  np.splice(idx + 1, 0, { ...plan, id: nid });
                                  const copyRefs = (arr) => (arr || []).includes(plan.id) ? [...arr, nid] : arr;
                                  updateBlock(id, {
                                    ...content,
                                    plans: np,
                                    categories: (content.categories || []).map((c) => ({ ...c, features: (c.features || []).map((f) => ({ ...f, values: { ...(f.values || {}), [nid]: (f.values || {})[plan.id] } })) })),
                                    recommenderQuestions: (content.recommenderQuestions || []).map((q) => ({ ...q, yesPlanIds: copyRefs(q.yesPlanIds) })),
                                  });
                                }}
                              />
                            </div>
                            <div className="flex items-center gap-2">
                              <label className="text-xs flex items-center gap-1 cursor-pointer">
                                <input
                                  type="checkbox"
                                  checked={!!plan.highlighted}
                                  onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, highlighted: e.target.checked } : p))}
                                />
                                Highlight
                              </label>
                              <Button variant="ghost" size="sm" onClick={() => removePlan(plan.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700">
                                <Trash2 size={12} />
                              </Button>
                            </div>
                          </div>
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              value={plan.name || ''}
                              onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, name: e.target.value } : p))}
                              placeholder="Plan name"
                              className="h-8 text-xs"
                            />
                            <Input
                              value={plan.badge || ''}
                              onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, badge: e.target.value } : p))}
                              placeholder="Badge (e.g. Most Popular)"
                              className="h-8 text-xs"
                            />
                            <Input
                              value={plan.price || ''}
                              onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, price: e.target.value } : p))}
                              placeholder="$29"
                              className="h-8 text-xs"
                            />
                            <Input
                              value={plan.period || ''}
                              onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, period: e.target.value } : p))}
                              placeholder="/month"
                              className="h-8 text-xs"
                            />
                            <Input
                              value={plan.buttonText || ''}
                              onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, buttonText: e.target.value } : p))}
                              placeholder="Button label"
                              className="h-8 text-xs"
                            />
                            <Input
                              value={plan.buttonUrl || ''}
                              onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, buttonUrl: e.target.value } : p))}
                              placeholder="Button URL"
                              className="h-8 text-xs"
                            />
                          </div>
                        </div>
                        </SortableColumnCard>
                      ))}
                      </ColumnDndContext>
                    </div>

                    {/* Feature categories + rows */}
                    <div className="space-y-3 pt-3 border-t border-slate-200">
                      <div className="flex justify-between items-center">
                        <Label className="text-xs font-semibold">Feature Categories</Label>
                        <Button data-testid={`fc-add-category-${id}`} variant="outline" size="sm" onClick={addCategory}>
                          <Plus size={14} className="mr-1" /> Add Section
                        </Button>
                      </div>
                      {categories.map((cat) => (
                        <div key={cat.id} className="border rounded-lg bg-white">
                          <div className="flex items-center gap-2 p-2 border-b bg-slate-50">
                            <Input
                              value={cat.name || ''}
                              onChange={(e) => updateCategories(categories.map((c) => c.id === cat.id ? { ...c, name: e.target.value } : c))}
                              placeholder="Section name (e.g. Core, Support)"
                              className="h-8 text-sm font-medium flex-1"
                            />
                            <Button variant="ghost" size="sm" onClick={() => addFeature(cat.id)} className="h-8 text-xs">
                              <Plus size={12} className="mr-1" /> Feature
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => removeCategory(cat.id)} className="h-8 w-8 p-0 text-red-500 hover:text-red-700">
                              <Trash2 size={12} />
                            </Button>
                          </div>
                          <div className="p-2 space-y-1.5">
                            {(cat.features || []).map((f) => (
                              <div key={f.id} className="space-y-1">
                                <div className="flex items-center gap-2">
                                  <Input
                                    value={f.name || ''}
                                    onChange={(e) => updateCategories(categories.map((c) =>
                                      c.id === cat.id
                                        ? { ...c, features: (c.features || []).map((ff) => ff.id === f.id ? { ...ff, name: e.target.value } : ff) }
                                        : c
                                    ))}
                                    placeholder="Feature name"
                                    className="h-7 text-xs flex-1"
                                  />
                                  <Button variant="ghost" size="sm" onClick={() => removeFeature(cat.id, f.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700">
                                    <Trash2 size={12} />
                                  </Button>
                                </div>
                                <div
                                  className="grid gap-1 pl-3"
                                  style={{ gridTemplateColumns: `repeat(${Math.max(plans.length, 1)}, minmax(0, 1fr))` }}
                                >
                                  {plans.map((p) => (
                                    <Input
                                      key={p.id}
                                      value={(f.values || {})[p.id] || ''}
                                      onChange={(e) => setFeatureValue(cat.id, f.id, p.id, e.target.value)}
                                      placeholder={p.name || 'value'}
                                      className="h-6 text-[11px]"
                                      title="Use 'yes' / 'no' for check / cross, or any text"
                                    />
                                  ))}
                                </div>
                              </div>
                            ))}
                            {(cat.features || []).length === 0 && (
                              <p className="text-[11px] text-slate-400 italic p-2">No features yet. Click "+ Feature" to add one.</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                    <p className="text-[11px] text-slate-400">Tip: Use <code>yes</code> / <code>no</code> in a value cell to render a checkmark or cross.</p>
                  </div>
                }
                rightPane={
                  <>
                    <BlockTabContent activeTab={getActiveTab(id)} tab="style">
                      <BlockStyleControls
                        styles={styles}
                        onChange={(newStyles) => updateBlock(id, content, newStyles)}
                        content={content}
                        onContentChange={(newContent) => updateBlock(id, newContent)}
                      />
                    </BlockTabContent>
                    <BlockTabContent activeTab={getActiveTab(id)} tab="animation">
                      <AnimationControls
                        animation={content.animation || {}}
                        onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                      />
                    </BlockTabContent>
                  </>
                }
              />
            </>
          );
        })()}

        {/* Plan Comparison Table Block (clean, auto-from-pricing) */}
        {type === 'plan-comparison' && (() => {
          const sourceMode = content.source === 'manual' ? 'manual' : 'pricing';
          const plans = content.plans || [];
          const rows = content.rows || [];

          // Local derive (mirrors PreviewRenderer.derivePlanComparison) used for
          // seeding a manual copy + listing plans in the recommender.
          const derive = (items) => {
            const dplans = (items || []).map((p) => ({ id: p.id, name: p.name, price: p.price, period: p.period, highlighted: !!p.highlighted, badge: p.highlighted ? 'Most Popular' : '', buttonText: p.buttonText, buttonUrl: p.buttonUrl }));
            const seen = new Map();
            (items || []).forEach((p) => (p.features || []).forEach((f) => { const l = (f || '').trim(); const k = l.toLowerCase(); if (k && !seen.has(k)) seen.set(k, l); }));
            const inc = (p, k) => (p.features || []).some((f) => (f || '').trim().toLowerCase() === k);
            const drows = Array.from(seen.entries()).map(([k, l]) => ({ id: `row-${k.replace(/[^a-z0-9]+/g, '-')}`, label: l, values: (items || []).reduce((a, p) => { a[p.id] = inc(p, k) ? 'yes' : 'no'; return a; }, {}) })).filter((r) => dplans.some((p) => r.values[p.id] === 'yes'));
            return { plans: dplans, rows: drows };
          };

          const pricingBlock = (blocks || []).find((b) => b.type === 'pricing' && (b.content?.items || []).length > 0);
          const derived = derive(pricingBlock?.content?.items || []);
          const availablePlans = sourceMode === 'manual' ? plans : derived.plans;

          const updatePlans = (next) => updateBlock(id, { ...content, plans: next });
          const updateRows = (next) => updateBlock(id, { ...content, rows: next });
          const addPlan = () => updatePlans([...plans, { id: `pl-${Date.now()}`, name: 'New Plan', price: '$0', period: '/mo', highlighted: false, badge: '', buttonText: 'Get Started', buttonUrl: '#' }]);
          const removePlan = (pid) => updateBlock(id, { ...content, plans: plans.filter((p) => p.id !== pid), rows: rows.map((r) => { const v = { ...(r.values || {}) }; delete v[pid]; return { ...r, values: v }; }) });
          const addRow = () => updateRows([...rows, { id: `row-${Date.now()}`, label: 'New feature', values: {} }]);
          const removeRow = (rid) => updateRows(rows.filter((r) => r.id !== rid));
          const setRowValue = (rid, pid, value) => updateRows(rows.map((r) => r.id === rid ? { ...r, values: { ...(r.values || {}), [pid]: value } } : r));

          return (
            <>
              <BlockTabs
                activeTab={getActiveTab(id)}
                onTabChange={(tab) => setActiveTab(id, tab)}
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
                onDuplicate={onDuplicate}
                onDelete={onDelete}
                onTogglePublished={onTogglePublished}
                isFirst={isFirst}
                isLast={isLast}
                isPublished={isPublished}
              />
              <BlockSplitPane
                showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
                contentPane={
                  <div className="space-y-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Section Title</Label>
                      <Input
                        data-testid={`pc-title-${id}`}
                        value={content.title || ''}
                        onChange={(e) => updateBlock(id, { ...content, title: e.target.value })}
                        placeholder="Compare Plans"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Subtitle</Label>
                      <Input
                        value={content.subtitle || ''}
                        onChange={(e) => updateBlock(id, { ...content, subtitle: e.target.value })}
                        placeholder="All the features, side-by-side."
                      />
                    </div>
                    <TitleStyleControls
                      content={content}
                      onChange={(newContent) => updateBlock(id, newContent)}
                      styles={styles}
                      onStylesChange={(newStyles) => updateBlock(id, content, newStyles)}
                      testIdPrefix={`pc-title-style-${id}`}
                    />

                    {/* Data source */}
                    <div className="space-y-2 pt-3 border-t border-slate-200">
                      <Label className="text-xs font-semibold">Data Source</Label>
                      <Select
                        value={sourceMode}
                        onValueChange={(v) => updateBlock(id, { ...content, source: v })}
                      >
                        <SelectTrigger data-testid={`pc-source-${id}`} className="h-8 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pricing">Auto — from this page's Pricing block</SelectItem>
                          <SelectItem value="manual">Custom — edit plans &amp; rows manually</SelectItem>
                        </SelectContent>
                      </Select>

                      {sourceMode === 'pricing' && (
                        <div className="p-3 bg-indigo-50/50 border border-indigo-100 rounded-lg space-y-2">
                          {pricingBlock ? (
                            <p className="text-[11px] text-slate-600">
                              Auto-generating from your <span className="font-semibold">Pricing block</span> ({derived.plans.length} plans, {derived.rows.length} feature rows). A feature shows a ✓ for every plan whose feature list includes it.
                            </p>
                          ) : (
                            <p className="text-[11px] text-amber-700">No Pricing block found on this page yet. Add a <span className="font-semibold">Pricing Table</span> block (with feature lists), or switch to Custom mode below.</p>
                          )}
                          <Button
                            data-testid={`pc-copy-to-custom-${id}`}
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs"
                            disabled={!pricingBlock}
                            onClick={() => updateBlock(id, { ...content, source: 'manual', plans: derived.plans, rows: derived.rows })}
                          >
                            Edit manually (copy current)
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Manual: Plans */}
                    {sourceMode === 'manual' && (
                      <>
                        <div className="space-y-2 pt-3 border-t border-slate-200">
                          <div className="flex justify-between items-center">
                            <Label className="text-xs font-semibold">Plans (Columns)</Label>
                            <Button data-testid={`pc-add-plan-${id}`} variant="outline" size="sm" onClick={addPlan} className="h-7 text-xs">
                              <Plus size={12} className="mr-1" /> Add Plan
                            </Button>
                          </div>
                          {plans.length === 0 && <p className="text-[11px] text-slate-400 italic">No plans yet. Click "Add Plan".</p>}
                          <ColumnDndContext ids={plans.map((p, i) => p.id || `pc-${i}`)} onReorder={(from, to) => updatePlans(arrayMove(plans, from, to))}>
                          {plans.map((plan, idx) => (
                            <SortableColumnCard key={plan.id || `pc-${idx}`} id={plan.id || `pc-${idx}`}>
                            <div className={`p-3 border rounded-lg space-y-2 ${plan.highlighted ? 'bg-indigo-50 border-indigo-300' : 'bg-slate-50'}`}>
                              <div className="flex justify-between items-center">
                                <div className="flex items-center gap-1">
                                  <span className="text-xs text-slate-500">Plan {idx + 1}</span>
                                  <ColumnMoveButtons index={idx} total={plans.length} testIdPrefix={`pc-plan-${id}`} onMove={(from, to) => updatePlans(reorderList(plans, from, to))} />
                                  <DuplicateColumnButton
                                    testId={`pc-plan-${id}-dup-${idx}`}
                                    onClick={() => {
                                      const nid = newColId('pl');
                                      const np = [...plans];
                                      np.splice(idx + 1, 0, { ...plan, id: nid });
                                      updateBlock(id, {
                                        ...content,
                                        plans: np,
                                        rows: (content.rows || []).map((r) => ({ ...r, values: { ...(r.values || {}), [nid]: (r.values || {})[plan.id] } })),
                                      });
                                    }}
                                  />
                                </div>
                                <div className="flex items-center gap-2">
                                  <label className="text-xs flex items-center gap-1 cursor-pointer">
                                    <input type="checkbox" checked={!!plan.highlighted} onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, highlighted: e.target.checked } : p))} />
                                    Highlight
                                  </label>
                                  <Button variant="ghost" size="sm" onClick={() => removePlan(plan.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700"><Trash2 size={12} /></Button>
                                </div>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <Input value={plan.name || ''} onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, name: e.target.value } : p))} placeholder="Plan name" className="h-8 text-xs" />
                                <Input value={plan.badge || ''} onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, badge: e.target.value } : p))} placeholder="Badge (e.g. Most Popular)" className="h-8 text-xs" />
                                <Input value={plan.price || ''} onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, price: e.target.value } : p))} placeholder="$29" className="h-8 text-xs" />
                                <Input value={plan.period || ''} onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, period: e.target.value } : p))} placeholder="/mo" className="h-8 text-xs" />
                                <Input value={plan.buttonText || ''} onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, buttonText: e.target.value } : p))} placeholder="Button label" className="h-8 text-xs" />
                                <Input value={plan.buttonUrl || ''} onChange={(e) => updatePlans(plans.map((p) => p.id === plan.id ? { ...p, buttonUrl: e.target.value } : p))} placeholder="Button URL" className="h-8 text-xs" />
                              </div>
                            </div>
                            </SortableColumnCard>
                          ))}
                          </ColumnDndContext>
                        </div>

                        {/* Manual: Rows */}
                        <div className="space-y-2 pt-3 border-t border-slate-200">
                          <div className="flex justify-between items-center">
                            <Label className="text-xs font-semibold">Feature Rows</Label>
                            <Button data-testid={`pc-add-row-${id}`} variant="outline" size="sm" onClick={addRow} className="h-7 text-xs">
                              <Plus size={12} className="mr-1" /> Add Row
                            </Button>
                          </div>
                          {rows.length === 0 && <p className="text-[11px] text-slate-400 italic">No rows yet. Click "Add Row".</p>}
                          {rows.map((row) => (
                            <div key={row.id} className="p-2 border border-slate-200 rounded-md bg-white space-y-1.5">
                              <div className="flex items-center gap-2">
                                <Input value={row.label || ''} onChange={(e) => updateRows(rows.map((r) => r.id === row.id ? { ...r, label: e.target.value } : r))} placeholder="Feature name" className="h-7 text-xs flex-1" />
                                <Button variant="ghost" size="sm" onClick={() => removeRow(row.id)} className="h-7 w-7 p-0 text-red-500 hover:text-red-700"><Trash2 size={12} /></Button>
                              </div>
                              {plans.length > 0 && (
                                <div className="grid gap-1 pl-1" style={{ gridTemplateColumns: `repeat(${Math.max(plans.length, 1)}, minmax(0, 1fr))` }}>
                                  {plans.map((p) => (
                                    <Input key={p.id} value={(row.values || {})[p.id] || ''} onChange={(e) => setRowValue(row.id, p.id, e.target.value)} placeholder={p.name || 'value'} className="h-6 text-[11px]" title="Use 'yes' / 'no' for check / cross, or any text" />
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                          <p className="text-[11px] text-slate-400">Tip: type <code>yes</code> / <code>no</code> in a cell for a checkmark or cross, or any text like "5 GB".</p>
                        </div>
                      </>
                    )}

                    {/* Plan Recommender Wizard */}
                    <div className="space-y-2 pt-3 border-t border-slate-200">
                      <div className="flex items-center justify-between">
                        <Label className="text-xs font-semibold">Plan Recommender (Optional)</Label>
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input type="checkbox" checked={!!content.enableRecommender} onChange={(e) => updateBlock(id, { ...content, enableRecommender: e.target.checked })} data-testid={`pc-enable-recommender-${id}`} />
                          <span className="text-xs">Enable</span>
                        </label>
                      </div>
                      {content.enableRecommender && (
                        <div className="space-y-2 p-3 bg-indigo-50/40 border border-indigo-100 rounded-lg">
                          <Input value={content.recommenderTitle || ''} onChange={(e) => updateBlock(id, { ...content, recommenderTitle: e.target.value })} placeholder="Wizard title" className="h-8 text-xs" />
                          <Input value={content.recommenderSubtitle || ''} onChange={(e) => updateBlock(id, { ...content, recommenderSubtitle: e.target.value })} placeholder="Short helper text" className="h-8 text-xs" />
                          <div className="space-y-2">
                            <div className="flex items-center justify-between">
                              <span className="text-[11px] text-slate-500 font-medium">Yes / No Questions</span>
                              <Button variant="outline" size="sm" onClick={() => updateBlock(id, { ...content, recommenderQuestions: [...(content.recommenderQuestions || []), { id: `rq-${Date.now()}`, text: 'New question?', yesPlanIds: [] }] })} className="h-7 text-xs" data-testid={`pc-add-question-${id}`}>
                                <Plus size={12} className="mr-1" /> Question
                              </Button>
                            </div>
                            {(content.recommenderQuestions || []).map((q, qi) => (
                              <div key={q.id} className="p-2 bg-white border border-slate-200 rounded-md space-y-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="text-[11px] text-slate-400 font-medium">Q{qi + 1}</span>
                                  <Input value={q.text || ''} onChange={(e) => updateBlock(id, { ...content, recommenderQuestions: (content.recommenderQuestions || []).map((qq) => qq.id === q.id ? { ...qq, text: e.target.value } : qq) })} placeholder="e.g. Do you need SSO / SAML?" className="h-7 text-xs flex-1" />
                                  <Button variant="ghost" size="sm" onClick={() => updateBlock(id, { ...content, recommenderQuestions: (content.recommenderQuestions || []).filter((qq) => qq.id !== q.id) })} className="h-7 w-7 p-0 text-red-500 hover:text-red-700"><Trash2 size={12} /></Button>
                                </div>
                                <div className="pl-6">
                                  <p className="text-[11px] text-slate-500 mb-1">If "Yes" → favours:</p>
                                  <div className="flex flex-wrap gap-1.5">
                                    {availablePlans.length === 0 && <span className="text-[11px] text-slate-400 italic">Add plans / a pricing block first</span>}
                                    {availablePlans.map((plan) => {
                                      const checked = (q.yesPlanIds || []).includes(plan.id);
                                      return (
                                        <label key={plan.id} className={`px-2 py-0.5 rounded-full text-[11px] cursor-pointer border ${checked ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300 hover:border-indigo-300'}`}>
                                          <input type="checkbox" checked={checked} onChange={() => { const next = checked ? (q.yesPlanIds || []).filter((pid) => pid !== plan.id) : [...(q.yesPlanIds || []), plan.id]; updateBlock(id, { ...content, recommenderQuestions: (content.recommenderQuestions || []).map((qq) => qq.id === q.id ? { ...qq, yesPlanIds: next } : qq) }); }} className="hidden" />
                                          {plan.name || 'Plan'}
                                        </label>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            ))}
                            <p className="text-[11px] text-slate-400">Each "Yes" answer adds +1 to the favoured plan(s). The highest-scoring plan is recommended.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                }
                rightPane={
                  <>
                    <BlockTabContent activeTab={getActiveTab(id)} tab="style">
                      <BlockStyleControls
                        styles={styles}
                        onChange={(newStyles) => updateBlock(id, content, newStyles)}
                        content={content}
                        onContentChange={(newContent) => updateBlock(id, newContent)}
                      />
                    </BlockTabContent>
                    <BlockTabContent activeTab={getActiveTab(id)} tab="animation">
                      <AnimationControls
                        animation={content.animation || {}}
                        onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                      />
                    </BlockTabContent>
                  </>
                }
              />
            </>
          );
        })()}


        {/* FAQ Block */}
        {type === 'faq' && (
          <>
            <BlockTabs
              activeTab={getActiveTab(id)}
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Section Title</Label>
                    <Input
                      data-testid={`faq-title-${id}`}
                      value={content.title || ''}
                      onChange={(e) => updateBlock(id, { ...content, title: e.target.value })}
                      placeholder="Frequently Asked Questions"
                    />
                  </div>
                  <TitleStyleControls
                    content={content}
                    onChange={(newContent) => updateBlock(id, newContent)}
                    styles={styles}
                    onStylesChange={(newStyles) => updateBlock(id, content, newStyles)}
                    testIdPrefix={`faq-title-style-${id}`}
                  />
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs">Questions</Label>
                      <Button
                        data-testid={`faq-add-${id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => updateBlock(id, {
                          ...content,
                          items: [...(content.items || []), { id: `fq-${Date.now()}`, question: '', answer: '' }]
                        })}
                      >
                        <Plus size={14} className="mr-1" /> Add
                      </Button>
                    </div>
                    {(content.items || []).map((item, idx) => (
                      <div key={item.id} className="p-3 border rounded-lg space-y-2 bg-slate-50">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-500">FAQ {idx + 1}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 h-6"
                            onClick={() => updateBlock(id, {
                              ...content,
                              items: (content.items || []).filter(i => i.id !== item.id)
                            })}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                        <Input
                          value={item.question || ''}
                          onChange={(e) => {
                            const items = [...(content.items || [])];
                            items[idx] = { ...item, question: e.target.value };
                            updateBlock(id, { ...content, items });
                          }}
                          placeholder="Question"
                        />
                        <Textarea
                          value={item.answer || ''}
                          onChange={(e) => {
                            const items = [...(content.items || [])];
                            items[idx] = { ...item, answer: e.target.value };
                            updateBlock(id, { ...content, items });
                          }}
                          placeholder="Answer"
                          rows={2}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={
                <>
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {/* Call to Action Block */}
        {type === 'cta' && (
          <>
            <BlockTabs
              activeTab={getActiveTab(id)}
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Title</Label>
                    <Input
                      data-testid={`cta-title-${id}`}
                      value={content.title || ''}
                      onChange={(e) => updateBlock(id, { ...content, title: e.target.value })}
                      placeholder="Ready to Get Started?"
                    />
                  </div>
                  <TitleStyleControls
                    content={content}
                    onChange={(newContent) => updateBlock(id, newContent)}
                    styles={styles}
                    onStylesChange={(newStyles) => updateBlock(id, content, newStyles)}
                    testIdPrefix={`cta-title-style-${id}`}
                  />
                  <div className="space-y-1">
                    <Label className="text-xs">Subtitle</Label>
                    <Input
                      value={content.subtitle || ''}
                      onChange={(e) => updateBlock(id, { ...content, subtitle: e.target.value })}
                      placeholder="Join thousands of satisfied customers"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Button Text</Label>
                      <Input
                        value={content.buttonText || ''}
                        onChange={(e) => updateBlock(id, { ...content, buttonText: e.target.value })}
                        placeholder="Start Now"
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Button URL</Label>
                      <Input
                        value={content.buttonUrl || ''}
                        onChange={(e) => updateBlock(id, { ...content, buttonUrl: e.target.value })}
                        placeholder="#contact"
                      />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={content.buttonOpenInNewTab || false}
                      onChange={(e) => updateBlock(id, { ...content, buttonOpenInNewTab: e.target.checked })}
                      className="w-3.5 h-3.5 rounded accent-indigo-600"
                      data-testid={`cta-button-new-tab-${id}`}
                    />
                    Open button in new tab
                  </label>
                  <div className="space-y-1">
                    <Label className="text-xs">Color Scheme</Label>
                    <Select
                      value={content.style || 'primary'}
                      onValueChange={(v) => updateBlock(id, { ...content, style: v })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="primary">Primary (theme color)</SelectItem>
                        <SelectItem value="secondary">Secondary (theme accent)</SelectItem>
                        <SelectItem value="dark">Dark</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              }
              settingsPane={
                <>
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {/* Team Members Block */}
        {type === 'team' && (
          <>
            <BlockTabs
              activeTab={getActiveTab(id)}
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Section Title</Label>
                    <Input
                      data-testid={`team-title-${id}`}
                      value={content.title || ''}
                      onChange={(e) => updateBlock(id, { ...content, title: e.target.value })}
                      placeholder="Meet Our Team"
                    />
                  </div>
                  <TitleStyleControls
                    content={content}
                    onChange={(newContent) => updateBlock(id, newContent)}
                    styles={styles}
                    onStylesChange={(newStyles) => updateBlock(id, content, newStyles)}
                    testIdPrefix={`team-title-style-${id}`}
                  />
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs">Team Members</Label>
                      <Button
                        data-testid={`team-add-${id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => updateBlock(id, {
                          ...content,
                          members: [...(content.members || []), { id: `tm-${Date.now()}`, name: '', role: '', image: '', bio: '' }]
                        })}
                      >
                        <Plus size={14} className="mr-1" /> Add
                      </Button>
                    </div>
                    {(content.members || []).map((member, idx) => (
                      <div key={member.id} className="p-3 border rounded-lg space-y-2 bg-slate-50">
                        <div className="flex justify-between items-center">
                          <span className="text-xs text-slate-500">Member {idx + 1}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-500 h-6"
                            onClick={() => updateBlock(id, {
                              ...content,
                              members: (content.members || []).filter(m => m.id !== member.id)
                            })}
                          >
                            <Trash2 size={14} />
                          </Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input
                            value={member.name || ''}
                            onChange={(e) => {
                              const members = [...(content.members || [])];
                              members[idx] = { ...member, name: e.target.value };
                              updateBlock(id, { ...content, members });
                            }}
                            placeholder="Name"
                          />
                          <Input
                            value={member.role || ''}
                            onChange={(e) => {
                              const members = [...(content.members || [])];
                              members[idx] = { ...member, role: e.target.value };
                              updateBlock(id, { ...content, members });
                            }}
                            placeholder="Role"
                          />
                        </div>
                        <div className="flex items-center gap-2">
                          {member.image ? (
                            <img src={member.image} alt="" className="h-10 w-10 object-cover rounded-full border" />
                          ) : (
                            <div className="h-10 w-10 border border-dashed rounded-full flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>
                          )}
                          <Button variant="outline" size="sm" data-testid={`team-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:teammember:${idx}`)}>Choose from Library</Button>
                        </div>
                        <Input
                          value={member.image || ''}
                          onChange={(e) => {
                            const members = [...(content.members || [])];
                            members[idx] = { ...member, image: e.target.value };
                            updateBlock(id, { ...content, members });
                          }}
                          placeholder="…or paste photo URL"
                          className="h-8 text-xs"
                        />
                        <Textarea
                          value={member.bio || ''}
                          onChange={(e) => {
                            const members = [...(content.members || [])];
                            members[idx] = { ...member, bio: e.target.value };
                            updateBlock(id, { ...content, members });
                          }}
                          placeholder="Short bio (optional)"
                          rows={2}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={
                <>
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {/* Statistics Block */}
        {type === 'stats' && (
          <>
            <BlockTabs
              activeTab={getActiveTab(id)}
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls
                    content={content}
                    onChange={(newContent) => updateBlock(id, newContent)}
                    styles={styles}
                    onStylesChange={(newStyles) => updateBlock(id, content, newStyles)}
                    testIdPrefix={`stats-title-style-${id}`}
                  />
                  <div className="space-y-1">
                    <Label className="text-xs">Title (optional)</Label>
                    <Input
                      data-testid={`stats-title-${id}`}
                      value={content.title || ''}
                      onChange={(e) => updateBlock(id, { ...content, title: e.target.value })}
                      placeholder="Section title"
                    />
                  </div>
                  <div className="grid grid-cols-3 gap-2 p-2 rounded-md bg-slate-50 border border-slate-200">
                    <div className="flex items-center justify-between col-span-1"><Label className="text-xs">Value</Label><ColorPickerSwatch value={content.statValueColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, statValueColor: e.target.value })} className="h-7 w-10 rounded border" data-testid={`stats-valuecolor-${id}`} /></div>
                    <div className="flex items-center justify-between col-span-1"><Label className="text-xs">Label</Label><ColorPickerSwatch value={content.statLabelColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, statLabelColor: e.target.value })} className="h-7 w-10 rounded border" data-testid={`stats-labelcolor-${id}`} /></div>
                    <div className="col-span-3 space-y-1">
                      <Label className="text-xs">Stats font</Label>
                      <Select value={content.statFontFamily || 'inherit'} onValueChange={(v) => updateBlock(id, { ...content, statFontFamily: v })}>
                        <SelectTrigger className="h-8 text-xs" data-testid={`stats-font-${id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{FONT_FAMILIES.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs">Statistics</Label>
                      <Button
                        data-testid={`stats-add-${id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => updateBlock(id, {
                          ...content,
                          items: [...(content.items || []), { id: `st-${Date.now()}`, value: '0', label: 'Label' }]
                        })}
                      >
                        <Plus size={14} className="mr-1" /> Add
                      </Button>
                    </div>
                    {(content.items || []).map((item, idx) => (
                      <div key={item.id} className="flex gap-2 items-center">
                        <Input
                          value={item.value || ''}
                          onChange={(e) => {
                            const items = [...(content.items || [])];
                            items[idx] = { ...item, value: e.target.value };
                            updateBlock(id, { ...content, items });
                          }}
                          placeholder="100+"
                          className="w-24"
                        />
                        <Input
                          value={item.label || ''}
                          onChange={(e) => {
                            const items = [...(content.items || [])];
                            items[idx] = { ...item, label: e.target.value };
                            updateBlock(id, { ...content, items });
                          }}
                          placeholder="Label"
                          className="flex-1"
                        />
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-500"
                          onClick={() => updateBlock(id, {
                            ...content,
                            items: (content.items || []).filter(i => i.id !== item.id)
                          })}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={
                <>
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls
                      styles={styles || {}}
                      onChange={(newStyles) => updateBlock(id, content, newStyles)}
                      content={content}
                      onContentChange={(newContent) => updateBlock(id, newContent, styles)}
                    />
                  </BlockTabContent>
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls
                      animation={content.animation || {}}
                      onChange={(newAnimation) => updateBlock(id, { ...content, animation: newAnimation })}
                    />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {type === 'logo-cloud' && (
          <>
            <BlockTabs
              activeTab={getActiveTab(id)}
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Title (optional)</Label>
                    <Input
                      data-testid={`logocloud-title-${id}`}
                      value={content.title || ''}
                      onChange={(e) => updateBlock(id, { ...content, title: e.target.value })}
                      placeholder="Trusted by leading teams"
                    />
                  </div>
                  <TitleStyleControls
                    content={content}
                    onChange={(nc) => updateBlock(id, nc)}
                    styles={styles}
                    onStylesChange={(ns) => updateBlock(id, content, ns)}
                    testIdPrefix={`logocloud-title-style-${id}`}
                  />
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Switch
                        data-testid={`logocloud-grayscale-${id}`}
                        checked={content.grayscale !== false}
                        onCheckedChange={(v) => updateBlock(id, { ...content, grayscale: v })}
                      />
                      <Label className="text-xs cursor-pointer">Grayscale (color on hover)</Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Columns</Label>
                      <Select value={String(content.columns || 5)} onValueChange={(v) => updateBlock(id, { ...content, columns: parseInt(v) })}>
                        <SelectTrigger className="w-20 h-8 text-xs" data-testid={`logocloud-columns-${id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{[3, 4, 5, 6].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <Label className="text-xs">Logos</Label>
                      <Button
                        data-testid={`logocloud-add-${id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => updateBlock(id, { ...content, logos: [...(content.logos || []), { id: `lg-${Date.now()}`, imageUrl: '', name: '', link: '' }] })}
                      >
                        <Plus size={14} className="mr-1" /> Add
                      </Button>
                    </div>
                    {(content.logos || []).map((logo, idx) => (
                      <div key={logo.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`logocloud-item-${id}-${idx}`}>
                        <div className="flex items-center gap-2">
                          {logo.imageUrl ? (
                            <img src={logo.imageUrl} alt="" className="h-8 w-12 object-contain border rounded" />
                          ) : (
                            <div className="h-8 w-12 border border-dashed rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>
                          )}
                          <Button variant="outline" size="sm" data-testid={`logocloud-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:logocloud:${idx}`)}>Image</Button>
                          <Button variant="ghost" size="sm" className="text-red-500 ml-auto" onClick={() => updateBlock(id, { ...content, logos: (content.logos || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <Input value={logo.imageUrl || ''} onChange={(e) => { const logos = [...(content.logos || [])]; logos[idx] = { ...logo, imageUrl: e.target.value }; updateBlock(id, { ...content, logos }); }} placeholder="…or paste image URL" className="h-8 text-xs" />
                        <Input value={logo.name || ''} onChange={(e) => { const logos = [...(content.logos || [])]; logos[idx] = { ...logo, name: e.target.value }; updateBlock(id, { ...content, logos }); }} placeholder="Name (alt / fallback text)" className="h-8 text-xs" />
                        <Input value={logo.link || ''} onChange={(e) => { const logos = [...(content.logos || [])]; logos[idx] = { ...logo, link: e.target.value }; updateBlock(id, { ...content, logos }); }} placeholder="Link (optional)" className="h-8 text-xs" />
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={
                <>
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} />
                  </BlockTabContent>
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {type === 'hero-spotlight' && (
          <>
            <BlockTabs
              activeTab={getActiveTab(id)}
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`herospot-titlestyle-${id}`} />
                  <HeroLayoutControls id={id} content={content} updateBlock={updateBlock} prefix="herospot" includeHeight={true} includeOverlay={true} includeTextColor={true} />
                  <HeroTypographyControls id={id} content={content} updateBlock={updateBlock} prefix="herospot" />
                  <div className="space-y-1">
                    <Label className="text-xs">Eyebrow (small badge)</Label>
                    <Input data-testid={`herospot-eyebrow-${id}`} value={content.eyebrow || ''} onChange={(e) => updateBlock(id, { ...content, eyebrow: e.target.value })} placeholder="NEW" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Headline</Label>
                    <Input data-testid={`herospot-headline-${id}`} value={content.headline || ''} onChange={(e) => updateBlock(id, { ...content, headline: e.target.value })} placeholder="Build something people love" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Subheadline</Label>
                    <Textarea data-testid={`herospot-subheadline-${id}`} value={content.subheadline || ''} onChange={(e) => updateBlock(id, { ...content, subheadline: e.target.value })} rows={2} placeholder="A short supporting sentence." />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Primary button</Label>
                      <Input data-testid={`herospot-primary-text-${id}`} value={content.primaryButtonText || ''} onChange={(e) => updateBlock(id, { ...content, primaryButtonText: e.target.value })} placeholder="Get Started" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Primary link</Label>
                      <Input data-testid={`herospot-primary-url-${id}`} value={content.primaryButtonUrl || ''} onChange={(e) => updateBlock(id, { ...content, primaryButtonUrl: e.target.value })} placeholder="https://…" />
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mt-1">
                      <input type="checkbox" checked={content.primaryButtonOpenInNewTab || false} onChange={(e) => updateBlock(id, { ...content, primaryButtonOpenInNewTab: e.target.checked })} className="w-3.5 h-3.5 rounded accent-indigo-600" data-testid={`primary-btn-newtab-${id}`} />
                      Open in new tab
                    </label>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Secondary button</Label>
                      <Input data-testid={`herospot-secondary-text-${id}`} value={content.secondaryButtonText || ''} onChange={(e) => updateBlock(id, { ...content, secondaryButtonText: e.target.value })} placeholder="Learn more" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Secondary link</Label>
                      <Input data-testid={`herospot-secondary-url-${id}`} value={content.secondaryButtonUrl || ''} onChange={(e) => updateBlock(id, { ...content, secondaryButtonUrl: e.target.value })} placeholder="https://…" />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between"><Label className="text-xs">Primary button colour</Label><ColorPickerSwatch value={content.primaryButtonColor || '#6366f1'} onChange={(e) => updateBlock(id, { ...content, primaryButtonColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herospot-pbtn-color-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Secondary button colour</Label><ColorPickerSwatch value={content.secondaryButtonColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, secondaryButtonColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herospot-sbtn-color-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Primary button text</Label><ColorPickerSwatch value={content.primaryButtonTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, primaryButtonTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herospot-pbtn-textcolor-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Secondary button text</Label><ColorPickerSwatch value={content.secondaryButtonColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, secondaryButtonColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herospot-sbtn-textcolor-${id}`} /></div>
                  </div>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Align</Label>
                      <Select value={content.align || 'center'} onValueChange={(v) => updateBlock(id, { ...content, align: v })}>
                        <SelectTrigger className="w-28 h-8 text-xs" data-testid={`herospot-align-${id}`}><SelectValue /></SelectTrigger>
                        <SelectContent><SelectItem value="center">Center</SelectItem><SelectItem value="left">Left</SelectItem></SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label className="text-xs">Height (px)</Label>
                      <Input type="number" data-testid={`herospot-height-${id}`} value={content.height || 560} onChange={(e) => updateBlock(id, { ...content, height: parseInt(e.target.value) || 560 })} className="w-24 h-8 text-xs" />
                    </div>
                  </div>
                  <p className="text-[11px] text-slate-400">Tip: set the accent (glow + buttons) via “Border color” and the base background under the Style tab.</p>
                </div>
              }
              settingsPane={
                <>
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} />
                  </BlockTabContent>
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {type === 'countdown' && (
          <>
            <BlockTabs
              activeTab={getActiveTab(id)}
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`countdown-titlestyle-${id}`} />
                  <div className="space-y-1">
                    <Label className="text-xs">Heading</Label>
                    <Input data-testid={`countdown-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} placeholder="Launching soon" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Subheading</Label>
                    <Textarea data-testid={`countdown-subheading-${id}`} value={content.subheading || ''} onChange={(e) => updateBlock(id, { ...content, subheading: e.target.value })} rows={2} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Target date &amp; time</Label>
                    <Input type="datetime-local" data-testid={`countdown-date-${id}`} value={content.targetDate || ''} onChange={(e) => updateBlock(id, { ...content, targetDate: e.target.value })} />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">When expired, show</Label>
                    <Input data-testid={`countdown-expired-${id}`} value={content.expiredText || ''} onChange={(e) => updateBlock(id, { ...content, expiredText: e.target.value })} placeholder="We're live!" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Button text</Label>
                      <Input data-testid={`countdown-btn-text-${id}`} value={content.buttonText || ''} onChange={(e) => updateBlock(id, { ...content, buttonText: e.target.value })} placeholder="Notify me" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Button link</Label>
                      <Input data-testid={`countdown-btn-url-${id}`} value={content.buttonUrl || ''} onChange={(e) => updateBlock(id, { ...content, buttonUrl: e.target.value })} placeholder="https://…" />
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mt-1">
                      <input type="checkbox" checked={content.buttonOpenInNewTab || false} onChange={(e) => updateBlock(id, { ...content, buttonOpenInNewTab: e.target.checked })} className="w-3.5 h-3.5 rounded accent-indigo-600" data-testid={`btn-newtab-${id}`} />
                      Open in new tab
                    </label>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between"><Label className="text-xs">Time/date background</Label><ColorPickerSwatch value={content.boxBgColor || '#6366f1'} onChange={(e) => updateBlock(id, { ...content, boxBgColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`countdown-boxbg-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Time/date font</Label><ColorPickerSwatch value={content.boxTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, boxTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`countdown-boxtext-${id}`} /></div>
                    <div className="col-span-2 flex items-center justify-between"><Label className="text-xs">Button text colour</Label><ColorPickerSwatch value={content.buttonTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, buttonTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`countdown-btntextcolor-${id}`} /></div>
                    <div className="col-span-2 pt-1"><ButtonEnhancements fields={content} testPrefix={`countdown-btn`} onPatch={(patch) => updateBlock(id, { ...content, ...patch })} /></div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Align</Label>
                    <Select value={content.align || 'center'} onValueChange={(v) => updateBlock(id, { ...content, align: v })}>
                      <SelectTrigger className="w-28 h-8 text-xs" data-testid={`countdown-align-${id}`}><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="center">Center</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <p className="text-[11px] text-slate-400">Tip: set the digit / button color via “Border color” under the Style tab.</p>
                </div>
              }
              settingsPane={
                <>
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} />
                  </BlockTabContent>
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {type === 'before-after' && (
          <>
            <BlockTabs
              activeTab={getActiveTab(id)}
              onTabChange={(tab) => setActiveTab(id, tab)}
              onMoveUp={onMoveUp}
              onMoveDown={onMoveDown}
              onDuplicate={onDuplicate}
              onDelete={onDelete}
              onTogglePublished={onTogglePublished}
              isFirst={isFirst}
              isLast={isLast}
              isPublished={isPublished}
            />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Heading (optional)</Label>
                    <Input data-testid={`beforeafter-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} placeholder="See the difference" />
                  </div>
                  {['before', 'after'].map((which) => {
                    const key = which === 'after' ? 'afterImage' : 'beforeImage';
                    return (
                      <div key={which} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`beforeafter-item-${which}-${id}`}>
                        <Label className="text-xs capitalize">{which} image</Label>
                        <div className="flex items-center gap-2">
                          {content[key] ? (
                            <img src={content[key]} alt="" className="h-10 w-16 object-cover border rounded" />
                          ) : (
                            <div className="h-10 w-16 border border-dashed rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>
                          )}
                          <Button variant="outline" size="sm" data-testid={`beforeafter-pick-${which}-${id}`} onClick={() => openMediaPicker(`${id}:beforeafter:${which}`)}>Choose</Button>
                        </div>
                        <Input value={content[key] || ''} onChange={(e) => updateBlock(id, { ...content, [key]: e.target.value })} placeholder="…or paste image URL" className="h-8 text-xs" data-testid={`beforeafter-urlinput-${which}-${id}`} />
                      </div>
                    );
                  })}
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                      <Label className="text-xs">Before label</Label>
                      <Input data-testid={`beforeafter-beforelabel-${id}`} value={content.beforeLabel || ''} onChange={(e) => updateBlock(id, { ...content, beforeLabel: e.target.value })} placeholder="Before" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">After label</Label>
                      <Input data-testid={`beforeafter-afterlabel-${id}`} value={content.afterLabel || ''} onChange={(e) => updateBlock(id, { ...content, afterLabel: e.target.value })} placeholder="After" />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">Height (px)</Label>
                    <Input type="number" data-testid={`beforeafter-height-${id}`} value={content.height || 420} onChange={(e) => updateBlock(id, { ...content, height: parseInt(e.target.value) || 420 })} className="w-24 h-8 text-xs" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Slider start position: {content.startPosition != null ? content.startPosition : 50}%</Label>
                    <input type="range" min="0" max="100" step="1" data-testid={`beforeafter-start-${id}`} value={content.startPosition != null ? content.startPosition : 50} onChange={(e) => updateBlock(id, { ...content, startPosition: parseInt(e.target.value) })} className="w-full" />
                    <p className="text-[11px] text-slate-400">Where the wipe handle sits when the page first loads.</p>
                  </div>
                  <p className="text-[11px] text-slate-400">Drag the handle on the published page to wipe between the two images.</p>
                </div>
              }
              settingsPane={
                <>
                  <BlockTabContent isActive={getActiveTab(id) === 'style'}>
                    <BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} />
                  </BlockTabContent>
                  <BlockTabContent isActive={getActiveTab(id) === 'animation'}>
                    <AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} />
                  </BlockTabContent>
                </>
              }
            />
          </>
        )}

        {type === 'hero-tabs' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`herotabs-titlestyle-${id}`} />
                  <HeroLayoutControls id={id} content={content} updateBlock={updateBlock} prefix="herotabs" />
                  <HeroTypographyControls id={id} content={content} updateBlock={updateBlock} prefix="herotabs" />
                  <div className="space-y-1"><Label className="text-xs">Eyebrow</Label><Input data-testid={`herotabs-eyebrow-${id}`} value={content.eyebrow || ''} onChange={(e) => updateBlock(id, { ...content, eyebrow: e.target.value })} placeholder="Featured" /></div>
                  <div className="space-y-1"><Label className="text-xs">Headline</Label><Input data-testid={`herotabs-headline-${id}`} value={content.headline || ''} onChange={(e) => updateBlock(id, { ...content, headline: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subtitle</Label><Textarea data-testid={`herotabs-subtitle-${id}`} value={content.subtitle || ''} onChange={(e) => updateBlock(id, { ...content, subtitle: e.target.value })} rows={2} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Subtitle colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herotabs-subcolor-${id}`} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between"><Label className="text-xs">Tabs colour</Label><ColorPickerSwatch value={content.tabColor || '#6366f1'} onChange={(e) => updateBlock(id, { ...content, tabColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herotabs-tabcolor-${id}`} /></div>
                    <div className="flex items-center justify-between gap-2"><Label className="text-xs">Tab shape</Label><Select value={content.tabShape || 'rounded'} onValueChange={(v) => updateBlock(id, { ...content, tabShape: v })}><SelectTrigger className="w-28 h-8 text-xs" data-testid={`herotabs-tabshape-${id}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="rounded">Rounded</SelectItem><SelectItem value="rectangle">Rectangle</SelectItem></SelectContent></Select></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">CTA text</Label><Input data-testid={`herotabs-cta-text-${id}`} value={content.ctaText || ''} onChange={(e) => updateBlock(id, { ...content, ctaText: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs">CTA link</Label><Input data-testid={`herotabs-cta-url-${id}`} value={content.ctaUrl || ''} onChange={(e) => updateBlock(id, { ...content, ctaUrl: e.target.value })} placeholder="https://…" />
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mt-1">
                      <input type="checkbox" checked={content.ctaOpenInNewTab || false} onChange={(e) => updateBlock(id, { ...content, ctaOpenInNewTab: e.target.checked })} className="w-3.5 h-3.5 rounded accent-indigo-600" data-testid={`cta-newtab-${id}`} />
                      Open in new tab
                    </label></div>
                    <div className="col-span-2 flex items-center justify-between"><Label className="text-xs">Button text colour</Label><ColorPickerSwatch value={content.buttonTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, buttonTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herotabs-btntextcolor-${id}`} /></div>
                    <div className="col-span-2 pt-1"><ButtonEnhancements fields={content} testPrefix={`herotabs-btn`} onPatch={(patch) => updateBlock(id, { ...content, ...patch })} /></div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Tabs (each shows an image)</Label><Button variant="outline" size="sm" data-testid={`herotabs-add-${id}`} onClick={() => updateBlock(id, { ...content, tabs: [...(content.tabs || []), { id: `t-${Date.now()}`, label: '', imageUrl: '' }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.tabs || []).map((t, idx) => (
                      <div key={t.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`herotabs-item-${id}-${idx}`}>
                        <div className="flex items-center gap-2">
                          {t.imageUrl ? <img src={t.imageUrl} alt="" className="h-8 w-12 object-cover border rounded" /> : <div className="h-8 w-12 border border-dashed rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>}
                          <Button variant="outline" size="sm" data-testid={`herotabs-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:heroimg:tabs:${idx}`)}>Image</Button>
                          <Button variant="ghost" size="sm" className="text-red-500 ml-auto" onClick={() => updateBlock(id, { ...content, tabs: (content.tabs || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <Input value={t.label || ''} onChange={(e) => { const tabs = [...(content.tabs || [])]; tabs[idx] = { ...t, label: e.target.value }; updateBlock(id, { ...content, tabs }); }} placeholder="Tab label" className="h-8 text-xs" />
                        <Input value={t.imageUrl || ''} onChange={(e) => { const tabs = [...(content.tabs || [])]; tabs[idx] = { ...t, imageUrl: e.target.value }; updateBlock(id, { ...content, tabs }); }} placeholder="…or paste image URL" className="h-8 text-xs" />
                        <details className="text-xs">
                          <summary className="cursor-pointer text-slate-500 hover:text-slate-700 py-1">Tab label typography</summary>
                          <div className="mt-2">
                            <HeroItemTypographyRow
                              style={t.labelStyle || {}}
                              defaultSize={14}
                              testPrefix={`herotabs-labelstyle-${id}-${idx}`}
                              onPatch={(patch) => {
                                const tabs = [...(content.tabs || [])];
                                const merged = { ...(tabs[idx].labelStyle || {}), ...patch };
                                Object.keys(merged).forEach(k => { if (merged[k] === undefined || merged[k] === '') delete merged[k]; });
                                tabs[idx] = { ...t, labelStyle: merged };
                                updateBlock(id, { ...content, tabs });
                              }}
                            />
                          </div>
                        </details>
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'hero-feature-cards' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`herofeat-titlestyle-${id}`} />
                  <HeroLayoutControls id={id} content={content} updateBlock={updateBlock} prefix="herofeat" />
                  <HeroTypographyControls id={id} content={content} updateBlock={updateBlock} prefix="herofeat" />
                  <div className="space-y-1"><Label className="text-xs">Headline</Label><Input data-testid={`herofeat-headline-${id}`} value={content.headline || ''} onChange={(e) => updateBlock(id, { ...content, headline: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Accent word (coloured)</Label><Input data-testid={`herofeat-accent-${id}`} value={content.accentWord || ''} onChange={(e) => updateBlock(id, { ...content, accentWord: e.target.value })} placeholder="values." /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between"><Label className="text-xs">Accent word colour</Label><ColorPickerSwatch value={content.accentColor || '#6366f1'} onChange={(e) => updateBlock(id, { ...content, accentColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herofeat-accentcolor-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Card text colour</Label><ColorPickerSwatch value={content.cardTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, cardTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herofeat-cardtextcolor-${id}`} /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Subtitle</Label><Textarea data-testid={`herofeat-subtitle-${id}`} value={content.subtitle || ''} onChange={(e) => updateBlock(id, { ...content, subtitle: e.target.value })} rows={2} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Subtitle colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herofeat-subcolor-${id}`} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">CTA text</Label><Input data-testid={`herofeat-cta-text-${id}`} value={content.ctaText || ''} onChange={(e) => updateBlock(id, { ...content, ctaText: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs">CTA link</Label><Input data-testid={`herofeat-cta-url-${id}`} value={content.ctaUrl || ''} onChange={(e) => updateBlock(id, { ...content, ctaUrl: e.target.value })} placeholder="https://…" /></div>
                    <div className="col-span-2 flex items-center justify-between"><Label className="text-xs">Button text colour</Label><ColorPickerSwatch value={content.buttonTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, buttonTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herofeat-btntextcolor-${id}`} /></div>
                    <div className="col-span-2 pt-1"><ButtonEnhancements fields={content} testPrefix={`herofeat-btn`} onPatch={(patch) => updateBlock(id, { ...content, ...patch })} /></div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Feature cards</Label><Button variant="outline" size="sm" data-testid={`herofeat-add-${id}`} onClick={() => updateBlock(id, { ...content, features: [...(content.features || []), { id: `f-${Date.now()}`, title: '', imageUrl: '', link: '' }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.features || []).map((f, idx) => (
                      <div key={f.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`herofeat-item-${id}-${idx}`}>
                        <div className="flex items-center gap-2">
                          {f.imageUrl ? <img src={f.imageUrl} alt="" className="h-8 w-12 object-cover border rounded" /> : <div className="h-8 w-12 border border-dashed rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>}
                          <Button variant="outline" size="sm" data-testid={`herofeat-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:heroimg:features:${idx}`)}>Image</Button>
                          <Button variant="ghost" size="sm" className="text-red-500 ml-auto" onClick={() => updateBlock(id, { ...content, features: (content.features || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <Input value={f.title || ''} onChange={(e) => { const features = [...(content.features || [])]; features[idx] = { ...f, title: e.target.value }; updateBlock(id, { ...content, features }); }} placeholder="Card title" className="h-8 text-xs" />
                        <Input value={f.link || ''} onChange={(e) => { const features = [...(content.features || [])]; features[idx] = { ...f, link: e.target.value }; updateBlock(id, { ...content, features }); }} placeholder="Link (optional)" className="h-8 text-xs" />
                        <details className="text-xs">
                          <summary className="cursor-pointer text-slate-500 hover:text-slate-700 py-1">Card title typography</summary>
                          <div className="mt-2">
                            <HeroItemTypographyRow
                              style={f.titleStyle || {}}
                              defaultSize={18}
                              testPrefix={`herofeat-titlestyle-${id}-${idx}`}
                              onPatch={(patch) => {
                                const features = [...(content.features || [])];
                                const merged = { ...(features[idx].titleStyle || {}), ...patch };
                                Object.keys(merged).forEach(k => { if (merged[k] === undefined || merged[k] === '') delete merged[k]; });
                                features[idx] = { ...f, titleStyle: merged };
                                updateBlock(id, { ...content, features });
                              }}
                            />
                          </div>
                        </details>
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'hero-split-stats' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`herostats-titlestyle-${id}`} />
                  <HeroLayoutControls id={id} content={content} updateBlock={updateBlock} prefix="herostats" />
                  <HeroTypographyControls id={id} content={content} updateBlock={updateBlock} prefix="herostats" />
                  <div className="space-y-1"><Label className="text-xs">Headline</Label><Input data-testid={`herostats-headline-${id}`} value={content.headline || ''} onChange={(e) => updateBlock(id, { ...content, headline: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subtitle</Label><Textarea data-testid={`herostats-subtitle-${id}`} value={content.subtitle || ''} onChange={(e) => updateBlock(id, { ...content, subtitle: e.target.value })} rows={2} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Subtitle colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herostats-subcolor-${id}`} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Stats colour</Label><ColorPickerSwatch value={content.statColor || '#6366f1'} onChange={(e) => updateBlock(id, { ...content, statColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herostats-statcolor-${id}`} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Primary button</Label><Input data-testid={`herostats-pbtn-text-${id}`} value={content.primaryButtonText || ''} onChange={(e) => updateBlock(id, { ...content, primaryButtonText: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs">Primary link</Label><Input data-testid={`herostats-pbtn-url-${id}`} value={content.primaryButtonUrl || ''} onChange={(e) => updateBlock(id, { ...content, primaryButtonUrl: e.target.value })} placeholder="https://…" /></div>
                    <div className="space-y-1"><Label className="text-xs">Secondary button</Label><Input data-testid={`herostats-sbtn-text-${id}`} value={content.secondaryButtonText || ''} onChange={(e) => updateBlock(id, { ...content, secondaryButtonText: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs">Secondary link</Label><Input data-testid={`herostats-sbtn-url-${id}`} value={content.secondaryButtonUrl || ''} onChange={(e) => updateBlock(id, { ...content, secondaryButtonUrl: e.target.value })} placeholder="https://…" /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Primary btn text</Label><ColorPickerSwatch value={content.buttonTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, buttonTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herostats-btntextcolor-${id}`} /></div>
                    <div className="col-span-2 pt-1"><ButtonEnhancements fields={content} testPrefix={`herostats-btn`} onPatch={(patch) => updateBlock(id, { ...content, ...patch })} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Secondary btn text</Label><ColorPickerSwatch value={content.secondaryButtonTextColor || '#0f172a'} onChange={(e) => updateBlock(id, { ...content, secondaryButtonTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herostats-sbtntextcolor-${id}`} /></div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Stats</Label><Button variant="outline" size="sm" data-testid={`herostats-addstat-${id}`} onClick={() => updateBlock(id, { ...content, stats: [...(content.stats || []), { value: '', label: '' }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.stats || []).map((s, idx) => (
                      <div key={idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`herostats-stat-${id}-${idx}`}>
                        <div className="flex gap-2 items-center">
                          <Input value={s.value || ''} onChange={(e) => { const stats = [...(content.stats || [])]; stats[idx] = { ...s, value: e.target.value }; updateBlock(id, { ...content, stats }); }} placeholder="15.2K" className="h-8 text-xs w-24" />
                          <Input value={s.label || ''} onChange={(e) => { const stats = [...(content.stats || [])]; stats[idx] = { ...s, label: e.target.value }; updateBlock(id, { ...content, stats }); }} placeholder="Active students" className="h-8 text-xs flex-1" />
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => updateBlock(id, { ...content, stats: (content.stats || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <details className="text-xs">
                          <summary className="cursor-pointer text-slate-500 hover:text-slate-700 py-1">Stat typography (value / label)</summary>
                          <div className="mt-2 space-y-2">
                            <div>
                              <Label className="text-[10px] text-slate-500 font-semibold">Value</Label>
                              <HeroItemTypographyRow
                                style={s.valueStyle || {}}
                                defaultSize={30}
                                testPrefix={`herostats-valstyle-${id}-${idx}`}
                                onPatch={(patch) => {
                                  const stats = [...(content.stats || [])];
                                  const merged = { ...(stats[idx].valueStyle || {}), ...patch };
                                  Object.keys(merged).forEach(k => { if (merged[k] === undefined || merged[k] === '') delete merged[k]; });
                                  stats[idx] = { ...s, valueStyle: merged };
                                  updateBlock(id, { ...content, stats });
                                }}
                              />
                            </div>
                            <div>
                              <Label className="text-[10px] text-slate-500 font-semibold">Label</Label>
                              <HeroItemTypographyRow
                                style={s.labelStyle || {}}
                                defaultSize={14}
                                testPrefix={`herostats-lblstyle-${id}-${idx}`}
                                onPatch={(patch) => {
                                  const stats = [...(content.stats || [])];
                                  const merged = { ...(stats[idx].labelStyle || {}), ...patch };
                                  Object.keys(merged).forEach(k => { if (merged[k] === undefined || merged[k] === '') delete merged[k]; });
                                  stats[idx] = { ...s, labelStyle: merged };
                                  updateBlock(id, { ...content, stats });
                                }}
                              />
                            </div>
                          </div>
                        </details>
                      </div>
                    ))}
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs">Images (up to 3)</Label>
                    {(content.images || []).map((im, idx) => (
                      <div key={im.id || idx} className="flex items-center gap-2" data-testid={`herostats-img-${id}-${idx}`}>
                        {im.imageUrl ? <img src={im.imageUrl} alt="" className="h-8 w-12 object-cover border rounded" /> : <div className="h-8 w-12 border border-dashed rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>}
                        <Button variant="outline" size="sm" data-testid={`herostats-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:heroimg:images:${idx}`)}>Image {idx + 1}</Button>
                        <Input value={im.imageUrl || ''} onChange={(e) => { const images = [...(content.images || [])]; images[idx] = { ...im, imageUrl: e.target.value }; updateBlock(id, { ...content, images }); }} placeholder="…or URL" className="h-8 text-xs flex-1" />
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'hero-split-cards' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`herocards-titlestyle-${id}`} />
                  <HeroLayoutControls id={id} content={content} updateBlock={updateBlock} prefix="herocards" />
                  <HeroTypographyControls id={id} content={content} updateBlock={updateBlock} prefix="herocards" />
                  <div className="space-y-1"><Label className="text-xs">Headline</Label><Input data-testid={`herocards-headline-${id}`} value={content.headline || ''} onChange={(e) => updateBlock(id, { ...content, headline: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Accent word (coloured)</Label><Input data-testid={`herocards-accent-${id}`} value={content.accentWord || ''} onChange={(e) => updateBlock(id, { ...content, accentWord: e.target.value })} placeholder="workflow?" /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Accent word colour</Label><ColorPickerSwatch value={content.accentColor || '#6366f1'} onChange={(e) => updateBlock(id, { ...content, accentColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herocards-accentcolor-${id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subtitle</Label><Textarea data-testid={`herocards-subtitle-${id}`} value={content.subtitle || ''} onChange={(e) => updateBlock(id, { ...content, subtitle: e.target.value })} rows={2} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Subtitle colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herocards-subcolor-${id}`} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Button text</Label><Input data-testid={`herocards-btn-text-${id}`} value={content.buttonText || ''} onChange={(e) => updateBlock(id, { ...content, buttonText: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs">Button link</Label><Input data-testid={`herocards-btn-url-${id}`} value={content.buttonUrl || ''} onChange={(e) => updateBlock(id, { ...content, buttonUrl: e.target.value })} placeholder="https://…" />
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mt-1">
                      <input type="checkbox" checked={content.buttonOpenInNewTab || false} onChange={(e) => updateBlock(id, { ...content, buttonOpenInNewTab: e.target.checked })} className="w-3.5 h-3.5 rounded accent-indigo-600" data-testid={`btn-newtab-${id}`} />
                      Open in new tab
                    </label></div>
                    <div className="col-span-2 flex items-center justify-between"><Label className="text-xs">Button text colour</Label><ColorPickerSwatch value={content.buttonTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, buttonTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`herocards-btntextcolor-${id}`} /></div>
                    <div className="col-span-2 pt-1"><ButtonEnhancements fields={content} testPrefix={`herocards-btn`} onPatch={(patch) => updateBlock(id, { ...content, ...patch })} /></div>
                  </div>
                  {['imageUrl1', 'imageUrl2'].map((fld, n) => (
                    <div key={fld} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`herocards-imgrow-${id}-${n}`}>
                      <Label className="text-xs">Image {n + 1}</Label>
                      <div className="flex items-center gap-2">
                        {content[fld] ? <img src={content[fld]} alt="" className="h-10 w-16 object-cover border rounded" /> : <div className="h-10 w-16 border border-dashed rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>}
                        <Button variant="outline" size="sm" data-testid={`herocards-pick-${id}-${n}`} onClick={() => openMediaPicker(`${id}:heroimg:${fld}`)}>Choose</Button>
                      </div>
                      <Input value={content[fld] || ''} onChange={(e) => updateBlock(id, { ...content, [fld]: e.target.value })} placeholder="…or paste image URL" className="h-8 text-xs" />
                    </div>
                  ))}
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'expandable-tabs' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`exptabs-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Heading</Label><Input data-testid={`exptabs-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subheading</Label><Textarea data-testid={`exptabs-subheading-${id}`} value={content.subheading || ''} onChange={(e) => updateBlock(id, { ...content, subheading: e.target.value })} rows={2} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Subtitle colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`exptabs-subcolor-${id}`} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Tabs colour</Label><ColorPickerSwatch value={content.tabColor || '#6366f1'} onChange={(e) => updateBlock(id, { ...content, tabColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`exptabs-tabcolor-${id}`} /></div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Tabs</Label>
                      <div className="flex gap-1">
                        <Button variant="outline" size="sm" data-testid={`exptabs-add-sep-${id}`} onClick={() => updateBlock(id, { ...content, tabs: [...(content.tabs || []), { type: 'separator' }] })}>| Sep</Button>
                        <Button variant="outline" size="sm" data-testid={`exptabs-add-${id}`} onClick={() => updateBlock(id, { ...content, tabs: [...(content.tabs || []), { id: `et-${Date.now()}`, label: '', icon: 'star', url: '' }] })}><Plus size={14} className="mr-1" /> Add</Button>
                      </div>
                    </div>
                    {(content.tabs || []).map((t, idx) => (
                      t.type === 'separator' ? (
                        <div key={idx} className="flex items-center gap-2 text-xs text-slate-400 border border-dashed border-slate-200 rounded-md p-2" data-testid={`exptabs-item-${id}-${idx}`}>
                          <span className="flex-1">— Separator —</span>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => updateBlock(id, { ...content, tabs: (content.tabs || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                      ) : (
                        <div key={t.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`exptabs-item-${id}-${idx}`}>
                          <div className="flex items-center gap-2">
                            <select value={t.icon || 'star'} onChange={(e) => { const tabs = [...(content.tabs || [])]; tabs[idx] = { ...t, icon: e.target.value }; updateBlock(id, { ...content, tabs }); }} className="h-8 text-xs border border-slate-200 rounded px-1" data-testid={`exptabs-icon-${id}-${idx}`}>
                              {['home','bell','settings','help','shield','mail','user','file','lock','star','heart','globe','phone','calendar','cart','zap'].map((ic) => <option key={ic} value={ic}>{ic}</option>)}
                            </select>
                            <Input value={t.label || ''} onChange={(e) => { const tabs = [...(content.tabs || [])]; tabs[idx] = { ...t, label: e.target.value }; updateBlock(id, { ...content, tabs }); }} placeholder="Label" className="h-8 text-xs" />
                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => updateBlock(id, { ...content, tabs: (content.tabs || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                          </div>
                          <Input value={t.url || ''} onChange={(e) => { const tabs = [...(content.tabs || [])]; tabs[idx] = { ...t, url: e.target.value }; updateBlock(id, { ...content, tabs }); }} placeholder="Link (optional)" className="h-8 text-xs" />
                        </div>
                      )
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'sparkles' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`sparkles-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Headline</Label><Input data-testid={`sparkles-headline-${id}`} value={content.headline || ''} onChange={(e) => updateBlock(id, { ...content, headline: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subheadline</Label><Textarea data-testid={`sparkles-subheadline-${id}`} value={content.subheadline || ''} onChange={(e) => updateBlock(id, { ...content, subheadline: e.target.value })} rows={2} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Button text</Label><Input data-testid={`sparkles-btn-text-${id}`} value={content.buttonText || ''} onChange={(e) => updateBlock(id, { ...content, buttonText: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs">Button link</Label><Input data-testid={`sparkles-btn-url-${id}`} value={content.buttonUrl || ''} onChange={(e) => updateBlock(id, { ...content, buttonUrl: e.target.value })} placeholder="https://…" />
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mt-1">
                      <input type="checkbox" checked={content.buttonOpenInNewTab || false} onChange={(e) => updateBlock(id, { ...content, buttonOpenInNewTab: e.target.checked })} className="w-3.5 h-3.5 rounded accent-indigo-600" data-testid={`btn-newtab-${id}`} />
                      Open in new tab
                    </label></div>
                    <div className="col-span-2 flex items-center justify-between"><Label className="text-xs">Button text colour</Label><ColorPickerSwatch value={content.buttonTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, buttonTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`sparkles-btntextcolor-${id}`} /></div>
                    <div className="col-span-2 pt-1"><ButtonEnhancements fields={content} testPrefix={`sparkles-btn`} onPatch={(patch) => updateBlock(id, { ...content, ...patch })} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between"><Label className="text-xs">Background</Label><ColorPickerSwatch value={content.backgroundColor || '#020617'} onChange={(e) => updateBlock(id, { ...content, backgroundColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`sparkles-bgcolor-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Particles</Label><ColorPickerSwatch value={content.particleColor || '#FFFFFF'} onChange={(e) => updateBlock(id, { ...content, particleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`sparkles-particlecolor-${id}`} /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Density ({content.density || 120})</Label><input type="range" min="20" max="400" step="10" value={content.density || 120} onChange={(e) => updateBlock(id, { ...content, density: parseInt(e.target.value) })} className="w-full" data-testid={`sparkles-density-${id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Height (px)</Label><Input type="number" data-testid={`sparkles-height-${id}`} value={content.height || 460} onChange={(e) => updateBlock(id, { ...content, height: parseInt(e.target.value) || 460 })} /></div>
                  <p className="text-xs text-slate-400">Tip: the gradient accent line uses Style → Border color.</p>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'gallery-carousel' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`gallerycar-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Title</Label><Input data-testid={`gallerycar-title-${id}`} value={content.title || ''} onChange={(e) => updateBlock(id, { ...content, title: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Description</Label><Textarea data-testid={`gallerycar-desc-${id}`} value={content.description || ''} onChange={(e) => updateBlock(id, { ...content, description: e.target.value })} rows={2} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Subtitle colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`gallerycar-subcolor-${id}`} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Card text colour</Label><ColorPickerSwatch value={content.cardTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, cardTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`gallerycar-cardtextcolor-${id}`} /></div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Cards</Label><Button variant="outline" size="sm" data-testid={`gallerycar-add-${id}`} onClick={() => updateBlock(id, { ...content, items: [...(content.items || []), { id: `gc-${Date.now()}`, title: '', description: '', href: '', imageUrl: '' }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.items || []).map((it, idx) => (
                      <div key={it.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`gallerycar-item-${id}-${idx}`}>
                        <div className="flex items-center gap-2">
                          {it.imageUrl ? <img src={it.imageUrl} alt="" className="h-8 w-12 object-cover border rounded" /> : <div className="h-8 w-12 border border-dashed rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>}
                          <Button variant="outline" size="sm" data-testid={`gallerycar-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:heroimg:items:${idx}`)}>Image</Button>
                          <Button variant="ghost" size="sm" className="text-red-500 ml-auto" onClick={() => updateBlock(id, { ...content, items: (content.items || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <Input value={it.imageUrl || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...it, imageUrl: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="…or paste image URL" className="h-8 text-xs" />
                        <Input value={it.title || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...it, title: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Card title" className="h-8 text-xs" />
                        <Textarea value={it.description || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...it, description: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Description" rows={2} className="text-xs" />
                        <Input value={it.href || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...it, href: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Link (optional)" className="h-8 text-xs" />
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'testimonial-deck' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`tdeck-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Heading</Label><Input data-testid={`tdeck-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subheading</Label><Textarea data-testid={`tdeck-subheading-${id}`} value={content.subheading || ''} onChange={(e) => updateBlock(id, { ...content, subheading: e.target.value })} rows={2} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Subtitle colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`tdeck-subcolor-${id}`} /></div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={content.autoplay !== false} onChange={(e) => updateBlock(id, { ...content, autoplay: e.target.checked })} data-testid={`tdeck-autoplay-${id}`} /> Auto-rotate</label>
                    <div className="flex items-center gap-1"><Label className="text-xs">every</Label><Input type="number" min="1" data-testid={`tdeck-autoplay-secs-${id}`} value={content.autoplaySeconds || 5} onChange={(e) => updateBlock(id, { ...content, autoplaySeconds: parseInt(e.target.value) || 5 })} className="h-7 w-16 text-xs" disabled={content.autoplay === false} /><Label className="text-xs">sec</Label></div>
                  </div>
                  <div className="flex items-center gap-2"><Label className="text-xs">Max characters per quote</Label><Input type="number" min="0" data-testid={`tdeck-maxchars-${id}`} value={content.maxChars || ''} onChange={(e) => updateBlock(id, { ...content, maxChars: parseInt(e.target.value) || 0 })} placeholder="0 = no limit" className="h-7 w-24 text-xs" /></div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Testimonials</Label><Button variant="outline" size="sm" data-testid={`tdeck-add-${id}`} onClick={() => updateBlock(id, { ...content, items: [...(content.items || []), { id: `td-${Date.now()}`, quote: '', name: '', role: '', avatarUrl: '', rating: 5 }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.items || []).map((t, idx) => (
                      <div key={t.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`tdeck-item-${id}-${idx}`}>
                        <Textarea value={t.quote || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, quote: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Quote" rows={2} className="text-xs" />
                        <div className="flex items-center gap-2">
                          {t.avatarUrl ? <img src={t.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover border" /> : <div className="h-8 w-8 rounded-full border border-dashed flex items-center justify-center"><ImageIcon size={12} className="text-slate-300" /></div>}
                          <Button variant="outline" size="sm" data-testid={`tdeck-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:tavatar:${idx}`)}>Avatar</Button>
                          <select value={t.rating || 5} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, rating: parseInt(e.target.value) }; updateBlock(id, { ...content, items }); }} className="h-8 text-xs border border-slate-200 rounded px-1 ml-auto" data-testid={`tdeck-rating-${id}-${idx}`}>{[5,4,3,2,1].map((r) => <option key={r} value={r}>{r}★</option>)}</select>
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => updateBlock(id, { ...content, items: (content.items || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={t.name || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, name: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Name" className="h-8 text-xs" />
                          <Input value={t.role || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, role: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Role / company" className="h-8 text-xs" />
                        </div>
                        <Input value={t.avatarUrl || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, avatarUrl: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="…or paste avatar URL" className="h-8 text-xs" />
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'liquid-glass' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`liquid-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Eyebrow</Label><Input data-testid={`liquid-eyebrow-${id}`} value={content.eyebrow || ''} onChange={(e) => updateBlock(id, { ...content, eyebrow: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Headline</Label><Input data-testid={`liquid-headline-${id}`} value={content.headline || ''} onChange={(e) => updateBlock(id, { ...content, headline: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subtitle</Label><Textarea data-testid={`liquid-subtitle-${id}`} value={content.subtitle || ''} onChange={(e) => updateBlock(id, { ...content, subtitle: e.target.value })} rows={2} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Button text</Label><Input data-testid={`liquid-btn-text-${id}`} value={content.buttonText || ''} onChange={(e) => updateBlock(id, { ...content, buttonText: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs">Button link</Label><Input data-testid={`liquid-btn-url-${id}`} value={content.buttonUrl || ''} onChange={(e) => updateBlock(id, { ...content, buttonUrl: e.target.value })} placeholder="https://…" />
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mt-1">
                      <input type="checkbox" checked={content.buttonOpenInNewTab || false} onChange={(e) => updateBlock(id, { ...content, buttonOpenInNewTab: e.target.checked })} className="w-3.5 h-3.5 rounded accent-indigo-600" data-testid={`btn-newtab-${id}`} />
                      Open in new tab
                    </label></div>
                    <div className="col-span-2 flex items-center justify-between"><Label className="text-xs">Button text colour</Label><ColorPickerSwatch value={content.buttonTextColor || '#0f172a'} onChange={(e) => updateBlock(id, { ...content, buttonTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`liquid-btntextcolor-${id}`} /></div>
                    <div className="col-span-2 pt-1"><ButtonEnhancements fields={content} testPrefix={`liquid-btn`} onPatch={(patch) => updateBlock(id, { ...content, ...patch })} /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Glass tint</Label>
                    <select value={content.glassTint || 'light'} onChange={(e) => updateBlock(id, { ...content, glassTint: e.target.value })} className="w-full h-9 text-sm border border-slate-200 rounded px-2" data-testid={`liquid-tint-${id}`}>
                      <option value="light">Light glass (dark text)</option>
                      <option value="dark">Dark glass (light text)</option>
                    </select>
                  </div>
                  <div className="space-y-2 border border-slate-200 rounded-md p-2">
                    <Label className="text-xs">Background image</Label>
                    <div className="flex items-center gap-2">
                      {content.backgroundImageUrl ? <img src={content.backgroundImageUrl} alt="" className="h-10 w-16 object-cover border rounded" /> : <div className="h-10 w-16 border border-dashed rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>}
                      <Button variant="outline" size="sm" data-testid={`liquid-pick-${id}`} onClick={() => openMediaPicker(`${id}:heroimg:backgroundImageUrl`)}>Choose</Button>
                      {content.backgroundImageUrl && <Button variant="ghost" size="sm" className="text-red-500" onClick={() => updateBlock(id, { ...content, backgroundImageUrl: '' })}><Trash2 size={14} /></Button>}
                    </div>
                    <Input value={content.backgroundImageUrl || ''} onChange={(e) => updateBlock(id, { ...content, backgroundImageUrl: e.target.value })} placeholder="…or paste image URL" className="h-8 text-xs" />
                    <div className="space-y-1"><Label className="text-xs">Background height: {content.height ? `${content.height}px` : 'auto'}</Label><input type="range" min="0" max="800" step="20" data-testid={`liquid-height-${id}`} value={content.height || 0} onChange={(e) => updateBlock(id, { ...content, height: parseInt(e.target.value) || 0 })} className="w-full" /><p className="text-xs text-slate-400">Slide to 0 for auto height</p></div>
                    <p className="text-xs text-slate-400">No image? A gradient (from Style → Border color) is used.</p>
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'testimonials-columns' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`tcols-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Badge</Label><Input data-testid={`tcols-badge-${id}`} value={content.badge || ''} onChange={(e) => updateBlock(id, { ...content, badge: e.target.value })} placeholder="Testimonials" /></div>
                  <div className="space-y-1"><Label className="text-xs">Heading</Label><Input data-testid={`tcols-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subheading</Label><Textarea data-testid={`tcols-subheading-${id}`} value={content.subheading || ''} onChange={(e) => updateBlock(id, { ...content, subheading: e.target.value })} rows={2} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Subheading colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`tcols-subcolor-${id}`} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Card text colour (all cards)</Label><ColorPickerSwatch value={content.cardTextColor || '#0f172a'} onChange={(e) => updateBlock(id, { ...content, cardTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`tcols-cardtextcolor-${id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Scroll duration ({content.speed || 40}s — lower = faster)</Label><input type="range" min="15" max="90" step="5" value={content.speed || 40} onChange={(e) => updateBlock(id, { ...content, speed: parseInt(e.target.value) })} className="w-full" data-testid={`tcols-speed-${id}`} /></div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Testimonials (spread across 3 columns)</Label><Button variant="outline" size="sm" data-testid={`tcols-add-${id}`} onClick={() => updateBlock(id, { ...content, items: [...(content.items || []), { id: `tc-${Date.now()}`, quote: '', name: '', role: '', avatarUrl: '' }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.items || []).map((t, idx) => (
                      <div key={t.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`tcols-item-${id}-${idx}`}>
                        <Textarea value={t.quote || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, quote: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Quote" rows={2} className="text-xs" />
                        <div className="flex items-center gap-2">
                          {t.avatarUrl ? <img src={t.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover border" /> : <div className="h-8 w-8 rounded-full border border-dashed flex items-center justify-center"><ImageIcon size={12} className="text-slate-300" /></div>}
                          <Button variant="outline" size="sm" data-testid={`tcols-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:tavatar:${idx}`)}>Avatar</Button>
                          <Button variant="ghost" size="sm" className="text-red-500 ml-auto" onClick={() => updateBlock(id, { ...content, items: (content.items || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={t.name || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, name: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Name" className="h-8 text-xs" />
                          <Input value={t.role || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, role: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Role / @handle" className="h-8 text-xs" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'typewriter' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`typewriter-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Prefix text (static)</Label><Input data-testid={`typewriter-prefix-${id}`} value={content.prefixText || ''} onChange={(e) => updateBlock(id, { ...content, prefixText: e.target.value })} placeholder="We're born to " /></div>
                  <div className="space-y-1"><Label className="text-xs">Phrases (one per line — typed & cycled)</Label><Textarea data-testid={`typewriter-phrases-${id}`} value={(content.phrases || []).join('\n')} onChange={(e) => updateBlock(id, { ...content, phrases: e.target.value.split('\n').map((s) => s).filter((s) => s.trim().length || s === '') })} rows={4} className="text-sm" /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Animated word colour</Label><ColorPickerSwatch value={content.wordColor || '#eab308'} onChange={(e) => updateBlock(id, { ...content, wordColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`typewriter-wordcolor-${id}`} /></div>
                  <div className="grid grid-cols-3 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Type speed (ms)</Label><Input type="number" data-testid={`typewriter-speed-${id}`} value={content.speed || 70} onChange={(e) => updateBlock(id, { ...content, speed: parseInt(e.target.value) || 70 })} className="h-8 text-xs" /></div>
                    <div className="space-y-1"><Label className="text-xs">Delete speed</Label><Input type="number" value={content.deleteSpeed || 40} onChange={(e) => updateBlock(id, { ...content, deleteSpeed: parseInt(e.target.value) || 40 })} className="h-8 text-xs" /></div>
                    <div className="space-y-1"><Label className="text-xs">Wait (ms)</Label><Input type="number" value={content.waitTime || 1500} onChange={(e) => updateBlock(id, { ...content, waitTime: parseInt(e.target.value) || 1500 })} className="h-8 text-xs" /></div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="space-y-1"><Label className="text-xs">Cursor char</Label><Input value={content.cursorChar || '_'} onChange={(e) => updateBlock(id, { ...content, cursorChar: e.target.value })} className="h-8 text-xs w-20" /></div>
                    <label className="flex items-center gap-2 text-xs mt-4"><input type="checkbox" checked={content.loop !== false} onChange={(e) => updateBlock(id, { ...content, loop: e.target.checked })} data-testid={`typewriter-loop-${id}`} /> Loop</label>
                  </div>
                  <p className="text-xs text-slate-400">Typed text colour = Style → Border color.</p>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'gooey-text' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`gooey-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Texts (one per line — morphs between them)</Label><Textarea data-testid={`gooey-texts-${id}`} value={(content.texts || []).join('\n')} onChange={(e) => updateBlock(id, { ...content, texts: e.target.value.split('\n') })} rows={4} className="text-sm" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Morph time (s)</Label><Input type="number" step="0.1" data-testid={`gooey-morphtime-${id}`} value={content.morphTime || 1} onChange={(e) => updateBlock(id, { ...content, morphTime: parseFloat(e.target.value) || 1 })} className="h-8 text-xs" /></div>
                    <div className="space-y-1"><Label className="text-xs">Cooldown (s)</Label><Input type="number" step="0.05" value={content.cooldownTime || 0.25} onChange={(e) => updateBlock(id, { ...content, cooldownTime: parseFloat(e.target.value) || 0.25 })} className="h-8 text-xs" /></div>
                  </div>
                  <p className="text-xs text-slate-400">Text colour = Style → Border color; dark Style → Background looks best.</p>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'text-cycle' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`textcycle-titlestyle-${id}`} />
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Prefix</Label><Input data-testid={`textcycle-prefix-${id}`} value={content.prefix || ''} onChange={(e) => updateBlock(id, { ...content, prefix: e.target.value })} placeholder="Your" /></div>
                    <div className="space-y-1"><Label className="text-xs">Suffix</Label><Input data-testid={`textcycle-suffix-${id}`} value={content.suffix || ''} onChange={(e) => updateBlock(id, { ...content, suffix: e.target.value })} placeholder="deserves better" /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Words (one per line — cycled in the middle)</Label><Textarea data-testid={`textcycle-words-${id}`} value={(content.words || []).join('\n')} onChange={(e) => updateBlock(id, { ...content, words: e.target.value.split('\n') })} rows={4} className="text-sm" /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Cycled word colour</Label><ColorPickerSwatch value={content.wordColor || '#0ea5e9'} onChange={(e) => updateBlock(id, { ...content, wordColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`textcycle-wordcolor-${id}`} /></div>
                  <div className="space-y-1"><Label className="text-xs">Interval (seconds)</Label><Input type="number" min="1" data-testid={`textcycle-interval-${id}`} value={Math.round((content.interval || 3000) / 1000)} onChange={(e) => updateBlock(id, { ...content, interval: (parseInt(e.target.value) || 3) * 1000 })} className="h-8 text-xs" /></div>
                  <p className="text-xs text-slate-400">Cycled word colour = Style → Border color.</p>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'circular-testimonials' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`circtest-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Heading (optional)</Label><Input data-testid={`circtest-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Role / title colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`circtest-subcolor-${id}`} /></div>
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={content.autoplay !== false} onChange={(e) => updateBlock(id, { ...content, autoplay: e.target.checked })} data-testid={`circtest-autoplay-${id}`} /> Auto-advance every 5s</label>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Testimonials</Label><Button variant="outline" size="sm" data-testid={`circtest-add-${id}`} onClick={() => updateBlock(id, { ...content, items: [...(content.items || []), { id: `ct-${Date.now()}`, quote: '', name: '', designation: '', imageUrl: '' }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.items || []).map((t, idx) => (
                      <div key={t.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`circtest-item-${id}-${idx}`}>
                        <div className="flex items-center gap-2">
                          {t.imageUrl ? <img src={t.imageUrl} alt="" className="h-10 w-10 rounded-lg object-cover border" /> : <div className="h-10 w-10 rounded-lg border border-dashed flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>}
                          <Button variant="outline" size="sm" data-testid={`circtest-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:heroimg:items:${idx}`)}>Photo</Button>
                          <Button variant="ghost" size="sm" className="text-red-500 ml-auto" onClick={() => updateBlock(id, { ...content, items: (content.items || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <Input value={t.imageUrl || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, imageUrl: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="…or paste photo URL" className="h-8 text-xs" />
                        <Textarea value={t.quote || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, quote: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Quote" rows={2} className="text-xs" />
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={t.name || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, name: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Name" className="h-8 text-xs" />
                          <Input value={t.designation || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, designation: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Role / title" className="h-8 text-xs" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'stagger-testimonials' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`stagger-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Heading</Label><Input data-testid={`stagger-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Name / role colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stagger-subcolor-${id}`} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Active card colour</Label><ColorPickerSwatch value={content.cardColor || '#e11d48'} onChange={(e) => updateBlock(id, { ...content, cardColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stagger-cardcolor-${id}`} /></div>
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={content.autoplay !== false} onChange={(e) => updateBlock(id, { ...content, autoplay: e.target.checked })} data-testid={`stagger-autoplay-${id}`} /> Auto-advance every 5s</label>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Testimonials</Label><Button variant="outline" size="sm" data-testid={`stagger-add-${id}`} onClick={() => updateBlock(id, { ...content, items: [...(content.items || []), { id: `st-${Date.now()}`, quote: '', name: '', role: '', avatarUrl: '' }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.items || []).map((t, idx) => (
                      <div key={t.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`stagger-item-${id}-${idx}`}>
                        <Textarea value={t.quote || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, quote: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Quote" rows={2} className="text-xs" />
                        <div className="flex items-center gap-2">
                          {t.avatarUrl ? <img src={t.avatarUrl} alt="" className="h-8 w-8 rounded-full object-cover border" /> : <div className="h-8 w-8 rounded-full border border-dashed flex items-center justify-center"><ImageIcon size={12} className="text-slate-300" /></div>}
                          <Button variant="outline" size="sm" data-testid={`stagger-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:tavatar:${idx}`)}>Avatar</Button>
                          <Button variant="ghost" size="sm" className="text-red-500 ml-auto" onClick={() => updateBlock(id, { ...content, items: (content.items || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={t.name || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, name: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Name" className="h-8 text-xs" />
                          <Input value={t.role || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...t, role: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="Role / company" className="h-8 text-xs" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'steps-icons' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`stepsicons-titlestyle-${id}`} />
                  <HeroLayoutControls id={id} content={content} updateBlock={updateBlock} prefix="stepsicons" includePosition={false} />
                  <div className="flex items-center justify-between gap-2"><Label className="text-xs">Layout</Label><Select value={content.layout || 'horizontal'} onValueChange={(v) => updateBlock(id, { ...content, layout: v })}><SelectTrigger className="w-40 h-8 text-xs" data-testid={`stepsicons-layout-${id}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="horizontal">Horizontal (row)</SelectItem><SelectItem value="vertical">Vertical timeline</SelectItem></SelectContent></Select></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Badge text</Label><Input data-testid={`stepsicons-badge-${id}`} value={content.badge || ''} onChange={(e) => updateBlock(id, { ...content, badge: e.target.value })} placeholder="Brand new" /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Badge colour</Label><ColorPickerSwatch value={content.badgeColor || '#10b981'} onChange={(e) => updateBlock(id, { ...content, badgeColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepsicons-badgecolor-${id}`} /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Heading</Label><Input data-testid={`stepsicons-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subheading</Label><Textarea value={content.subheading || ''} onChange={(e) => updateBlock(id, { ...content, subheading: e.target.value })} rows={2} className="text-xs" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between"><Label className="text-xs">Accent colour</Label><ColorPickerSwatch value={content.accentColor || '#7c3aed'} onChange={(e) => updateBlock(id, { ...content, accentColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepsicons-accent-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Subheading colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepsicons-subcolor-${id}`} /></div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Steps</Label><Button variant="outline" size="sm" data-testid={`stepsicons-add-${id}`} onClick={() => updateBlock(id, { ...content, steps: [...(content.steps || []), { id: `si-${Date.now()}`, icon: 'zap', title: '', description: '', linkText: '', linkUrl: '' }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.steps || []).map((s, idx) => (
                      <div key={s.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`stepsicons-item-${id}-${idx}`}>
                        <div className="flex items-center gap-2">
                          <Select value={s.icon || 'zap'} onValueChange={(v) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, icon: v }; updateBlock(id, { ...content, steps }); }}>
                            <SelectTrigger className="h-8 text-xs w-28"><SelectValue /></SelectTrigger>
                            <SelectContent>{['zap','star','heart','shield','globe','settings','bell','calendar','mail','user','lock','cart','phone','file','home','help'].map((ic) => <SelectItem key={ic} value={ic}>{ic}</SelectItem>)}</SelectContent>
                          </Select>
                          <Input value={s.title || ''} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, title: e.target.value }; updateBlock(id, { ...content, steps }); }} placeholder="Step title" className="h-8 text-xs flex-1" />
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => updateBlock(id, { ...content, steps: (content.steps || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <Textarea value={s.description || ''} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, description: e.target.value }; updateBlock(id, { ...content, steps }); }} placeholder="Description" rows={2} className="text-xs" />
                        <div className="grid grid-cols-2 gap-2">
                          <Input value={s.linkText || ''} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, linkText: e.target.value }; updateBlock(id, { ...content, steps }); }} placeholder="Link text" className="h-8 text-xs" />
                          <Input value={s.linkUrl || ''} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, linkUrl: e.target.value }; updateBlock(id, { ...content, steps }); }} placeholder="Link URL" className="h-8 text-xs" />
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'steps-cards' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`stepscards-titlestyle-${id}`} />
                  <HeroLayoutControls id={id} content={content} updateBlock={updateBlock} prefix="stepscards" includePosition={false} />
                  <div className="flex items-center justify-between gap-2"><Label className="text-xs">Layout</Label><Select value={content.layout || 'horizontal'} onValueChange={(v) => updateBlock(id, { ...content, layout: v })}><SelectTrigger className="w-40 h-8 text-xs" data-testid={`stepscards-layout-${id}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="horizontal">Horizontal (row)</SelectItem><SelectItem value="vertical">Vertical timeline</SelectItem></SelectContent></Select></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Badge text</Label><Input data-testid={`stepscards-badge-${id}`} value={content.badge || ''} onChange={(e) => updateBlock(id, { ...content, badge: e.target.value })} placeholder="Brand new" /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Badge colour</Label><ColorPickerSwatch value={content.badgeColor || '#10b981'} onChange={(e) => updateBlock(id, { ...content, badgeColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepscards-badgecolor-${id}`} /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Heading</Label><Input data-testid={`stepscards-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subheading</Label><Textarea value={content.subheading || ''} onChange={(e) => updateBlock(id, { ...content, subheading: e.target.value })} rows={2} className="text-xs" /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between"><Label className="text-xs">Accent / number colour</Label><ColorPickerSwatch value={content.accentColor || '#7c3aed'} onChange={(e) => updateBlock(id, { ...content, accentColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepscards-accent-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Subheading colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepscards-subcolor-${id}`} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Button text</Label><Input data-testid={`stepscards-btn-text-${id}`} value={content.buttonText || ''} onChange={(e) => updateBlock(id, { ...content, buttonText: e.target.value })} placeholder="Learn more" /></div>
                    <div className="space-y-1"><Label className="text-xs">Button link</Label><Input data-testid={`stepscards-btn-url-${id}`} value={content.buttonUrl || ''} onChange={(e) => updateBlock(id, { ...content, buttonUrl: e.target.value })} placeholder="https://…" />
                    <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer mt-1">
                      <input type="checkbox" checked={content.buttonOpenInNewTab || false} onChange={(e) => updateBlock(id, { ...content, buttonOpenInNewTab: e.target.checked })} className="w-3.5 h-3.5 rounded accent-indigo-600" data-testid={`btn-newtab-${id}`} />
                      Open in new tab
                    </label></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Button colour</Label><ColorPickerSwatch value={content.buttonColor || '#7c3aed'} onChange={(e) => updateBlock(id, { ...content, buttonColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepscards-btncolor-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Button text colour</Label><ColorPickerSwatch value={content.buttonTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, buttonTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepscards-btntextcolor-${id}`} /></div>
                    <div className="col-span-2 pt-1"><ButtonEnhancements fields={content} testPrefix={`stepscards-btn`} onPatch={(patch) => updateBlock(id, { ...content, ...patch })} /></div>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Steps</Label><Button variant="outline" size="sm" data-testid={`stepscards-add-${id}`} onClick={() => updateBlock(id, { ...content, steps: [...(content.steps || []), { id: `sc-${Date.now()}`, title: '', description: '' }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.steps || []).map((s, idx) => (
                      <div key={s.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`stepscards-item-${id}-${idx}`}>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-bold text-slate-400 w-5 shrink-0">{idx + 1}.</span>
                          <Input value={s.title || ''} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, title: e.target.value }; updateBlock(id, { ...content, steps }); }} placeholder="Card title" className="h-8 text-xs flex-1" />
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => updateBlock(id, { ...content, steps: (content.steps || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <Textarea value={s.description || ''} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, description: e.target.value }; updateBlock(id, { ...content, steps }); }} placeholder="Description" rows={2} className="text-xs" />
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'steps-grid-image' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`stepsgrid-titlestyle-${id}`} />
                  <HeroLayoutControls id={id} content={content} updateBlock={updateBlock} prefix="stepsgrid" includePosition={false} />
                  <div className="space-y-1"><Label className="text-xs">Heading (optional)</Label><Input data-testid={`stepsgrid-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between"><Label className="text-xs">Accent colour</Label><ColorPickerSwatch value={content.accentColor || '#7c3aed'} onChange={(e) => updateBlock(id, { ...content, accentColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepsgrid-accent-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Text colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepsgrid-subcolor-${id}`} /></div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-slate-50 border border-slate-200">
                    {content.imageUrl ? <img src={content.imageUrl} alt="" className="h-12 w-12 object-cover rounded border" /> : <div className="h-12 w-12 border border-dashed rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>}
                    <Button variant="outline" size="sm" data-testid={`stepsgrid-pick-${id}`} onClick={() => openMediaPicker(`${id}:heroimg:imageUrl`)}>Choose from Library</Button>
                    <Select value={content.imagePosition || 'right'} onValueChange={(v) => updateBlock(id, { ...content, imagePosition: v })}><SelectTrigger className="w-24 h-8 text-xs ml-auto" data-testid={`stepsgrid-imgpos-${id}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent></Select>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Steps</Label><Button variant="outline" size="sm" data-testid={`stepsgrid-add-${id}`} onClick={() => updateBlock(id, { ...content, steps: [...(content.steps || []), { id: `sg-${Date.now()}`, title: '', description: '', badgeColor: '#7c3aed', highlight: false }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.steps || []).map((s, idx) => (
                      <div key={s.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`stepsgrid-item-${id}-${idx}`}>
                        <div className="flex items-center gap-2">
                          <Input value={s.title || ''} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, title: e.target.value }; updateBlock(id, { ...content, steps }); }} placeholder="Step title" className="h-8 text-xs flex-1" />
                          <ColorPickerSwatch value={s.badgeColor || '#7c3aed'} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, badgeColor: e.target.value }; updateBlock(id, { ...content, steps }); }} className="h-8 w-9 rounded border" title="Badge colour" />
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => updateBlock(id, { ...content, steps: (content.steps || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <Textarea value={s.description || ''} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, description: e.target.value }; updateBlock(id, { ...content, steps }); }} placeholder="Description" rows={2} className="text-xs" />
                        <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={!!s.highlight} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, highlight: e.target.checked }; updateBlock(id, { ...content, steps }); }} /> Highlight this step</label>
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'steps-timeline-image' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`stepstl-titlestyle-${id}`} />
                  <HeroLayoutControls id={id} content={content} updateBlock={updateBlock} prefix="stepstl" includePosition={false} />
                  <div className="space-y-1"><Label className="text-xs">Heading (optional)</Label><Input data-testid={`stepstl-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} /></div>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex items-center justify-between"><Label className="text-xs">Accent colour</Label><ColorPickerSwatch value={content.accentColor || '#7c3aed'} onChange={(e) => updateBlock(id, { ...content, accentColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepstl-accent-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Text colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`stepstl-subcolor-${id}`} /></div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-md bg-slate-50 border border-slate-200">
                    {content.imageUrl ? <img src={content.imageUrl} alt="" className="h-12 w-12 object-cover rounded border" /> : <div className="h-12 w-12 border border-dashed rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>}
                    <Button variant="outline" size="sm" data-testid={`stepstl-pick-${id}`} onClick={() => openMediaPicker(`${id}:heroimg:imageUrl`)}>Choose from Library</Button>
                    <Select value={content.imagePosition || 'right'} onValueChange={(v) => updateBlock(id, { ...content, imagePosition: v })}><SelectTrigger className="w-24 h-8 text-xs ml-auto" data-testid={`stepstl-imgpos-${id}`}><SelectValue /></SelectTrigger><SelectContent><SelectItem value="left">Left</SelectItem><SelectItem value="right">Right</SelectItem></SelectContent></Select>
                  </div>
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={content.showSuccess !== false} onChange={(e) => updateBlock(id, { ...content, showSuccess: e.target.checked })} data-testid={`stepstl-success-${id}`} /> Show final "Success" node</label>
                  {content.showSuccess !== false && <div className="space-y-1"><Label className="text-xs">Success label</Label><Input data-testid={`stepstl-successlabel-${id}`} value={content.successLabel || ''} onChange={(e) => updateBlock(id, { ...content, successLabel: e.target.value })} placeholder="Success" className="h-8 text-xs" /></div>}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Steps</Label><Button variant="outline" size="sm" data-testid={`stepstl-add-${id}`} onClick={() => updateBlock(id, { ...content, steps: [...(content.steps || []), { id: `stl-${Date.now()}`, title: '', description: '' }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.steps || []).map((s, idx) => (
                      <div key={s.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`stepstl-item-${id}-${idx}`}>
                        <div className="flex items-center gap-2">
                          <Input value={s.title || ''} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, title: e.target.value }; updateBlock(id, { ...content, steps }); }} placeholder="Step title" className="h-8 text-xs flex-1" />
                          <Button variant="ghost" size="sm" className="text-red-500" onClick={() => updateBlock(id, { ...content, steps: (content.steps || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <Textarea value={s.description || ''} onChange={(e) => { const steps = [...(content.steps || [])]; steps[idx] = { ...s, description: e.target.value }; updateBlock(id, { ...content, steps }); }} placeholder="Description" rows={2} className="text-xs" />
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}



        {type === 'handwriting-text' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`handwriting-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Title (drawn)</Label><Input data-testid={`handwriting-title-${id}`} value={content.title || ''} onChange={(e) => updateBlock(id, { ...content, title: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subtitle</Label><Textarea data-testid={`handwriting-subtitle-${id}`} value={content.subtitle || ''} onChange={(e) => updateBlock(id, { ...content, subtitle: e.target.value })} rows={2} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Subtitle colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`handwriting-subcolor-${id}`} /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Hand-drawn circle colour</Label><ColorPickerSwatch value={content.circleColor || '#9333ea'} onChange={(e) => updateBlock(id, { ...content, circleColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`handwriting-circlecolor-${id}`} /></div>
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={content.loop === true} onChange={(e) => updateBlock(id, { ...content, loop: e.target.checked })} data-testid={`handwriting-loop-${id}`} /> Loop the drawing animation</label>
                  <p className="text-xs text-slate-400">Hand-drawn stroke colour = Style → Border color.</p>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'typewriter-smooth' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`twsmooth-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Prefix line (above)</Label><Input data-testid={`twsmooth-prefix-${id}`} value={content.prefix || ''} onChange={(e) => updateBlock(id, { ...content, prefix: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Words (one per line — revealed; last word is accented)</Label><Textarea data-testid={`twsmooth-words-${id}`} value={(content.words || []).join('\n')} onChange={(e) => updateBlock(id, { ...content, words: e.target.value.split('\n') })} rows={4} className="text-sm" /></div>
                  <div className="flex items-center justify-between"><Label className="text-xs">Accent word / cursor colour</Label><ColorPickerSwatch value={content.wordColor || '#2563eb'} onChange={(e) => updateBlock(id, { ...content, wordColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`twsmooth-wordcolor-${id}`} /></div>
                  <label className="flex items-center gap-2 text-xs"><input type="checkbox" checked={content.loop === true} onChange={(e) => updateBlock(id, { ...content, loop: e.target.checked })} data-testid={`twsmooth-loop-${id}`} /> Loop the reveal (with pause)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1"><Label className="text-xs">Button 1 text</Label><Input data-testid={`twsmooth-btn1-text-${id}`} value={content.button1Text || ''} onChange={(e) => updateBlock(id, { ...content, button1Text: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs">Button 1 link</Label><Input value={content.button1Url || ''} onChange={(e) => updateBlock(id, { ...content, button1Url: e.target.value })} placeholder="https://…" /></div>
                    <div className="space-y-1"><Label className="text-xs">Button 2 text</Label><Input data-testid={`twsmooth-btn2-text-${id}`} value={content.button2Text || ''} onChange={(e) => updateBlock(id, { ...content, button2Text: e.target.value })} /></div>
                    <div className="space-y-1"><Label className="text-xs">Button 2 link</Label><Input value={content.button2Url || ''} onChange={(e) => updateBlock(id, { ...content, button2Url: e.target.value })} placeholder="https://…" /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Button 1 text colour</Label><ColorPickerSwatch value={content.buttonTextColor || '#ffffff'} onChange={(e) => updateBlock(id, { ...content, buttonTextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`twsmooth-btn1-textcolor-${id}`} /></div>
                    <div className="flex items-center justify-between"><Label className="text-xs">Button 2 text colour</Label><ColorPickerSwatch value={content.button2TextColor || '#0f172a'} onChange={(e) => updateBlock(id, { ...content, button2TextColor: e.target.value })} className="h-7 w-12 rounded border" data-testid={`twsmooth-btn2-textcolor-${id}`} /></div>
                  </div>
                  <p className="text-xs text-slate-400">Accent (last word + cursor) = Style → Border color.</p>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}

        {type === 'folder-gallery' && (
          <>
            <BlockTabs activeTab={getActiveTab(id)} onTabChange={(tab) => setActiveTab(id, tab)} onMoveUp={onMoveUp} onMoveDown={onMoveDown} onDuplicate={onDuplicate} onDelete={onDelete} onTogglePublished={onTogglePublished} isFirst={isFirst} isLast={isLast} isPublished={isPublished} />
            <BlockSplitPane
              showRightPane={getActiveTab(id) === 'style' || getActiveTab(id) === 'animation'}
              contentPane={
                <div className="space-y-3">
                  <TitleStyleControls content={content} onChange={(nc) => updateBlock(id, nc)} styles={styles} onStylesChange={(ns) => updateBlock(id, content, ns)} testIdPrefix={`folder-titlestyle-${id}`} />
                  <div className="space-y-1"><Label className="text-xs">Heading</Label><Input data-testid={`folder-heading-${id}`} value={content.heading || ''} onChange={(e) => updateBlock(id, { ...content, heading: e.target.value })} /></div>
                  <div className="space-y-1"><Label className="text-xs">Subheading</Label><Textarea data-testid={`folder-subheading-${id}`} value={content.subheading || ''} onChange={(e) => updateBlock(id, { ...content, subheading: e.target.value })} rows={2} /></div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1"><Label className="text-xs">Body text colour</Label><ColorPickerSwatch value={content.subtitleColor || '#475569'} onChange={(e) => updateBlock(id, { ...content, subtitleColor: e.target.value })} className="h-8 w-full rounded border cursor-pointer" data-testid={`folder-subcolor-${id}`} /></div>
                    <div className="space-y-1"><Label className="text-xs">Folder (envelope) colour</Label><ColorPickerSwatch value={content.folderColor || '#d97706'} onChange={(e) => updateBlock(id, { ...content, folderColor: e.target.value })} className="h-8 w-full rounded border cursor-pointer" data-testid={`folder-color-${id}`} /></div>
                  </div>
                  <div className="space-y-1"><Label className="text-xs">Folder label</Label><Input data-testid={`folder-label-${id}`} value={content.folderLabel || ''} onChange={(e) => updateBlock(id, { ...content, folderLabel: e.target.value })} placeholder="Photos" /></div>
                  <div className="space-y-2">
                    <div className="flex justify-between items-center"><Label className="text-xs">Photos (up to 6)</Label><Button variant="outline" size="sm" data-testid={`folder-add-${id}`} onClick={() => updateBlock(id, { ...content, items: [...(content.items || []), { id: `fg-${Date.now()}`, imageUrl: '' }] })}><Plus size={14} className="mr-1" /> Add</Button></div>
                    {(content.items || []).map((it, idx) => (
                      <div key={it.id || idx} className="border border-slate-200 rounded-md p-2 space-y-2" data-testid={`folder-item-${id}-${idx}`}>
                        <div className="flex items-center gap-2">
                          {it.imageUrl ? <img src={it.imageUrl} alt="" className="h-8 w-12 object-cover border rounded" /> : <div className="h-8 w-12 border border-dashed rounded flex items-center justify-center"><ImageIcon size={14} className="text-slate-300" /></div>}
                          <Button variant="outline" size="sm" data-testid={`folder-pick-${id}-${idx}`} onClick={() => openMediaPicker(`${id}:heroimg:items:${idx}`)}>Photo</Button>
                          <Button variant="ghost" size="sm" className="text-red-500 ml-auto" onClick={() => updateBlock(id, { ...content, items: (content.items || []).filter((_, i) => i !== idx) })}><Trash2 size={14} /></Button>
                        </div>
                        <Input value={it.imageUrl || ''} onChange={(e) => { const items = [...(content.items || [])]; items[idx] = { ...it, imageUrl: e.target.value }; updateBlock(id, { ...content, items }); }} placeholder="…or paste image URL" className="h-8 text-xs" />
                      </div>
                    ))}
                  </div>
                </div>
              }
              settingsPane={<><BlockTabContent isActive={getActiveTab(id) === 'style'}><BlockStyleControls styles={styles || {}} onChange={(ns) => updateBlock(id, content, ns)} content={content} onContentChange={(nc) => updateBlock(id, nc, styles)} /></BlockTabContent><BlockTabContent isActive={getActiveTab(id) === 'animation'}><AnimationControls animation={content.animation || {}} onChange={(na) => updateBlock(id, { ...content, animation: na })} /></BlockTabContent></>}
            />
          </>
        )}




      </>
    );
  };

  const blockTypes = [
    { type: 'heading', icon: Type, label: 'Heading' },
    { type: 'paragraph', icon: Type, label: 'Paragraph' },
    { type: 'image', icon: ImageIcon, label: 'Image' },
    { type: 'video', icon: Film, label: 'Video Embed', category: 'Media' },
    { type: 'button', icon: MousePointerClick, label: 'Button' },
    { type: 'hero', icon: Film, label: 'Hero Banner', category: 'Hero' },
    { type: 'hero-spotlight', icon: Sparkles, label: 'Animated Hero (Spotlight)', category: 'Hero' },
    { type: 'hero-tabs', icon: Layout, label: 'Tabbed Showcase', category: 'Featured' },
    { type: 'hero-feature-cards', icon: Grid3X3, label: 'Hero — Feature Cards', category: 'Hero' },
    { type: 'hero-split-stats', icon: LayoutPanelLeft, label: 'Hero — Split + Stats', category: 'Hero' },
    { type: 'hero-split-cards', icon: Images, label: 'Hero — Split + Image Cards', category: 'Hero' },
    { type: 'logo-cloud', icon: Images, label: 'Logo Cloud / Clients', category: 'Interactive' },
    { type: 'countdown', icon: Clock, label: 'Countdown Timer', category: 'Interactive' },
    { type: 'before-after', icon: SlidersHorizontal, label: 'Before / After Slider', category: 'Interactive' },
    { type: 'expandable-tabs', icon: LayoutPanelTop, label: 'Expandable Tabs', category: 'Interactive' },
    { type: 'sparkles', icon: Sparkles, label: 'Sparkles Hero', category: 'Interactive' },
    { type: 'gallery-carousel', icon: Images, label: 'Gallery Carousel', category: 'Interactive' },
    { type: 'folder-gallery', icon: FolderOpen, label: 'Interactive Folder Gallery', category: 'Interactive' },
    { type: 'steps-icons', icon: Workflow, label: 'Steps — Icons & Arrows', category: 'Interactive' },
    { type: 'steps-cards', icon: Footprints, label: 'Steps — Numbered Cards', category: 'Interactive' },
    { type: 'steps-grid-image', icon: Grid3X3, label: 'Steps — Grid + Image', category: 'Interactive' },
    { type: 'steps-timeline-image', icon: Workflow, label: 'Steps — Timeline + Image', category: 'Interactive' },
    { type: 'liquid-glass', icon: Sparkles, label: 'Liquid Glass Card', category: 'Interactive' },
    { type: 'testimonial-deck', icon: MessageSquareQuote, label: 'Testimonial Cards (Deck)', category: 'Testimonials' },
    { type: 'testimonials-columns', icon: MessageSquareQuote, label: 'Testimonials Columns (Scrolling)', category: 'Testimonials' },
    { type: 'circular-testimonials', icon: MessageSquareQuote, label: 'Block Testimonials', category: 'Testimonials' },
    { type: 'stagger-testimonials', icon: MessageSquareQuote, label: 'Stagger Testimonials', category: 'Testimonials' },
    { type: 'typewriter', icon: Type, label: 'Typewriter Text', category: 'Text Effects' },
    { type: 'typewriter-smooth', icon: Type, label: 'Typewriter (Smooth Reveal)', category: 'Text Effects' },
    { type: 'gooey-text', icon: Sparkles, label: 'Gooey Text Morph', category: 'Text Effects' },
    { type: 'text-cycle', icon: Type, label: 'Animated Text Cycle', category: 'Text Effects' },
    { type: 'handwriting-text', icon: Type, label: 'Hand Writing Text', category: 'Text Effects' },
    { type: 'gallery', icon: Images, label: 'Image Gallery', category: 'Media' },
    { type: 'form', icon: ClipboardList, label: 'Embed Form', category: 'Featured' },
    { type: 'ticker', icon: MonitorPlay, label: 'Ticker Tape / Logo Carousel', category: 'Featured' },
    { type: 'article-feed', icon: ClipboardList, label: 'Article Feed / Blog', category: 'Featured' },
    { type: 'events-calendar', icon: Clock, label: 'Events Calendar', category: 'Featured' },
    { type: 'search', icon: Search, label: 'Site Search', category: 'Featured' },
    { type: 'pricing', icon: DollarSign, label: 'Pricing Table', category: 'Featured' },
    { type: 'feature-comparison', icon: Grid3X3, label: 'Feature Comparison', category: 'Featured' },
    { type: 'plan-comparison', icon: Columns, label: 'Plan Comparison Table', category: 'Featured' },
    { type: 'testimonials', icon: MessageSquareQuote, label: 'Testimonials', category: 'Featured' },
    { type: 'faq', icon: HelpCircle, label: 'FAQ', category: 'Featured' },
    { type: 'cta', icon: Layout, label: 'Call to Action', category: 'Featured' },
    { type: 'team', icon: Users, label: 'Team Members', category: 'Featured' },
    { type: 'stats', icon: Star, label: 'Statistics', category: 'Featured' },
    { type: 'quote', icon: Quote, label: 'Quote' },
    { type: 'list', icon: List, label: 'List' },
    { type: 'code', icon: Code, label: 'Code' },
    { type: 'newsletter-header', icon: LayoutPanelTop, label: 'Newsletter Header', category: 'Newsletter' },
    { type: 'article-card', icon: FileText, label: 'Insert Article', category: 'Newsletter' },
    { type: 'unsubscribe', icon: MailX, label: 'Unsubscribe Link', category: 'Newsletter' },
    { type: 'custom-html', icon: FileCode, label: 'Custom HTML', category: 'Advanced' },
    { type: 'columns', icon: Grid3X3, label: 'Columns (2-5)', category: 'Layout' },
    { type: 'card-grid', icon: Grid3X3, label: 'Card Grid (ordered, unlimited)', category: 'Layout' },
    { type: 'image-text-row', icon: LayoutPanelLeft, label: 'Image + Text (Row)', category: 'Layout' },
    { type: 'image-text-stack', icon: LayoutPanelLeft, label: 'Image + Text (Stack)', category: 'Layout' },
  ];

  const basicBlocks = blockTypes.filter(b => !b.category);
  const mediaBlocks = blockTypes.filter(b => b.category === 'Media');
  const heroBlocks = blockTypes.filter(b => b.category === 'Hero');
  const featuredBlocks = blockTypes.filter(b => b.category === 'Featured');
  const textEffectBlocks = blockTypes.filter(b => b.category === 'Text Effects');
  const testimonialBlocks = blockTypes.filter(b => b.category === 'Testimonials');
  const interactiveBlocks = blockTypes.filter(b => b.category === 'Interactive');
  const layoutBlocks = blockTypes.filter(b => b.category === 'Layout');
  const advancedBlocks = blockTypes.filter(b => b.category === 'Advanced');
  const newsletterBlocks = blockTypes.filter(b => b.category === 'Newsletter');

  return (
    <div className="py-8 px-4" data-testid="block-editor">
      {/* Block Controls Header */}
      {blocks.length > 1 && (
        <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200">
          <span className="text-sm text-slate-500">{blocks.length} blocks</span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={collapseAllBlocks}
              className="text-xs h-7 px-2"
              title="Collapse all blocks"
            >
              <ChevronUp size={12} className="mr-1" />
              Collapse All
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={expandAllBlocks}
              className="text-xs h-7 px-2"
              title="Expand all blocks"
            >
              <ChevronDown size={12} className="mr-1" />
              Expand All
            </Button>
          </div>
        </div>
      )}
      
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={blocks.map(b => b.id)} strategy={verticalListSortingStrategy}>
          {blocks.map((block, index) => (
            <SortableBlock
              key={block.id}
              block={block}
              index={index}
              onDuplicate={duplicateBlock}
              onDelete={deleteBlock}
              onStyleClick={(blockId) => setActiveStyleBlockId(activeStyleBlockId === blockId ? null : blockId)}
              onMoveUp={() => moveBlockUp(index)}
              onMoveDown={() => moveBlockDown(index)}
              isFirst={index === 0}
              isLast={index === blocks.length - 1}
              isCollapsed={collapsedBlocks.has(block.id)}
              onToggleCollapse={() => toggleBlockCollapse(block.id)}
              onAnchorChange={handleAnchorChange}
              isDuplicateAnchor={isBlockAnchorDuplicate(block)}
              allAnchorIds={getAllAnchorIds()}
              onApplyPreset={applyBlockPreset}
              onApplyColumnPreset={applyColumnPreset}
              onTogglePublished={toggleBlockPublished}
              onSaveBlock={handleSaveBlockClick}
            >
              {renderBlockContent(block, index, {
                onMoveUp: () => moveBlockUp(index),
                onMoveDown: () => moveBlockDown(index),
                onDuplicate: () => duplicateBlock(block),
                onDelete: () => deleteBlock(block.id),
                onTogglePublished: () => toggleBlockPublished(block.id),
                isFirst: index === 0,
                isLast: index === blocks.length - 1,
                isPublished: block.content?.isPublished !== false,
              })}
            </SortableBlock>
          ))}
        </SortableContext>
      </DndContext>

      {/* Slash Command Input */}
      <div className="relative mt-4 mb-2">
        <div className="relative">
          <Command size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            ref={slashInputRef}
            type="text"
            placeholder='Type "/" to add a block quickly...'
            onChange={handleSlashCommand}
            onKeyDown={handleSlashKeyDown}
            className="w-full pl-10 pr-4 py-3 border border-slate-200 rounded-lg text-sm placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
            data-testid="slash-command-input"
          />
        </div>
        
        <SlashCommandMenu
          isOpen={showSlashMenu}
          onClose={() => {
            setShowSlashMenu(false);
            setSlashQuery('');
            if (slashInputRef.current) {
              slashInputRef.current.value = '';
            }
          }}
          onSelect={handleSlashSelect}
          position={slashMenuPosition}
          searchQuery={slashQuery}
        />
      </div>

      <div className="relative">
        <Button
          onClick={() => { setShowAddMenu(!showAddMenu); if (!showAddMenu) fetchSavedBlocks(); }}
          variant="outline"
          className="w-full justify-center border-dashed hover:border-indigo-400 hover:text-indigo-600"
          data-testid="add-block-button"
        >
          <Plus size={20} className="mr-2" />
          Add Block
        </Button>

        {showAddMenu && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/20" onClick={() => setShowAddMenu(false)}>
            <div 
              className="bg-white border border-slate-200 rounded-lg shadow-2xl p-4 w-[500px] max-h-[70vh] overflow-auto" 
              data-testid="block-type-menu"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-800">Add Block</h3>
                <div className="flex items-center gap-3">
                  <a
                    href="https://eu.nexuscms.ai/site/nexusone/page/home"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 bg-indigo-50 hover:bg-indigo-100 px-2.5 py-1.5 rounded-md transition-colors"
                    data-testid="block-examples-link"
                    title="See live examples of every block, with the settings used"
                  >
                    <ExternalLink size={13} />
                    Block Examples
                  </a>
                  <button 
                    onClick={() => setShowAddMenu(false)}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    ✕
                  </button>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-2">
                {/* My Saved Blocks */}
                <div className="col-span-2 text-xs font-semibold text-indigo-500 px-2 py-1 flex items-center gap-1">
                  <Bookmark size={12} />
                  My Saved Blocks
                </div>
                {loadingSavedBlocks ? (
                  <div className="col-span-2 px-2 py-2 text-xs text-slate-400">Loading…</div>
                ) : savedBlocks.length === 0 ? (
                  <div className="col-span-2 px-2 py-2 text-xs text-slate-400">
                    No saved blocks yet. Click the bookmark icon on any block to save it for reuse.
                  </div>
                ) : (
                  savedBlocks.map((tpl) => (
                    <div
                      key={tpl.template_id}
                      className="col-span-2 group flex items-center justify-between gap-2 px-3 py-2 hover:bg-indigo-50 rounded-lg transition-colors text-sm cursor-pointer"
                      onClick={() => insertSavedBlock(tpl)}
                      data-testid={`saved-block-${tpl.template_id}`}
                    >
                      <span className="flex items-center gap-2 truncate">
                        <Bookmark size={16} className="text-indigo-600 shrink-0" />
                        <span className="text-indigo-700 font-medium truncate">{tpl.name}</span>
                      </span>
                      <button
                        onClick={(e) => deleteSavedBlock(tpl.template_id, e)}
                        className="opacity-0 group-hover:opacity-100 text-slate-400 hover:text-red-600 transition-opacity shrink-0"
                        title="Remove saved block"
                        data-testid={`delete-saved-block-${tpl.template_id}`}
                      >
                        <Trash size={14} />
                      </button>
                    </div>
                  ))
                )}

                <div className="col-span-2 text-xs font-semibold text-slate-500 px-2 py-1 mt-2 border-t">Basic Blocks</div>
                {basicBlocks.map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    onClick={() => addBlock(type)}
                    data-testid={`add-block-${type}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-lg transition-colors text-left text-sm"
                  >
                    <Icon size={18} className="text-slate-600" />
                    <span>{label}</span>
                  </button>
                ))}
                
                <div className="col-span-2 text-xs font-semibold text-teal-500 px-2 py-1 mt-2 border-t flex items-center gap-1">
                  <Film size={12} />
                  Media
                </div>
                {mediaBlocks.map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    onClick={() => addBlock(type)}
                    data-testid={`add-block-${type}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-teal-50 rounded-lg transition-colors text-left text-sm"
                  >
                    <Icon size={18} className="text-teal-600" />
                    <span className="text-teal-700 font-medium">{label}</span>
                  </button>
                ))}

                <div className="col-span-2 text-xs font-semibold text-amber-500 px-2 py-1 mt-2 border-t flex items-center gap-1">
                  <Film size={12} />
                  Hero Section
                </div>
                {heroBlocks.map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    onClick={() => addBlock(type)}
                    data-testid={`add-block-${type}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-amber-50 rounded-lg transition-colors text-left text-sm"
                  >
                    <Icon size={18} className="text-amber-600" />
                    <span className="text-amber-700 font-medium">{label}</span>
                  </button>
                ))}

                {featuredBlocks.length > 0 && (
                <div className="col-span-2 text-xs font-semibold text-purple-500 px-2 py-1 mt-2 border-t flex items-center gap-1">
                  <Sparkles size={12} />
                  Featured Blocks
                </div>
                )}
                {featuredBlocks.map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    onClick={() => addBlock(type)}
                    data-testid={`add-block-${type}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-purple-50 rounded-lg transition-colors text-left text-sm"
                  >
                    <Icon size={18} className="text-purple-600" />
                    <span className="text-purple-700 font-medium">{label}</span>
                  </button>
                ))}

                {interactiveBlocks.length > 0 && (
                  <div className="col-span-2 text-xs font-semibold text-fuchsia-500 px-2 py-1 mt-2 border-t flex items-center gap-1">
                    <MousePointerClick size={12} />
                    Interactive & Media
                  </div>
                )}
                {interactiveBlocks.map(({ type, icon: Icon, label }) => (
                  <button key={type} onClick={() => addBlock(type)} data-testid={`add-block-${type}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-fuchsia-50 rounded-lg transition-colors text-left text-sm">
                    <Icon size={18} className="text-fuchsia-600" />
                    <span className="text-fuchsia-700 font-medium">{label}</span>
                  </button>
                ))}

                {textEffectBlocks.length > 0 && (
                  <div className="col-span-2 text-xs font-semibold text-blue-500 px-2 py-1 mt-2 border-t flex items-center gap-1">
                    <Type size={12} />
                    Text Effects
                  </div>
                )}
                {textEffectBlocks.map(({ type, icon: Icon, label }) => (
                  <button key={type} onClick={() => addBlock(type)} data-testid={`add-block-${type}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-blue-50 rounded-lg transition-colors text-left text-sm">
                    <Icon size={18} className="text-blue-600" />
                    <span className="text-blue-700 font-medium">{label}</span>
                  </button>
                ))}

                {testimonialBlocks.length > 0 && (
                  <div className="col-span-2 text-xs font-semibold text-teal-500 px-2 py-1 mt-2 border-t flex items-center gap-1">
                    <MessageSquareQuote size={12} />
                    Testimonials
                  </div>
                )}
                {testimonialBlocks.map(({ type, icon: Icon, label }) => (
                  <button key={type} onClick={() => addBlock(type)} data-testid={`add-block-${type}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-teal-50 rounded-lg transition-colors text-left text-sm">
                    <Icon size={18} className="text-teal-600" />
                    <span className="text-teal-700 font-medium">{label}</span>
                  </button>
                ))}
                
                <div className="col-span-2 text-xs font-semibold text-slate-500 px-2 py-1 mt-2 border-t">Layout Blocks</div>
                {layoutBlocks.map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    onClick={() => addBlock(type)}
                    data-testid={`add-block-${type}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-slate-50 rounded-lg transition-colors text-left text-sm"
                  >
                    <Icon size={18} className="text-indigo-600" />
                    <span>{label}</span>
                  </button>
                ))}
                
                <div className="col-span-2 text-xs font-semibold text-red-500 px-2 py-1 mt-2 border-t flex items-center gap-1">
                  <FileCode size={12} />
                  Advanced
                </div>
                {advancedBlocks.map(({ type, icon: Icon, label }) => (
                  <button
                    key={type}
                    onClick={() => addBlock(type)}
                    data-testid={`add-block-${type}`}
                    className="flex items-center gap-2 px-3 py-2 hover:bg-red-50 rounded-lg transition-colors text-left text-sm"
                  >
                    <Icon size={18} className="text-red-600" />
                    <span className="text-red-700 font-medium">{label}</span>
                  </button>
                ))}
                
                {/* Newsletter Blocks */}
                {newsletterBlocks.length > 0 && (
                  <>
                    <div className="col-span-2 text-xs font-semibold text-rose-500 px-2 py-1 mt-2 border-t flex items-center gap-1">
                      <MailX size={12} />
                      Newsletter
                    </div>
                    {newsletterBlocks.map(({ type, icon: Icon, label }) => (
                      <button
                        key={type}
                        onClick={() => addBlock(type)}
                        data-testid={`add-block-${type}`}
                        className="flex items-center gap-2 px-3 py-2 hover:bg-rose-50 rounded-lg transition-colors text-left text-sm"
                      >
                        <Icon size={18} className="text-rose-600" />
                        <span className="text-rose-700 font-medium">{label}</span>
                      </button>
                    ))}
                  </>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      <MediaPickerModal
        open={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={handleMediaSelect}
      />

      <SnippetBrowserModal
        open={showSnippetBrowser}
        onClose={() => {
          setShowSnippetBrowser(false);
          setSnippetTargetBlockId(null);
        }}
        onSelect={handleSnippetSelect}
      />

      <SaveBlockTemplateModal
        open={!!blockToSave}
        block={blockToSave}
        onClose={() => setBlockToSave(null)}
        onSaved={() => fetchSavedBlocks()}
      />
    </div>
  );
};

export { generateAnchorId };
export default BlockEditor;
